import { extractedNameFields, nameFields } from "../../../utils/individualName";
import { createPortal } from "react-dom";
import StaffDocumentsReviewPage, { requirementOptionMatchesDetectedDocumentKind } from "../../../pages/StaffDocumentsReviewPage";
import { pendingReviewFilesFromSubmissions, pendingFileMatchesRequirement } from "../../../utils/pendingDocumentReview";
import AdditionalDocuments, { additionalDocumentsFromSubmissions, isAdditionalDocument } from '../../AdditionalDocuments';
import PdfDocumentPreview from '../../../components/ui/PdfDocumentPreview';
import { requiresDocumentReview } from '../../../utils/documentReviewPolicy';
import { corporateRegWithApprovedDocuments } from '../../../utils/corporateDocumentChecklist';
import {
  CORPORATE_DOCUMENT_REQUIREMENT_COLLECTION,
  CORPORATE_DOCUMENT_FALLBACK_LABEL,
  corporateRequirementFromData,
  corporateRequirementSatisfied,
  corporateRequirementProgress,
  corporateRequirementSnapshotCases,
} from '../../../utils/corporateDocumentRequirements';
import { corporateScanPresentation, attachmentScanResult } from '../../../utils/corporateScanPresentation';
import KycDocumentIntakeModal from '../../KycDocumentIntakeModal';
import KycDocumentProcessingOverlay from '../../KycDocumentProcessingOverlay';
import { extractKycDocuments, applyKycIntake } from '../../kycDocumentIntake';
/* KycSectionDocuments.jsx */

import React, { useEffect, useMemo, useState } from "react";
import "../css/KycSectionDocuments.css";
import "../../../pages/css/KycFormPage.css";
import "../../../pages/css/StaffDocumentsReviewPage.css";
import { auth, db, storage } from "../../../firebase";
import { AnimatePresence, motion } from "framer-motion";
import { Toast } from "primereact/toast";
import { useRef } from "react";

// import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import {
  collection,
  doc,
  FieldPath,
  getDoc,
  getDocs,
  onSnapshot,
  limit,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { deleteObject, ref as storageRef } from "firebase/storage";

import DocumentPreviewModal from "../../../components/ui/DocumentPreviewModal";
import { appConfirm } from "../../../components/ui/appConfirm";
import { MAIL_API_BASE, MYKYC_API_BASE } from "../../../config/api";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import iconIdCardBlueDoc from "../../../images/id-card-blue-doc.svg";
import iconUtilityBlueDoc from "../../../images/utility-blue-doc.svg";
import iconCorporateDocBlue from "../../../images/corporate-doc-blue-2.svg";
import iconSowBlueDoc from "../../../images/sow-blue.svg";
import iconUploadBlue from "../../../images/upload-blue.svg";
import iconCircleBlueDownload from "../../../images/circle-blue-download.svg";
import iconCircleBlueView from "../../../images/circle-blue-view.svg";
import {
  DEFAULT_UBO_DOCUMENT_SCOPE,
  SUPPORTING_DOCUMENT_POLICY_COLLECTION,
  SOW_LABEL_BY_VALUE as POLICY_SOW_LABEL_BY_VALUE,
  buildDefaultSupportingDocumentPolicy,
  buildSowEntryFromPolicy,
  buildSowMissingParts,
  isSowEntrySatisfied,
  normalizeRelationshipRiskLevel,
  normalizeSowKey as normalizePolicySowKey,
  normalizeUboDocumentScope,
  riskPolicy as supportingDocRiskPolicy,
  sanitizeSupportingDocumentPolicy,
  UBO_DOCUMENT_SCOPE_ALL_UBOS,
  UBO_DOCUMENT_SCOPE_THRESHOLD_AND_DIRECT,
  UBO_DOCUMENT_SCOPE_THRESHOLD_ONLY,
} from "../../../utils/sowDocumentPolicy";
import {
  DEFAULT_RELATIONSHIP_RISK_SETTINGS,
  sanitizeRelationshipRiskSettings,
} from "../../../utils/relationshipRiskRules";

import {
  getDirectIndividualShareholderRows,
  getIndirectIndividualUboRows,
  getQualifiedIndividualUboRows,
} from "../../../utils/uboSelection";

countries.registerLocale(enLocale);

/**
 * Stored in Firestore under:
 * form.documentsChecklist = { parties: {...}, sow: {...} }
 */

/* -------------------- Helpers -------------------- */

function normPartyType(v) {
  const s = String(v || "")
    .trim()
    .toLowerCase();
  if (s === "individual" || s === "person" || s === "natural person")
    return "individual";
  if (s === "trust") return "trust";
  if (s === "entity" || s === "company" || s === "legal person")
    return "entity";
  return "";
}

function normalizeChecklistDate(value) {
  if (!value) return "";
  if (typeof value?.toDate === "function") {
    const d = value.toDate();
    return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
  }
  if (typeof value === "object" && Number.isFinite(Number(value.seconds))) {
    const d = new Date(Number(value.seconds) * 1000);
    return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? "" : value.toISOString().slice(0, 10);
  }
  const raw = String(value || "").trim();
  const direct = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (direct) return `${direct[1]}-${direct[2]}-${direct[3]}`;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString().slice(0, 10);
}

function formatChecklistDate(value) {
  const normalized = normalizeChecklistDate(value);
  if (!normalized) return "";
  const d = new Date(`${normalized}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function todayChecklistDate() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function isChecklistDateExpired(value) {
  const normalized = normalizeChecklistDate(value);
  return !!normalized && normalized < todayChecklistDate();
}

function makeId() {
  try {
    return crypto?.randomUUID?.() || `id_${Date.now()}_${Math.random()}`;
  } catch {
    return `id_${Date.now()}_${Math.random()}`;
  }
}

function cleanStr(value) {
  return String(value || "").trim();
}

function stripUndefinedForFirestore(value) {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (value instanceof Date) return value;
  if (Array.isArray(value)) {
    return value
      .map((item) => stripUndefinedForFirestore(item))
      .filter((item) => item !== undefined);
  }
  if (typeof value === "object") {
    if (value.constructor && value.constructor !== Object) return value;
    return Object.entries(value).reduce((acc, [key, entry]) => {
      const cleaned = stripUndefinedForFirestore(entry);
      if (cleaned !== undefined) acc[key] = cleaned;
      return acc;
    }, {});
  }
  return value;
}

function sanitizeFirestoreUpdatePayload(payload = {}) {
  return Object.entries(payload).reduce((acc, [key, value]) => {
    const cleaned = stripUndefinedForFirestore(value);
    if (cleaned !== undefined) acc[key] = cleaned;
    return acc;
  }, {});
}

function updateDocFieldPath(...segments) {
  return new FieldPath(...segments.map((segment) => cleanStr(segment)));
}

function parseRequirementKey(requirementKey) {
  const key = cleanStr(requirementKey);
  if (!key) return null;

  if (key.includes("|")) {
    const parts = key.split("|").map(cleanStr);
    const kind = parts[0];
    if (kind === "party" && parts.length >= 3) {
      return {
        kind,
        partyId: parts.slice(1, -1).join("|"),
        docKey: parts[parts.length - 1],
      };
    }
    if (kind === "sow" && parts.length >= 3) {
      return {
        kind,
        sowKey: parts[1],
        docKey: parts[parts.length - 1],
      };
    }
    return null;
  }

  const parts = key.split(":").map(cleanStr);
  const kind = parts[0];

  if (kind === "party" && parts.length >= 3) {
    return {
      kind,
      partyId: parts.slice(1, -1).join(":"),
      docKey: parts[parts.length - 1],
    };
  }

  if (kind === "sow" && parts.length >= 3) {
    const [, sowKey, maybeMode, ...rest] = parts;
    const hasMode =
      maybeMode === "req" || maybeMode === "opt" || maybeMode === "grp";
    return {
      kind,
      sowKey,
      docKey:
        maybeMode === "grp"
          ? rest.slice(1).join(":")
          : hasMode
          ? rest.join(":")
          : parts.slice(2).join(":"),
    };
  }

  return null;
}

function withDocRefsIfMissing(list, keyName = "_docRef") {
  let changed = false;
  const next = (list || []).map((row) => {
    if (row && row[keyName]) return row;
    changed = true;
    return { ...(row || {}), [keyName]: makeId() };
  });
  return { next, changed };
}

function slugId(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function canonicalCountry(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 12);
}

function canonicalPartyId(party) {
  const partyType = normPartyType(party?.partyType);
  const nameKey = slugId(party?.name) || "unknown";

  if (partyType === "individual") {
    return [
      "person",
      nameKey,
      canonicalCountry(party?.idCountry) || "xx",
      canonicalCountry(party?.addressCountry) || "xx",
    ].join(":");
  }

  if (partyType === "entity") {
    return [
      "entity",
      nameKey,
      canonicalCountry(party?.corporateCountry || party?.addressCountry) || "xx",
    ].join(":");
  }

  if (partyType === "trust") {
    return ["trust", nameKey].join(":");
  }

  return ["party", nameKey].join(":");
}

function joinRoles(roles = []) {
  return Array.from(new Set((roles || []).map((role) => String(role || "").trim()).filter(Boolean))).join(" · ");
}

function defaultDocsForType(partyType) {
  if (partyType === "individual") {
    return {
      id: { required: true, obtained: false },
      poa: { required: true, obtained: false },
    };
  }
  if (partyType === "trust") {
    return {
      trustDeed: { required: true, obtained: false },
    };
  }
  if (partyType === "entity") {
    return { reg: { required: true, obtained: false } };
  }
  return {};
}

function iso2(v) {
  return String(v || "")
    .trim()
    .toUpperCase();
}

// Requirement for a country: the documents of all its cases (see utils/corporateDocumentRequirements).
function corporateRequirementForIso2(countryIso2, requirementMap = {}) {
  const c = iso2(countryIso2);
  return corporateRequirementFromData(c, c ? requirementMap[c] : null);
}

const CORPORATE_CASE_LOGIC_TEXT = { ALL: "all required", ONE_OF: "at least 1 of" };

function corporateRequirementHeading(requirement) {
  const cases = requirement?.cases || [];
  if (cases.length > 1) return "Provide one of the following:";
  if (cases[0]?.logic === "ONE_OF") return "Provide at least one of:";
  if ((requirement?.docs || []).length > 1) return "All required:";
  return "";
}

function corporateRequirementItemMeta(requirement) {
  const cases = requirement?.cases || [];
  return cases.length === 1 && cases[0].logic === "ONE_OF" ? "(at least 1 of)" : "";
}

function corporateRequirementNoteText(requirement) {
  const condition = String(requirement?.doc_condition || "").trim();
  if (!condition) return "";
  return `Note: ${condition.replace(/^\((.*)\)$/s, "$1").trim()}`;
}

// Documents grouped by case for display (one untitled group for a single case): required documents
// first, then the optional ones (marked optional, not counted for completion).
function corporateRequirementGroups(requirement) {
  const cases = requirement?.cases || [];
  const byKey = Object.fromEntries((requirement?.docs || []).map((item) => [item.key, item]));
  if (!cases.length) return [{ title: "", items: requirement?.docs || [] }];
  return cases.map((kase) => ({
    title: cases.length > 1 ? `${kase.name || "Option"} (${CORPORATE_CASE_LOGIC_TEXT[kase.logic] || "all required"})` : "",
    items: [
      ...kase.keys.map((key) => byKey[key] && { ...byKey[key], optional: false }),
      ...(kase.optionalKeys || []).map((key) => byKey[key] && { ...byKey[key], optional: true }),
    ].filter(Boolean),
  }));
}

function isCorporateDocItemObtained(regDoc, key) {
  return !!regDoc?.items?.[key]?.obtained;
}

function buildCorporateRegSnapshot(regDoc = {}, requirement) {
  const nextItems = {};
  (requirement?.docs || []).forEach((item) => {
    if (!item?.key) return;
    const existing = regDoc?.items?.[item.key] || {};
    nextItems[item.key] = {
      key: item.key,
      label: item.label || CORPORATE_DOCUMENT_FALLBACK_LABEL,
      secondaryLabel: item.secondaryLabel || "",
      obtained: !!existing.obtained,
    };
  });

  const nextReg = {
    ...(regDoc || {}),
    required: true,
    doc_logic: requirement?.doc_logic || "ALL",
    doc_condition: requirement?.doc_condition || null,
    fallback: !!requirement?.fallback,
    cases: corporateRequirementSnapshotCases(requirement),
    items: nextItems,
  };
  nextReg.obtained = corporateRequirementSatisfied(nextReg, requirement);
  return nextReg;
}

function corporateRequirementEmailLines(requirement) {
  const lines = [];
  const heading = corporateRequirementHeading(requirement);
  if (heading) lines.push(heading);
  if (requirement?.doc_condition) lines.push(requirement.doc_condition);
  const groups = corporateRequirementGroups(requirement);
  groups.forEach((group) => {
    if (group.title) lines.push(`${group.title}:`);
    group.items.forEach((item) => {
      const label = item.secondaryLabel ? `${item.label} (${item.secondaryLabel})` : item.label;
      lines.push(item.optional ? `${label} (optional)` : label);
    });
  });
  return lines.length ? lines : [CORPORATE_DOCUMENT_FALLBACK_LABEL];
}

// Labels of the documents of a single case: required ones, or optional ones (marked optional).
function corporateRequirementEmailLabels(requirement, { optional = false } = {}) {
  const items = corporateRequirementGroups(requirement)[0]?.items || [];
  return items.filter((item) => item.optional === optional).map((item) => {
    const label = item.secondaryLabel ? `${item.label} (${item.secondaryLabel})` : item.label;
    return optional ? `${label} (optional)` : label;
  });
}

function mergeParty(existing, derived) {
  const base = { ...derived, docs: defaultDocsForType(derived.partyType) };
  const exDocs = existing?.docs || {};
  Object.keys(base.docs || {}).forEach((k) => {
    base.docs[k] = {
      ...base.docs[k],
      ...(exDocs?.[k] || {}),
      required: base.docs[k]?.required !== false,
      obtained: !!exDocs?.[k]?.obtained,
    };
  });
  return base;
}

function stableDocumentPartyId(party) {
  const sources = Array.isArray(party?.sourcePartyIds) ? party.sourcePartyIds : [];
  return cleanStr(sources.find(Boolean)) || cleanStr(party?.partyId);
}

function partyRequirementKey(party, docKey) {
  const stableId = stableDocumentPartyId(party);
  if (!stableId || !docKey) return "";
  return `party:${stableId}:${docKey}`;
}

function partyRequirementKeyCandidates(party, docKey) {
  const ids = [
    stableDocumentPartyId(party),
    cleanStr(party?.partyId),
    ...(Array.isArray(party?.sourcePartyIds) ? party.sourcePartyIds : []),
  ].filter(Boolean);
  return Array.from(new Set(ids)).map((partyId) => `party:${partyId}:${docKey}`);
}

function cleanNodeId(value) {
  return String(value || "").trim();
}

function getQualifiedUboRows(ownership) {
  return getQualifiedIndividualUboRows(ownership);
}

function getThresholdUboRows(ownership) {
  return getQualifiedUboRows(ownership);
}

function getScopedDirectShareholderRows(ownership, uboDocumentScope) {
  if (uboDocumentScope === UBO_DOCUMENT_SCOPE_THRESHOLD_ONLY) {
    return [];
  }

  if (
    uboDocumentScope === UBO_DOCUMENT_SCOPE_THRESHOLD_AND_DIRECT ||
    uboDocumentScope === UBO_DOCUMENT_SCOPE_ALL_UBOS
  ) {
    return getDirectIndividualShareholderRows(ownership);
  }

  return [];
}

function getScopedUboRows(ownership, uboDocumentScope) {
  if (uboDocumentScope === UBO_DOCUMENT_SCOPE_ALL_UBOS) {
    return getIndirectIndividualUboRows(ownership);
  }

  return getThresholdUboRows(ownership);
}

function buildDerivedParties(form, uboDocumentScope = DEFAULT_UBO_DOCUMENT_SCOPE) {
  const customer = form?.customer || {};
  const ownership = form?.ownership || {};
  const normalizedUboDocumentScope = normalizeUboDocumentScope(uboDocumentScope);
  const rawParties = [];
  const legalArrangements = Array.isArray(
    ownership?.legalArrangements?.arrangements
  )
    ? ownership.legalArrangements.arrangements
    : [];
  const customerIsTrust = !!ownership?.uboFlags?.accountHolderTrust;

  const customerType = customerIsTrust
    ? "trust"
    : normPartyType(customer?.type) || "individual";
  const customerName = String(customer?.name || "").trim() || "Customer";

  rawParties.push({
    partyId: "customer",
    name: customerName,
    role:
      customerType === "entity"
        ? "Customer (Entity)"
        : customerType === "trust"
        ? "Customer (Trust)"
        : "Customer (Individual)",
    partyType: customerType,
    idCountry: customer?.nationalityOrIncorporationCountry || "",
    addressCountry: customer?.country || "",
    corporateCountry: customer?.nationalityOrIncorporationCountry || "",
  });

  const directDocumentNodeIds = new Set();

  getScopedDirectShareholderRows(ownership, normalizedUboDocumentScope).forEach((r) => {
    const name = String(r?.name || "").trim();
    if (!name) return;
    const holderType = normPartyType(
      r?.holderType || r?.shareholderEntityType || r?.partyType || r?.type
    );
    if (!holderType) return;
    if (holderType === "trust") return;

    const treeNodeId = cleanNodeId(r?.treeNodeId);
    if (treeNodeId) directDocumentNodeIds.add(treeNodeId);

    rawParties.push({
      partyId: `shareholder:${r?._rowId || r?._docRef || name}`,
      name,
      role: "Direct shareholder",
      partyType: holderType,
      idCountry: r?.nationality || "",
      addressCountry: r?.countryOfResidence || "",
      corporateCountry: r?.countryOfResidence || "",
    });
  });

  getScopedUboRows(ownership, normalizedUboDocumentScope).forEach((r) => {
    const name = String(r?.name || "").trim();
    if (!name) return;
    const treeNodeId = cleanNodeId(r?.treeNodeId || r?.subjectNodeId);
    if (treeNodeId && directDocumentNodeIds.has(treeNodeId)) return;
    rawParties.push({
      partyId: `ubo:${r?._rowId || r?._docRef || name}`,
      name,
      role: "UBO (Ultimate beneficial owner)",
      partyType: "individual",
      idCountry: r?.nationality || "",
      addressCountry: r?.countryOfResidence || "",
      corporateCountry: "",
    });
  });

  (ownership?.signatories || ownership?.controllers || []).forEach((r) => {
    const name = String(r?.name || "").trim();
    if (!name) return;
    const cap = String(r?.capacity || "").trim();
    rawParties.push({
      partyId: `signatory:${r?._rowId || r?._docRef || name}`,
      name,
      role: cap ? `Signatory (${cap})` : "Signatory",
      partyType: "individual",
      idCountry: r?.nationality || "",
      addressCountry: r?.countryOfResidence || "",
      corporateCountry: "",
    });
  });

  legalArrangements.forEach((arr, idx) => {
    const arrangementType = String(arr?.arrangementType || "")
      .trim()
      .toLowerCase();
    if (arrangementType !== "trust") return;

    const isAccountHolderTrustArrangement =
      customerIsTrust && String(arr?.source || "") === "account_holder";

    const trustName =
      String(arr?.name || "").trim() ||
      String(arr?.relatedShareholderName || "").trim() ||
      `Trust ${idx + 1}`;

    if (!isAccountHolderTrustArrangement) {
      rawParties.push({
        partyId: `trust:${arr?._rowId || idx}`,
        name: trustName,
        role: "Trust",
        partyType: "trust",
        idCountry: "",
        addressCountry: "",
        corporateCountry: "",
      });
    }

    const trustee = (Array.isArray(arr?.parties) ? arr.parties : []).find(
      (p) => String(p?.role || "").trim() === "Trustee"
    );
    const trusteeName = String(trustee?.name || "").trim();
    if (!trusteeName) return;

    const trusteeType =
      normPartyType(trustee?.trusteeKind) === "entity" ? "entity" : "individual";

    rawParties.push({
      partyId: `trustee:${arr?._rowId || idx}:${trustee?._rowId || trusteeName}`,
      name: trusteeName,
      role: `Trustee of ${trustName}`,
      partyType: trusteeType,
      idCountry: trustee?.nationality || "",
      addressCountry: trustee?.domicile || "",
      corporateCountry: trustee?.domicile || "",
    });
  });

  const byCanonicalId = new Map();

  rawParties.forEach((party) => {
    const partyType = normPartyType(party?.partyType);
    const partyId = canonicalPartyId({ ...party, partyType });
    const current = byCanonicalId.get(partyId);
    const role = String(party?.role || "").trim();
    const sourcePartyId = String(party?.partyId || "").trim();

    if (!current) {
      byCanonicalId.set(partyId, {
        ...party,
        partyId,
        partyType,
        role,
        roles: role ? [role] : [],
        sourcePartyIds: sourcePartyId ? [sourcePartyId] : [],
      });
      return;
    }

    const roles = Array.from(
      new Set([...(current.roles || []), role].filter(Boolean))
    );
    const sourcePartyIds = Array.from(
      new Set([...(current.sourcePartyIds || []), sourcePartyId].filter(Boolean))
    );

    byCanonicalId.set(partyId, {
      ...current,
      name: current.name || party.name,
      idCountry: current.idCountry || party.idCountry || "",
      addressCountry: current.addressCountry || party.addressCountry || "",
      corporateCountry: current.corporateCountry || party.corporateCountry || "",
      roles,
      sourcePartyIds,
      role: joinRoles(roles),
    });
  });

  return Array.from(byCanonicalId.values());
}

const statusIcon = (ok) =>
  ok
    ? "bi bi-check-circle-fill kycDocStatusIcon kycDocStatusIconOk"
    : "bi bi-exclamation-circle-fill kycDocStatusIcon kycDocStatusIconTodo";

const docCheckIcon = "bi bi-check-circle-fill";

/* -------------------- SoW mapping -------------------- */

const SOW_DOC_RULES = {
  employment: [
    { key: "payslips", label: "Payslips", isBankStatement: false },
    {
      key: "employment_contract",
      label: "Employment contract",
      isBankStatement: false,
    },
    { key: "tax_return", label: "Tax return", isBankStatement: false },
    {
      key: "bank_statements",
      label: "Bank statement(s)",
      isBankStatement: true,
    },
  ],
  self_employed: [
    { key: "contracts", label: "Copies of contracts", isBankStatement: false },
    { key: "tax_return", label: "Tax return", isBankStatement: false },
    {
      key: "audited_fs",
      label: "Audited financial statement(s)",
      isBankStatement: false,
    },
    {
      key: "bank_statements",
      label: "Bank statement(s)",
      isBankStatement: true,
    },
  ],
  business_owner: [
    {
      key: "shareholder_register",
      label: "Shareholder register",
      isBankStatement: false,
    },
    {
      key: "audited_fs",
      label: "Audited financial statement(s)",
      isBankStatement: false,
    },
    {
      key: "corporate_doc",
      label: "Corporate document",
      isBankStatement: false,
    },
    {
      key: "bank_statements",
      label: "Bank statement(s)",
      isBankStatement: true,
    },
  ],
  sale_business: [
    {
      key: "sale_agreement",
      label: "Sale agreement / SPA",
      isBankStatement: false,
    },
    {
      key: "completion_statement",
      label: "Completion statement",
      isBankStatement: false,
    },
    {
      key: "bank_statements",
      label: "Bank statement(s)",
      isBankStatement: true,
    },
  ],
  sale_real_estate: [
    { key: "sale_agreement", label: "Sale agreement", isBankStatement: false },
    { key: "title_deed", label: "Title deed", isBankStatement: false },
    {
      key: "bank_statements",
      label: "Bank statement(s)",
      isBankStatement: true,
    },
  ],
  rental_income: [
    {
      key: "rental_contract",
      label: "Rental contract",
      isBankStatement: false,
    },
    { key: "title_deed", label: "Title deed", isBankStatement: false },
    {
      key: "bank_statements",
      label: "Bank statement(s)",
      isBankStatement: true,
    },
  ],
  investment_returns: [
    {
      key: "broker_statement",
      label: "Broker / portfolio statement",
      isBankStatement: false,
    },
    {
      key: "trade_confirmation",
      label: "Trade confirmation(s)",
      isBankStatement: false,
    },
    {
      key: "bank_statements",
      label: "Bank statement(s)",
      isBankStatement: true,
    },
  ],
  inheritance: [
    {
      key: "inheritance_certificate",
      label: "Certificate of inheritance",
      isBankStatement: false,
    },
    {
      key: "grant_of_probate",
      label: "Grant of probate",
      isBankStatement: false,
    },
    { key: "will_copy", label: "Copy of will", isBankStatement: false },
    {
      key: "bank_statements",
      label: "Bank statement(s)",
      isBankStatement: true,
    },
  ],
  gift: [
    { key: "gift_deed", label: "Gift deed", isBankStatement: false },
    {
      key: "donor_explanation",
      label: "Donor letter / explanation",
      isBankStatement: false,
    },
    {
      key: "bank_statements",
      label: "Bank statement(s)",
      isBankStatement: true,
    },
  ],
  loan: [
    { key: "loan_agreement", label: "Loan agreement", isBankStatement: false },
    {
      key: "repayment_schedule",
      label: "Repayment schedule",
      isBankStatement: false,
    },
    {
      key: "bank_statements",
      label: "Bank statement(s)",
      isBankStatement: true,
    },
  ],
  legal_settlement: [
    {
      key: "court_order",
      label: "Court order / settlement agreement",
      isBankStatement: false,
    },
    {
      key: "bank_statements",
      label: "Bank statement(s)",
      isBankStatement: true,
    },
  ],
  divorce_settlement: [
    {
      key: "divorce_decree",
      label: "Divorce decree / court order",
      isBankStatement: false,
    },
    {
      key: "financial_settlement",
      label: "Financial settlement agreement",
      isBankStatement: false,
    },
    {
      key: "bank_statements",
      label: "Bank statement(s)",
      isBankStatement: true,
    },
  ],
  virtual_asset_gains: [
    {
      key: "exchange_statement",
      label: "Exchange statement",
      isBankStatement: false,
    },
    {
      key: "trade_history",
      label: "Trade history / transaction report",
      isBankStatement: false,
    },
    {
      key: "bank_statements",
      label: "Bank statement(s)",
      isBankStatement: true,
    },
  ],
  other: [],
};

const SOW_OPTIONS = [
  { label: "Employment", value: "employment" },
  { label: "Self-employed", value: "self_employed" },
  { label: "Business owner", value: "business_owner" },
  { label: "Sale of company", value: "sale_business" },
  { label: "Real estate sale", value: "sale_real_estate" },
  { label: "Rental income", value: "rental_income" },
  { label: "Investment returns", value: "investment_returns" },
  { label: "Inheritance", value: "inheritance" },
  { label: "Gift", value: "gift" },
  { label: "Legal settlement", value: "legal_settlement" },
  { label: "Divorce settlement", value: "divorce_settlement" },
  { label: "Virtual asset gains", value: "virtual_asset_gains" },
  { label: "Other", value: "other" },
];

const SOW_LABEL_BY_VALUE = Object.fromEntries(
  SOW_OPTIONS.map((o) => [o.value, o.label])
);

const SOW_VALUE_BY_LABEL = Object.fromEntries(
  SOW_OPTIONS.map((o) => [String(o.label).toLowerCase(), o.value])
);

function normalizeSowKey(value) {
  return normalizePolicySowKey(value);
}

function buildSowDocsMap(rules = [], existingDocs = {}) {
  const nextDocs = {};

  (rules || []).forEach((d) => {
    const existing = existingDocs?.[d.key] || {};
    nextDocs[d.key] = {
      ...existing,
      label: d.label,
      obtained: !!existing.obtained,
      isBankStatement: !!d.isBankStatement,
    };
  });

  return nextDocs;
}

function buildSowEntry(it, existing) {
  if (it?.policyConfig) {
    return buildSowEntryFromPolicy({
      sowKey: it.sowKey,
      label: it.label,
      riskLevel: it.riskLevel || "",
      cfg: it.policyConfig,
      existing,
    });
  }

  return {
    ...(existing || {}),
    sowKey: it.sowKey,
    label: it.label,
    otherDetails: existing?.otherDetails || "",
    docs: buildSowDocsMap(it.docs || [], existing?.docs || {}),
  };
}

function sowEntryHasUserFacingRequirements(entry) {
  if (!entry) return false;
  if (entry.sowKey === "other") return true;

  const required = Array.isArray(entry.required) ? entry.required : [];
  const optional = Array.isArray(entry.optional) ? entry.optional : [];
  const groups = Array.isArray(entry.groups) ? entry.groups : [];
  const docs = entry.docs || {};

  return (
    required.length > 0 ||
    optional.length > 0 ||
    groups.some((group) => Array.isArray(group?.documents) && group.documents.length > 0) ||
    Object.keys(docs).length > 0
  );
}

function hasSavedScreeningRunForRisk(screening) {
  if (!screening || typeof screening !== "object") return false;
  return !!(
    screening.result ||
    screening.runAt ||
    screening.completedAt ||
    screening.lastRunAt ||
    screening.lastCheckedAt
  );
}

function industryRiskIsReady(form) {
  const saved =
    form?.internalUse?.industryRiskResult?.final?.riskLevel ??
    form?.internalUse?.industryRiskResult?.auto?.riskLevel ??
    null;
  const n = Number(saved);
  return n === 1 || n === 2 || n === 3;
}

function geoRiskIsReady(form) {
  const v = String(form?.internalUse?.geoRiskLevel || "").toLowerCase();
  return v === "high" || v === "medium" || v === "low";
}

function screeningRiskIsReady(screening) {
  const decision = String(screening?.staffDecision || "").toLowerCase();
  return (
    decision === "flagged_confirmed" ||
    decision === "flagged" ||
    decision === "no_exposure" ||
    decision === "flagged_remediated" ||
    hasSavedScreeningRunForRisk(screening)
  );
}

function relationshipRiskInputsAreReady(form) {
  const negativeNews = form?.adverseMedia?.negativeNews;
  return (
    geoRiskIsReady(form) &&
    industryRiskIsReady(form) &&
    (negativeNews === true || negativeNews === false) &&
    screeningRiskIsReady(form?.pep) &&
    screeningRiskIsReady(form?.sanctions)
  );
}

function shouldShowReadOnlyRequirement(docs, docKey) {
  const entries = Object.entries(docs || {}).filter(([, doc]) => doc?.required);
  if (!entries.length) return true;

  const requiredCount = entries.length;
  const obtainedCount = entries.filter(([, doc]) => !!doc?.obtained).length;
  if (obtainedCount < requiredCount) return true;

  return !!docs?.[docKey]?.obtained;
}

function visibleReadOnlySowDocEntries(docsMap) {
  return Object.entries(docsMap || {});
}

/* -------------------- Preview document link helper -------------------- */

function pickFileUrl(f) {
  if (!f) return "";
  // Try common shapes
  return String(
    f.downloadUrl ||
      f.downloadURL || // ✅ FIX (Firebase often uses this)
      f.url ||
      f.publicUrl ||
      f.signedUrl ||
      f.fileUrl ||
      f.viewUrl ||
      f.storageDownloadUrl ||
      f?.file?.downloadUrl ||
      f?.file?.downloadURL || // ✅
      f?.file?.url ||
      f?.meta?.downloadUrl ||
      f?.meta?.downloadURL || // ✅
      ""
  ).trim();
}

async function fileToAuthenticatedBlobUrl(file, docId) {
  const objectPath = cleanStr(file?.objectPath);
  if (!objectPath || !docId) return "";

  const user = auth?.currentUser;
  const token = await user?.getIdToken?.();
  if (!token) return "";

  const filename =
    cleanStr(file?.originalName || file?.name) ||
    cleanStr(objectPath.split("/").pop()) ||
    "document";
  const url =
    `${MYKYC_API_BASE}/myKyc/staff/doc-upload/download` +
    `?docId=${encodeURIComponent(docId)}` +
    `&objectPath=${encodeURIComponent(objectPath)}` +
    `&filename=${encodeURIComponent(filename)}`;

  const response = await fetch(url, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Preview failed (${response.status})`);
  }

  const blob = await response.blob();
  const contentType = cleanStr(file?.contentType || file?.mimeType);
  const typedBlob = contentType ? new Blob([blob], { type: contentType }) : blob;
  return window.URL.createObjectURL(typedBlob);
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

function allowedStaffAttachFile(file) {
  const type = String(file?.type || "").toLowerCase();
  return type === "application/pdf" || type.startsWith("image/");
}

function scanStatusDone(result) {
  if (!result) return false;
  const status = cleanStr(result?.overallStatus || result?.status);
  return !!status && !["pending", "processing", "running"].includes(status);
}

function scanBadgeClass(status) {
  const s = cleanStr(status);
  if (s === "pass" || s === "passed" || s === "clear" || s === "extracted") return "isClear";
  if (s === "fail" || s === "failed" || s === "critical_mismatch") return "isCritical";
  if (s === "expired") return "isExpired";
  if (s === "pending") return "isPending";
  return "isManual";
}

function scanBadgeLabel(result) {
  const status = cleanStr(result?.overallStatus || result?.status);
  if (status === "passed" || status === "clear") return "Clear";
  if (status === "failed" || status === "critical_mismatch") return "Mismatch";
  if (status === "extracted") return "Extracted";
  if (status === "pending") return "Scanning";
  if (status === "manual_review") return "Manual review";
  return status || "Scan outcome";
}

function prettyBytes(bytes) {
  const n = Number(bytes || 0);
  if (!Number.isFinite(n) || n <= 0) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let value = n;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function staffAttachApiBase() {
  return String(MYKYC_API_BASE || MAIL_API_BASE).replace(/\/+$/, "");
}

function formatStaffAttachName(value) {
  const clean = cleanStr(value).replace(/\s+/g, " ");
  if (!clean) return "";
  return clean
    .toLowerCase()
    .replace(/(^|[\s'’.-])([a-zÀ-ÖØ-öø-ÿ])/g, (_, separator, letter) => (
      `${separator}${letter.toUpperCase()}`
    ));
}

function normalizeStaffAttachDate(value) {
  const s = cleanStr(value);
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

function todayStaffAttachDateOnly() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function formatStaffAttachDate(value) {
  const normalized = normalizeStaffAttachDate(value);
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return cleanStr(value) || "Not available";
  const [, year, month, day] = match;
  const monthLabel = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][Number(month) - 1];
  return monthLabel ? `${Number(day)} ${monthLabel} ${year}` : normalized;
}

function normalizeStaffAttachCountry(value) {
  const raw = cleanStr(value).toUpperCase().replace(/[^A-Z]/g, "");
  if (!raw) return "";
  if (/^[A-Z]{2}$/.test(raw)) return countries.alpha2ToAlpha3(raw) || raw;
  return raw;
}

function normalizeStaffAttachName(value) {
  return cleanStr(value)
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Z ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function formatStaffAttachCountry(value) {
  const normalized = normalizeStaffAttachCountry(value);
  const iso2 = /^[A-Z]{3}$/.test(normalized)
    ? countries.alpha3ToAlpha2(normalized)
    : /^[A-Z]{2}$/.test(normalized)
      ? normalized
      : "";
  const name = iso2 ? countries.getName(iso2, "en") : "";
  if (name && normalized) return `${name} (${normalized})`;
  return normalized || cleanStr(value) || "Not available";
}

function staffAttachScanResultFromFile(file, docKey) {
  return attachmentScanResult(file, docKey);
}

function compareStaffAttachIdScanWithContext(result, context = {}) {
  if (!result || cleanStr(result?.status) === "pending") return result;
  const kind = staffAttachScanKind(result, context);
  if (kind !== "id") return result;

  const extractedData = result?.extractedData || {};
  const existingChecks = Array.isArray(result?.checks) ? result.checks : [];
  const hasProfileComparison = existingChecks.some((check) =>
    ["name", "dateOfBirth", "nationality"].includes(cleanStr(check?.type))
      && cleanStr(check?.profileValue || check?.expectedValue)
  );
  if (hasProfileComparison) {
    const hasExpiryCheck = existingChecks.some((check) => cleanStr(check?.type) === "expiryDate");
    const extractedExpiry = normalizeStaffAttachDate(extractedData.expiryDate);
    if (!hasExpiryCheck && /^\d{4}-\d{2}-\d{2}$/.test(extractedExpiry)) {
      const expired = extractedExpiry < todayStaffAttachDateOnly();
      const nextMessages = Array.isArray(result?.messages) ? [...result.messages] : [];
      if (expired) nextMessages.push("The document is expired.");
      return {
        ...result,
        checks: [
          ...existingChecks,
          {
            type: "expiryDate",
            label: "Expiry date",
            status: expired ? "expired" : "match",
            message: expired ? "The document is expired." : "The document is not expired.",
            extractedValue: extractedData.expiryDate || extractedExpiry,
          },
        ],
        messages: nextMessages.length ? [...new Set(nextMessages)] : result?.messages,
      };
    }
    return result;
  }

  const meta = context?.cardSnapshot?.meta || {};
  const profileName = cleanStr(meta.partyName);
  const profileNationality = cleanStr(meta.nationalityIso2 || meta.incorporationIso2);
  const profileDob = cleanStr(meta.dateOfBirth || meta.dob);
  const checks = [...existingChecks];
  const messages = Array.isArray(result?.messages) ? [...result.messages] : [];

  const extractedName = cleanStr(extractedData.fullName);
  if (profileName && extractedName) {
    const nameMatch = normalizeStaffAttachName(profileName) === normalizeStaffAttachName(extractedName);
    checks.push({
      type: "name",
      label: "Name",
      status: nameMatch ? "match" : "critical_mismatch",
      message: nameMatch ? "Name is consistent with the current KYC profile." : "Name differs from the current KYC profile.",
      extractedValue: extractedName,
      profileValue: profileName,
    });
    if (!nameMatch) messages.push("The name differs from the current KYC profile.");
  }

  const extractedDob = normalizeStaffAttachDate(extractedData.dateOfBirth);
  const normalizedProfileDob = normalizeStaffAttachDate(profileDob);
  if (normalizedProfileDob && extractedDob) {
    const dobMatch = normalizedProfileDob === extractedDob;
    checks.push({
      type: "dateOfBirth",
      label: "Date of birth",
      status: dobMatch ? "match" : "critical_mismatch",
      message: dobMatch ? "Date of birth matches the current KYC profile." : "Date of birth differs from the current KYC profile.",
      extractedValue: extractedDob,
      profileValue: normalizedProfileDob,
    });
    if (!dobMatch) messages.push("The date of birth differs from the current KYC profile.");
  }

  const extractedNationality = normalizeStaffAttachCountry(extractedData.nationality);
  const normalizedProfileNationality = normalizeStaffAttachCountry(profileNationality);
  if (normalizedProfileNationality && extractedNationality) {
    const nationalityMatch = normalizedProfileNationality === extractedNationality;
    checks.push({
      type: "nationality",
      label: "Nationality",
      status: nationalityMatch ? "match" : "critical_mismatch",
      message: nationalityMatch ? "Nationality is consistent with the current KYC profile." : "Nationality differs from the current KYC profile.",
      extractedValue: extractedData.nationality,
      profileValue: profileNationality,
    });
    if (!nationalityMatch) messages.push("The nationality differs from the current KYC profile.");
  }

  const hasExpiryCheck = existingChecks.some((check) => cleanStr(check?.type) === "expiryDate");
  const extractedExpiry = normalizeStaffAttachDate(extractedData.expiryDate);
  if (!hasExpiryCheck && /^\d{4}-\d{2}-\d{2}$/.test(extractedExpiry)) {
    const expired = extractedExpiry < todayStaffAttachDateOnly();
    checks.push({
      type: "expiryDate",
      label: "Expiry date",
      status: expired ? "expired" : "match",
      message: expired ? "The document is expired." : "The document is not expired.",
      extractedValue: extractedData.expiryDate || extractedExpiry,
    });
    if (expired) messages.push("The document is expired.");
  }

  if (checks.length === existingChecks.length) return result;
  let overallStatus = cleanStr(result?.overallStatus || result?.status);
  if (checks.some((check) => cleanStr(check.status) === "critical_mismatch")) {
    overallStatus = "critical_mismatch";
  } else if (checks.some((check) => cleanStr(check.status) === "manual_review")) {
    overallStatus = "manual_review";
  } else if (!overallStatus || overallStatus === "extracted") {
    overallStatus = "clear";
  }

  return {
    ...result,
    liveComparison: true,
    overallStatus,
    checks,
    messages: messages.length ? [...new Set(messages)] : result?.messages,
  };
}

function staffAttachScanKind(result, context = {}) {
  const scanType = cleanStr(result?.scanType);
  const text = cleanStr(`${context?.requirementText || ""} ${context?.cardTitle || ""}`).toLowerCase();
  if (scanType.includes("proof_of_address") || text.includes("proof of address") || text.includes("utility")) return "utility";
  if (scanType.includes("corporate") || text.includes("corporate") || text.includes("company") || text.includes("register")) return "corporate";
  if (text.includes("source of wealth") || text.includes("sow")) return "sow";
  return "id";
}

function staffAttachDocumentIcon(kind) {
  if (kind === "utility") return iconUtilityBlueDoc;
  if (kind === "corporate") return iconCorporateDocBlue;
  if (kind === "sow") return iconSowBlueDoc;
  return iconIdCardBlueDoc;
}

function staffAttachDocumentTitle(kind) {
  if (kind === "utility") return "UTILITY DOCUMENT";
  if (kind === "corporate") return "CORPORATE DOCUMENT";
  if (kind === "sow") return "SOURCE OF WEALTH DOCUMENT";
  return "IDENTIFICATION DOCUMENT";
}

function staffAttachReasonLabelForCheck(check, kind) {
  const type = cleanStr(check?.type);
  const status = cleanStr(check?.status);
  const extracted = cleanStr(check?.extractedValue);
  const profile = cleanStr(check?.profileValue || check?.expectedValue);
  if (
    status === "match" ||
    status === "pass" ||
    status === "passed" ||
    status === "clear" ||
    status === "skipped" ||
    status === "extracted"
  ) {
    return "";
  }
  if (kind === "utility") {
    if (type === "document_type") return "";
    if (type === "issue_date") {
      const msg = cleanStr(check?.message);
      if (/missing/i.test(msg)) return "Issue date missing";
      if (/future/i.test(msg)) return "Issue date invalid";
      if (/older/i.test(msg)) return "Document too old";
      return "Issue date issue";
    }
    if (type === "name_match") return status === "warning" ? "Name to review" : "Name differs";
    if (type === "country_match" || type === "address_country_match" || type === "issuer_country_match") return "Domicile differs";
    if (type === "issuer_check") return "Issuer not recognized";
    if (type === "extraction_confidence") return "Low confidence";
  }
  if (kind === "corporate") {
    if (type === "corporate_company_name_match" || type.includes("name")) return extracted && profile ? "Company name differs" : "Company name not read";
    if (type === "corporate_country_match") return "Country differs";
    if (type === "corporate_incorporation_date_match" || type.includes("incorporation")) return "Incorporation date differs";
    if (type === "corporate_registered_address_match" || type.includes("address")) return "Registered address differs";
    if (type === "corporate_document_type") return "";
    if (type === "corporate_issue_date") return extracted ? "Document too old" : "No issue date identified";
    if (type === "corporate_entity_status") return "Entity status needs review";
    if (type.includes("registration")) return "Registration number differs";
    if (type.includes("legal")) return "Legal form to review";
  }
  if (type === "name") return !extracted ? "Name not read" : !profile ? "Name missing in KYC" : "Name differs";
  if (type === "dateOfBirth") return !extracted ? "DOB not read" : !profile ? "DOB missing in KYC" : "DOB differs";
  if (type === "nationality") return !extracted ? "Nationality not read" : !profile ? "Nationality missing in KYC" : "Nationality differs";
  if (type === "expiryDate") return status === "expired" ? "Expired" : "Expiry date issue";
  if (type === "mrzChecksum") return /partially verified/i.test(cleanStr(check?.message)) ? "MRZ partially verified" : "MRZ checksum failed";
  if (type === "ocrConfidence") return "Low OCR confidence";
  if (status === "critical_mismatch") return kind === "id" ? "ID data differs" : "Data differs";
  return "";
}

function isStaffAttachFailedDocumentTypeCheck(check, expectedType) {
  if (cleanStr(check?.type) !== expectedType) return false;
  const status = cleanStr(check?.status);
  return !["pass", "match", "passed", "clear", "skipped"].includes(status);
}

function staffAttachNoBadgeScanMessage(result, kind) {
  const checks = Array.isArray(result?.checks) ? result.checks : [];
  if (
    kind === "id" &&
    (
      cleanStr(result?.overallStatus || result?.status) === "no_id_data_detected" ||
      cleanStr(result?.idExtractionStatus) === "not_detected" ||
      cleanStr(result?.displayStatus) === "none"
    )
  ) {
    return "Not recognized as identification document. Please review.";
  }
  if (kind === "utility" && checks.some((check) => isStaffAttachFailedDocumentTypeCheck(check, "document_type"))) {
    return "Not recognized as proof of address. Please review.";
  }
  if (kind === "corporate" && checks.some((check) => isStaffAttachFailedDocumentTypeCheck(check, "corporate_document_type"))) {
    return "Not recognized as required corporate document. Please review.";
  }
  if (kind === "corporate") return corporateScanPresentation(result).message;
  return "No extracted findings";
}

function staffAttachReasonBadgeClass(reason, kind) {
  const check = reason?.check || {};
  const type = cleanStr(check?.type || reason?.type);
  const label = cleanStr(reason?.label);
  if (kind === "utility" && type === "issue_date" && label === "Document too old") {
    return "isExpired";
  }
  if (kind === "corporate") {
    if (type === "corporate_company_name_match" || type === "corporate_country_match") {
      return "isCritical";
    }
    if (
      type === "corporate_incorporation_date_match" ||
      type === "corporate_registered_address_match" ||
      type === "corporate_document_type" ||
      type === "corporate_issue_date" ||
      type === "corporate_entity_status"
    ) {
      return "isManual";
    }
  }
  return scanBadgeClass(reason?.status);
}

function staffAttachCorporateDisplayType(extracted = {}, check = null) {
  const matchedLabel = cleanStr(
    check?.matchedExpectedDocumentLabel ||
      check?.matched_expected_document_label ||
      extracted?.matched_expected_document_label
  );
  const matchConfidence = Math.max(0, Math.min(1, Number(check?.matchConfidence ?? extracted?.match_confidence ?? 0)));
  const isMatched =
    extracted?.is_one_of_expected_document_types === true &&
    !!matchedLabel &&
    matchConfidence >= 0.75;
  if (isMatched) return matchedLabel;
  return (
    cleanStr(
      check?.identifiedDocumentType ||
        check?.identified_document_type ||
        extracted?.semantic_document_type ||
        extracted?.identified_document_type
    ) ||
    cleanStr(extracted?.visible_document_title) ||
    "Not available"
  );
}

function sortKycUpdateOptionsAddFirst(options = []) {
  return (Array.isArray(options) ? options : [])
    .map((option, index) => ({ option, index }))
    .sort((a, b) => {
      const aIsAdd = /^add\b/i.test(cleanStr(a.option?.label));
      const bIsAdd = /^add\b/i.test(cleanStr(b.option?.label));
      if (aIsAdd !== bIsAdd) return aIsAdd ? -1 : 1;
      return a.index - b.index;
    })
    .map(({ option }) => option);
}

function hasSelectedKycUpdateOption(options = [], selected = {}) {
  return (Array.isArray(options) ? options : []).some((option) => selected?.[option?.key] !== false);
}

function isStaffAttachUtilityDomicileCheck(check) {
  const type = cleanStr(check?.type);
  return type === "country_match" || type === "address_country_match" || type === "issuer_country_match";
}

function staffAttachReasons(result, kind) {
  const checks = Array.isArray(result?.checks) ? result.checks : [];
  if (kind === "utility" && checks.some((check) => isStaffAttachFailedDocumentTypeCheck(check, "document_type"))) {
    return [];
  }
  if (kind === "corporate" && checks.some((check) => isStaffAttachFailedDocumentTypeCheck(check, "corporate_document_type"))) {
    return [];
  }
  const rawReasons = checks
    .map((check, index) => ({
      key: `${cleanStr(check?.type)}_${cleanStr(check?.status)}_${index}`,
      label: staffAttachReasonLabelForCheck(check, kind),
      status: cleanStr(check?.status || result?.overallStatus || result?.status),
      check,
    }))
    .filter((reason) => reason.label);
  const domicileReasons =
    kind === "utility"
      ? rawReasons.filter((reason) => isStaffAttachUtilityDomicileCheck(reason.check))
      : [];
  const reasons =
    kind === "utility"
      ? rawReasons.filter((reason) => !isStaffAttachUtilityDomicileCheck(reason.check))
      : rawReasons;
  if (domicileReasons.length) {
    reasons.push({
      key: "domicile_differs",
      label: "Domicile differs",
      status: domicileReasons.some((reason) => cleanStr(reason.status) === "failed" || cleanStr(reason.status) === "fail")
        ? "failed"
        : domicileReasons[0]?.status || "warning",
      check: domicileReasons[0]?.check,
      checks: domicileReasons.map((reason) => reason.check).filter(Boolean),
    });
  }
  if (reasons.length) return reasons.slice(0, 6);
  const status = cleanStr(result?.overallStatus || result?.status);
  if (status === "clear" || status === "passed") {
    const label =
      kind === "id"
        ? "ID data consistent"
        : kind === "utility"
          ? "Utility data consistent"
          : kind === "corporate"
            ? "Corporate data consistent"
            : "Data consistent";
    return [{ key: "clear", label, status }];
  }
  return [];
}

function staffAttachDetailForReason(result, reason, kind) {
  const check = reason?.check || {};
  const type = cleanStr(check?.type);
  const reasonChecks = Array.isArray(reason?.checks) ? reason.checks : [];
  const extracted = result?.extracted || result?.extractedData || {};
  const expected = result?.expectedParty || {};
  const rows = [];
  if (kind === "utility" && type === "issue_date") {
    rows.push({
      label: "Date of issuance",
      value: formatStaffAttachDate(check?.extractedValue || extracted.issueDate || extracted.issue_date),
    });
    if (check?.message) rows.push({ label: "Status", value: cleanStr(check.message) });
  } else if (kind === "utility" && type === "name_match") {
    rows.push({
      label: "Document",
      value: formatStaffAttachName(check?.extractedValue || extracted.client_name || extracted.name || extracted.fullName) || "Not available",
    });
    rows.push({
      label: "KYC profile",
      value: cleanStr(check?.expectedValue || expected.partyName) || "Not available",
    });
  } else if (
    kind === "utility" &&
    (isStaffAttachUtilityDomicileCheck(check) || reasonChecks.some(isStaffAttachUtilityDomicileCheck))
  ) {
    const domicileChecks = (reasonChecks.length ? reasonChecks : [check]).filter(isStaffAttachUtilityDomicileCheck);
    const addressCheck = domicileChecks.find((item) => {
      const itemType = cleanStr(item?.type);
      return itemType === "country_match" || itemType === "address_country_match";
    });
    const issuerCountryCheck = domicileChecks.find((item) => cleanStr(item?.type) === "issuer_country_match");
    const documentDomicileRows = [];
    if (addressCheck) {
      documentDomicileRows.push({
        label: "Document domicile",
        value: formatStaffAttachCountry(
          addressCheck?.extractedValue ||
            extracted.country_iso2 ||
            extracted.country ||
            extracted.address_country_iso2 ||
            extracted.addressCountry
        ),
      });
    }
    if (issuerCountryCheck) {
      documentDomicileRows.push({
        label: "Issuer country",
        value: formatStaffAttachCountry(
          issuerCountryCheck?.extractedValue ||
            extracted.issuer_country_iso2
        ),
      });
    }
    if (documentDomicileRows.length > 1) {
      rows.push({
        key: "document_domicile_sources",
        rows: documentDomicileRows,
      });
    } else {
      rows.push(...documentDomicileRows);
    }
    rows.push({
      label: "KYC Domicile",
      value: formatStaffAttachCountry(
        addressCheck?.expectedValue ||
          issuerCountryCheck?.expectedValue ||
          check?.expectedValue ||
          expected.domicileIso2 ||
          expected.incorporationIso2
      ),
    });
  } else if (kind === "utility" && type === "issuer_check") {
    rows.push({ label: "Issuer", value: cleanStr(check?.extractedValue || extracted.issuer_name || extracted.issuerName) || "Not available" });
    rows.push({ label: "Status", value: "Issuer not recognized. To be verified." });
  } else if (kind === "utility" && type === "document_type") {
    rows.push({ label: "Document type", value: cleanStr(check?.extractedValue || extracted.document_type || extracted.documentType) || "Not available" });
  } else if (kind === "utility" && type === "extraction_confidence") {
    const confidence = Number(check?.extractedValue || result?.extractionConfidence);
    rows.push({
      label: "Confidence",
      value: Number.isFinite(confidence) ? `${Math.round(Math.max(0, Math.min(1, confidence)) * 100)}%` : "n/a",
    });
  } else if (kind === "corporate" && type === "corporate_company_name_match") {
    rows.push({ label: "Document", value: formatStaffAttachName(check?.extractedValue || extracted.company_name || extracted.companyName) || "Not available" });
    rows.push({ label: "KYC profile", value: cleanStr(check?.expectedValue || expected.partyName) || "Not available" });
  } else if (kind === "corporate" && type === "corporate_country_match") {
    rows.push({ label: "Document country", value: formatStaffAttachCountry(check?.extractedValue || extracted.jurisdiction_country_iso2 || extracted.issuer_country_iso2) });
    rows.push({ label: "KYC incorporation country", value: formatStaffAttachCountry(check?.expectedValue || expected.incorporationIso2) });
  } else if (kind === "corporate" && type === "corporate_incorporation_date_match") {
    rows.push({ label: "Document", value: formatStaffAttachDate(check?.extractedValue || extracted.incorporation_date || extracted.incorporationDate) });
    rows.push({ label: "KYC profile", value: formatStaffAttachDate(check?.expectedValue || expected.incorporationDate) });
  } else if (kind === "corporate" && type === "corporate_registered_address_match") {
    rows.push({ label: "Document", value: cleanStr(check?.extractedValue || extracted.registered_address || extracted.registeredAddress) || "Not available" });
    rows.push({ label: "KYC profile", value: cleanStr(check?.expectedValue || expected.registeredAddress) || "Not available" });
  } else if (kind === "corporate" && type === "corporate_document_type") {
    const expectedLabels = Array.isArray(result?.expectedDocumentLabels) ? result.expectedDocumentLabels : [];
    rows.push({ label: "Document type", value: staffAttachCorporateDisplayType(extracted, check) });
    rows.push({ label: "Expected", value: cleanStr(check?.expectedValue) || expectedLabels.join(", ") || "Not available" });
  } else if (kind === "corporate" && type === "corporate_issue_date") {
    rows.push({ label: "Issue date", value: formatStaffAttachDate(check?.extractedValue || extracted.issue_date || extracted.issueDate) });
  } else if (kind === "corporate" && type === "corporate_entity_status") {
    rows.push({ label: "Entity status", value: cleanStr(check?.extractedValue || extracted.entity_status || extracted.entityStatus) || "Not available" });
  } else if (type === "nationality") {
    rows.push({ label: "KYC nationality", value: formatStaffAttachCountry(check?.profileValue || check?.expectedValue) });
    rows.push({ label: "ID nationality", value: formatStaffAttachCountry(check?.extractedValue) });
  } else if (type === "dateOfBirth") {
    rows.push({ label: "KYC DOB", value: formatStaffAttachDate(check?.profileValue || check?.expectedValue) });
    rows.push({ label: "ID DOB", value: formatStaffAttachDate(check?.extractedValue) });
  } else if (type === "expiryDate") {
    rows.push({ label: "ID expiry date", value: formatStaffAttachDate(check?.extractedValue || extracted.expiryDate) });
    rows.push({ label: "Status", value: cleanStr(check?.message) || "The document is expired." });
  } else if (type === "name") {
    rows.push({ label: "KYC name", value: formatStaffAttachName(check?.profileValue || check?.expectedValue) || "Not available" });
    rows.push({ label: "ID name", value: formatStaffAttachName(check?.extractedValue) || "Not available" });
  } else if (type === "mrzChecksum") {
    rows.push({ label: "MRZ checksum", value: result?.mrzChecksumValid === true ? "Valid" : "Failed" });
  } else if (check?.message) {
    rows.push({ label: "Message", value: cleanStr(check.message) });
  }

  const sectionRows =
    kind === "id"
      ? [
          { label: "Full name", value: formatStaffAttachName(extracted.fullName) || "Not available" },
          { label: "Date of birth", value: formatStaffAttachDate(extracted.dateOfBirth) },
          { label: "Nationality", value: formatStaffAttachCountry(extracted.nationality) },
          { label: "Expiry date", value: formatStaffAttachDate(extracted.expiryDate) },
          { label: "Issuing country", value: formatStaffAttachCountry(extracted.issuingCountry) },
        ]
      : kind === "utility"
        ? [
            { label: "Document name", value: formatStaffAttachName(extracted.name || extracted.fullName) || "Not available" },
            { label: "Issue date", value: formatStaffAttachDate(extracted.issueDate || extracted.issue_date) },
            { label: "Document domicile", value: formatStaffAttachCountry(extracted.addressCountry || extracted.address_country_iso2 || extracted.country_iso2 || extracted.country) },
            { label: "Issuer country", value: formatStaffAttachCountry(extracted.issuerCountry || extracted.issuer_country_iso2 || extracted.issuer_country) },
          ]
        : [
            { label: "Document type", value: staffAttachCorporateDisplayType(extracted) },
            { label: "Company name", value: formatStaffAttachName(extracted.companyName || extracted.company_name || extracted.name) || "Not available" },
            { label: "Registration number", value: cleanStr(extracted.registrationNumber || extracted.registration_number) || "Not available" },
            { label: "Incorporation date", value: formatStaffAttachDate(extracted.incorporationDate || extracted.incorporation_date) },
            { label: "Registered address", value: cleanStr(extracted.registeredAddress || extracted.registered_address) || "Not available" },
            { label: "Issue date", value: formatStaffAttachDate(extracted.issueDate || extracted.issue_date) },
            { label: "Issuer country", value: formatStaffAttachCountry(extracted.issuerCountry || extracted.issuer_country_iso2 || extracted.issuer_country) },
            { label: "Entity status", value: cleanStr(extracted.entityStatus || extracted.entity_status) || "Not available" },
            { label: "Legal form", value: cleanStr(extracted.legalForm || extracted.legal_form) || "Not available" },
          ];

  return {
    detailKey: `${kind}:${cleanStr(reason?.key || reason?.label || type || "scan-detail")}`,
    title: reason?.label || "Scan outcome",
    documentKind: kind,
    reasonTitle: reason?.label || "Scan outcome",
    reasonEyebrow: cleanStr(reason?.status) === "clear" || cleanStr(reason?.status) === "passed" ? "Check passed" : "Issue",
    status: reason?.status || result?.overallStatus || result?.status,
    rows,
    sections: [
      {
        title: kind === "id" ? "Identification document extracted" : kind === "utility" ? "Utility document extracted" : "Corporate document extracted",
        variant: kind === "id" ? "idDocumentDetails" : kind === "utility" ? "utilityDocumentDetails" : "corporateDocumentDetails",
        rows: sectionRows,
      },
    ],
  };
}

function normalizeStaffAttachKycName(value) {
  return normalizeStaffAttachName(value);
}

function staffAttachDateToFirestoreDate(value) {
  const normalized = normalizeStaffAttachDate(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return null;
  const date = new Date(`${normalized}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function staffAttachReviewMeta(context = {}) {
  const meta = context?.cardSnapshot?.meta || {};
  return {
    kind: cleanStr(meta.kind),
    partyId: cleanStr(meta.partyId),
    partyName: cleanStr(meta.partyName),
    partyRole: cleanStr(meta.partyRole),
    dateOfBirth:
      meta.dateOfBirth ||
      meta.dob ||
      meta.dobOrIncorporationDate ||
      "",
    nationalityIso2: cleanStr(meta.nationalityIso2),
    incorporationIso2: cleanStr(meta.incorporationIso2),
  };
}

function staffAttachProfileFromReviewMeta(reviewMeta = null) {
  if (!reviewMeta || typeof reviewMeta !== "object") return null;
  const partyName = cleanStr(reviewMeta.partyName);
  const dateOfBirth =
    reviewMeta.dateOfBirth ||
    reviewMeta.dob ||
    reviewMeta.dobOrIncorporationDate ||
    "";
  const kind = cleanStr(reviewMeta.kind);
  const nationality =
    kind === "entity"
      ? cleanStr(reviewMeta.incorporationIso2 || reviewMeta.nationalityIso2)
      : cleanStr(reviewMeta.nationalityIso2 || reviewMeta.incorporationIso2);

  if (!partyName && !dateOfBirth && !nationality) return null;
  return {
    fullName: partyName,
    dateOfBirth,
    nationality,
    partyId: cleanStr(reviewMeta.partyId),
    partyRole: cleanStr(reviewMeta.partyRole),
    partyType: kind,
  };
}

function staffAttachCurrentKycProfile(form = {}, reviewMeta = null) {
  const customer = form?.customer || {};
  const personal = form?.personal || {};
  const identity = form?.identity || {};
  const fallback = {
    fullName: cleanStr(
      customer.name ||
        personal.fullName ||
        identity.fullName ||
        ""
    ),
    dateOfBirth:
      customer.dobOrIncorporationDate ||
      customer.dateOfBirth ||
      customer.dob ||
      personal.dobOrIncorporationDate ||
      personal.dateOfBirth ||
      personal.dob ||
      identity.dobOrIncorporationDate ||
      identity.dateOfBirth ||
      "",
    nationality: cleanStr(
      customer.nationalityOrIncorporationCountry ||
        customer.nationality ||
        customer.nationalityIso3 ||
        personal.nationality ||
        identity.nationality ||
        ""
    ),
    partyId: "customer",
    partyRole: "",
    partyType: normPartyType(customer.type) || "individual",
  };
  const metaProfile = staffAttachProfileFromReviewMeta(reviewMeta);
  if (!metaProfile) return fallback;
  if (metaProfile.partyId && metaProfile.partyId !== "customer") return metaProfile;
  return {
    ...fallback,
    ...metaProfile,
    fullName: metaProfile.fullName || fallback.fullName,
    dateOfBirth: metaProfile.dateOfBirth || fallback.dateOfBirth,
    nationality: metaProfile.nationality || fallback.nationality,
    partyType: metaProfile.partyType || fallback.partyType,
  };
}

function staffAttachReviewMetaLooksNonCustomerIndividual(reviewMeta = null) {
  const partyId = cleanStr(reviewMeta?.partyId);
  if (!partyId || partyId === "customer") return false;
  if (normPartyType(reviewMeta?.kind) === "individual") return true;
  const role = cleanStr(reviewMeta?.partyRole).toLowerCase();
  return (
    role.includes("ubo") ||
    role.includes("direct shareholder") ||
    role.includes("signatory") ||
    role.includes("trustee") ||
    role.includes("trust party")
  );
}

function staffAttachShortPartyRoleLabel(reviewMeta = null) {
  const role = cleanStr(reviewMeta?.partyRole);
  const lower = role.toLowerCase();
  if (lower.includes("ubo")) return "UBO";
  if (lower.includes("direct shareholder")) return "direct shareholder";
  if (lower.includes("signatory")) return "signatory";
  if (lower.includes("trustee")) return "trustee";
  if (lower.includes("trust")) return "trust party";
  return "party";
}

function staffAttachPartyDobUpdateTarget(reviewMeta = null) {
  const partyId = cleanStr(reviewMeta?.partyId);
  if (!partyId || partyId === "customer") return { kind: "customer" };
  const parts = partyId.split(":");
  const type = cleanStr(parts[0]);
  if (type === "shareholder") {
    return { kind: "arrayRow", collection: "shareholders", rowId: parts.slice(1).join(":") };
  }
  if (type === "ubo") {
    return { kind: "arrayRow", collection: "ubos", rowId: parts.slice(1).join(":") };
  }
  if (type === "signatory") {
    return { kind: "arrayRow", collection: "signatories", rowId: parts.slice(1).join(":") };
  }
  if (type === "trustee") {
    return {
      kind: "legalArrangementParty",
      arrangementId: cleanStr(parts[1]),
      partyId: parts.slice(2).join(":"),
    };
  }
  const role = cleanStr(reviewMeta?.partyRole).toLowerCase();
  const fallbackRowId = cleanStr(reviewMeta?.partyName);
  if (!fallbackRowId) return null;
  if (role.includes("ubo")) return { kind: "arrayRow", collection: "ubos", rowId: fallbackRowId };
  if (role.includes("direct shareholder")) return { kind: "arrayRow", collection: "shareholders", rowId: fallbackRowId };
  if (role.includes("signatory")) return { kind: "arrayRow", collection: "signatories", rowId: fallbackRowId };
  return null;
}

function staffAttachIdUpdateOptions({ result, form, context }) {
  const reviewMeta = staffAttachReviewMeta(context);
  const metaPartyId = cleanStr(reviewMeta?.partyId);
  const isCustomerParty = !metaPartyId || metaPartyId === "customer";
  const isNonCustomerIndividual = staffAttachReviewMetaLooksNonCustomerIndividual(reviewMeta);
  const extractedData = result?.extractedData || {};
  const checks = Array.isArray(result?.checks) ? result.checks : [];
  const checkForType = (type) => checks.find((check) => cleanStr(check?.type) === type) || null;
  const nameCheck = checkForType("name");
  const dobCheck = checkForType("dateOfBirth");
  const nationalityCheck = checkForType("nationality");
  const profile = staffAttachCurrentKycProfile(form, reviewMeta);
  const dob = normalizeStaffAttachDate(extractedData.dateOfBirth || dobCheck?.extractedValue);
  const profileDob = normalizeStaffAttachDate(profile.dateOfBirth);
  const nationality = normalizeStaffAttachCountry(extractedData.nationality || nationalityCheck?.extractedValue);
  const profileNationality = normalizeStaffAttachCountry(profile.nationality);
  const fullName = formatStaffAttachName(extractedData.fullName || nameCheck?.extractedValue);
  const profileFullName = cleanStr(profile.fullName);
  const nationalityIso2 =
    /^[A-Z]{3}$/.test(nationality) ? countries.alpha3ToAlpha2(nationality) || "" : nationality;
  const options = [];
  const dobUpdateTarget = isCustomerParty ? { kind: "customer" } : staffAttachPartyDobUpdateTarget(reviewMeta);
  const roleLabel = isCustomerParty ? "KYC" : staffAttachShortPartyRoleLabel(reviewMeta);

  if (
    isCustomerParty &&
    fullName &&
    profileFullName &&
    normalizeStaffAttachKycName(fullName) !== normalizeStaffAttachKycName(profileFullName)
  ) {
    options.push({
      key: "fullName",
      label: `Update full name in KYC profile: ${profileFullName} -> ${fullName}`,
      value: fullName,
      nameParts: extractedNameFields(extractedData),
    });
  }

  if (
    !isCustomerParty &&
    isNonCustomerIndividual &&
    fullName &&
    profileFullName &&
    normalizeStaffAttachKycName(fullName) !== normalizeStaffAttachKycName(profileFullName) &&
    dobUpdateTarget
  ) {
    options.push({
      key: "partyName",
      label: `Update name in ${roleLabel} profile: ${profileFullName} -> ${fullName}`,
      value: fullName,
      nameParts: extractedNameFields(extractedData),
      updateTarget: dobUpdateTarget,
    });
  }

  if ((isCustomerParty || isNonCustomerIndividual) && dob && !profileDob && dobUpdateTarget) {
    options.push({
      key: "dob",
      label: isCustomerParty
        ? `Add DOB to KYC profile: ${formatStaffAttachDate(dob)}`
        : `Add DOB to ${roleLabel} profile: ${formatStaffAttachDate(dob)}`,
      value: dob,
      updateTarget: dobUpdateTarget,
    });
  }

  if ((isCustomerParty || isNonCustomerIndividual) && dob && profileDob && dob !== profileDob && dobUpdateTarget) {
    options.push({
      key: "dob",
      label: isCustomerParty
        ? `Update DOB in KYC profile: ${formatStaffAttachDate(profileDob)} -> ${formatStaffAttachDate(dob)}`
        : `Update DOB in ${roleLabel} profile: ${formatStaffAttachDate(profileDob)} -> ${formatStaffAttachDate(dob)}`,
      value: dob,
      updateTarget: dobUpdateTarget,
    });
  }

  if (isCustomerParty && nationality && !profileNationality) {
    options.push({
      key: "nationality",
      label: `Add nationality to KYC profile: ${formatStaffAttachCountry(nationality)}`,
      value: nationality,
      kycValue: nationalityIso2 || nationality,
    });
  }

  if (isCustomerParty && nationality && profileNationality && nationality !== profileNationality) {
    options.push({
      key: "nationality",
      label: `Update nationality in KYC profile: ${formatStaffAttachCountry(profileNationality)} -> ${formatStaffAttachCountry(nationality)}`,
      value: nationality,
      kycValue: nationalityIso2 || nationality,
    });
  }

  if (!isCustomerParty && isNonCustomerIndividual && nationality && !profileNationality && dobUpdateTarget) {
    options.push({
      key: "partyNationality",
      label: `Add nationality to ${roleLabel} profile: ${formatStaffAttachCountry(nationality)}`,
      value: nationality,
      kycValue: nationalityIso2 || nationality,
      updateTarget: dobUpdateTarget,
    });
  }

  if (!isCustomerParty && isNonCustomerIndividual && nationality && profileNationality && nationality !== profileNationality && dobUpdateTarget) {
    options.push({
      key: "partyNationality",
      label: `Update nationality in ${roleLabel} profile: ${formatStaffAttachCountry(profileNationality)} -> ${formatStaffAttachCountry(nationality)}`,
      value: nationality,
      kycValue: nationalityIso2 || nationality,
      updateTarget: dobUpdateTarget,
    });
  }

  return options;
}

function staffAttachProofOfAddressUpdateOptions({ result, form, context }) {
  if (!result) return [];
  const reviewMeta = staffAttachReviewMeta(context);
  const isCustomerParty = !cleanStr(reviewMeta?.partyId) || cleanStr(reviewMeta?.partyId) === "customer";
  const profile = staffAttachCurrentKycProfile(form, reviewMeta);
  const isIndividualParty =
    normPartyType(profile?.partyType) === "individual" ||
    normPartyType(reviewMeta?.kind) === "individual" ||
    (isCustomerParty && normPartyType(form?.customer?.type || "individual") === "individual");
  if (!isIndividualParty) return [];

  const checks = Array.isArray(result?.checks) ? result.checks : [];
  const checkForType = (type) => checks.find((check) => cleanStr(check?.type) === type) || null;
  const nameCheck = checkForType("name_match");
  const addressCountryCheck =
    checkForType("address_country_match") || checkForType("country_match") || null;
  const issuerCountryCheck = checkForType("issuer_country_match");
  const domicileCheck = addressCountryCheck || issuerCountryCheck;
  const extracted = result?.extracted || {};
  const expected = result?.expectedParty || {};
  const updateTarget = isCustomerParty ? { kind: "customer" } : staffAttachPartyDobUpdateTarget(reviewMeta);
  const roleLabel = isCustomerParty ? "KYC" : staffAttachShortPartyRoleLabel(reviewMeta);
  const options = [];

  const documentName = formatStaffAttachName(
    nameCheck?.extractedValue || extracted.client_name || extracted.name || extracted.fullName
  );
  const profileName = cleanStr(
    nameCheck?.expectedValue || expected.partyName || profile.fullName
  );
  const nameStatus = cleanStr(nameCheck?.status);
  if (
    updateTarget &&
    documentName &&
    profileName &&
    nameStatus !== "pass" &&
    nameStatus !== "match" &&
    normalizeStaffAttachKycName(documentName) !== normalizeStaffAttachKycName(profileName)
  ) {
    options.push({
      key: isCustomerParty ? "poaCustomerName" : "poaPartyName",
      label: isCustomerParty
        ? `Update full name in KYC profile: ${profileName} -> ${documentName}`
        : `Update name in ${roleLabel} profile: ${profileName} -> ${documentName}`,
      value: documentName,
      updateTarget,
    });
  }

  const documentDomicile = normalizeStaffAttachCountry(
    domicileCheck?.extractedValue ||
      extracted.country_iso2 ||
      extracted.country ||
      extracted.address_country_iso2 ||
      extracted.issuer_country_iso2
  );
  const profileDomicile = normalizeStaffAttachCountry(
    domicileCheck?.expectedValue ||
      expected.domicileIso2 ||
      profile.domicile ||
      profile.countryOfResidence ||
      (isCustomerParty ? form?.customer?.country : "")
  );
  const domicileStatus = cleanStr(domicileCheck?.status);
  const domicileIso2 =
    /^[A-Z]{3}$/.test(documentDomicile)
      ? countries.alpha3ToAlpha2(documentDomicile) || ""
      : /^[A-Z]{2}$/.test(documentDomicile)
      ? documentDomicile
      : "";
  if (
    updateTarget &&
    documentDomicile &&
    domicileStatus !== "pass" &&
    domicileStatus !== "match" &&
    (!profileDomicile || documentDomicile !== profileDomicile)
  ) {
    options.push({
      key: isCustomerParty ? "poaCustomerDomicile" : "poaPartyDomicile",
      label: isCustomerParty
        ? `${profileDomicile ? "Update" : "Add"} domicile in KYC profile: ${
            profileDomicile ? `${formatStaffAttachCountry(profileDomicile)} -> ` : ""
          }${formatStaffAttachCountry(documentDomicile)}`
        : `${profileDomicile ? "Update" : "Add"} domicile in ${roleLabel} profile: ${
            profileDomicile ? `${formatStaffAttachCountry(profileDomicile)} -> ` : ""
          }${formatStaffAttachCountry(documentDomicile)}`,
      value: documentDomicile,
      kycValue: domicileIso2 || documentDomicile,
      updateTarget,
    });
  }

  return options;
}

function staffAttachCorporateUpdateOptions({ result, form }) {
  const extracted = result?.extracted || result?.extractedData || {};
  if (!result || !Object.keys(extracted).length) return [];
  const customer = form?.customer || {};
  const checks = Array.isArray(result?.checks) ? result.checks : [];
  const checkForType = (type) => checks.find((check) => cleanStr(check?.type) === type) || null;
  const checkIsNonPassing = (check) => {
    const status = cleanStr(check?.status);
    return !!check && status !== "pass" && status !== "passed" && status !== "match" && status !== "skipped";
  };
  const nameCheck = checkForType("corporate_company_name_match");
  const dateCheck = checkForType("corporate_incorporation_date_match");
  const countryCheck = checkForType("corporate_country_match");
  const rawDocumentName = cleanStr(nameCheck?.extractedValue || extracted.company_name || extracted.companyName || extracted.name);
  const documentName = formatStaffAttachName(rawDocumentName);
  const profileName = cleanStr(nameCheck?.expectedValue || customer.name);
  const documentDate = normalizeStaffAttachDate(dateCheck?.extractedValue || extracted.incorporation_date || extracted.incorporationDate);
  const profileDate = normalizeStaffAttachDate(dateCheck?.expectedValue || customer.dobOrIncorporationDate);
  const documentCountry = normalizeStaffAttachCountry(
    countryCheck?.extractedValue ||
      extracted.jurisdiction_country_iso2 ||
      extracted.jurisdiction_country ||
      extracted.issuer_country_iso2 ||
      extracted.issuer_country ||
      extracted.country
  );
  const profileCountry = normalizeStaffAttachCountry(
    countryCheck?.expectedValue || customer.nationalityOrIncorporationCountry
  );
  const documentCountryIso2 =
    /^[A-Z]{3}$/.test(documentCountry)
      ? countries.alpha3ToAlpha2(documentCountry) || ""
      : /^[A-Z]{2}$/.test(documentCountry)
      ? documentCountry
      : "";
  const options = [];

  if (
    documentName &&
    profileName &&
    (checkIsNonPassing(nameCheck) || normalizeStaffAttachKycName(documentName) !== normalizeStaffAttachKycName(profileName))
  ) {
    options.push({
      key: "corporateName",
      label: `Update company name in KYC profile: ${profileName} -> ${documentName}`,
      value: documentName,
    });
  }

  if (documentDate && profileDate && (checkIsNonPassing(dateCheck) || documentDate !== profileDate)) {
    options.push({
      key: "corporateIncorporationDate",
      label: `Update incorporation date in KYC profile: ${formatStaffAttachDate(profileDate)} -> ${formatStaffAttachDate(documentDate)}`,
      value: documentDate,
    });
  }

  if (documentCountry && profileCountry && (checkIsNonPassing(countryCheck) || documentCountry !== profileCountry)) {
    options.push({
      key: "corporateIncorporationCountry",
      label: `Update incorporation country in KYC profile: ${formatStaffAttachCountry(profileCountry)} -> ${formatStaffAttachCountry(documentCountry)}`,
      value: documentCountry,
      kycValue: documentCountryIso2 || documentCountry,
    });
  }

  return options;
}

function staffAttachSelectedUpdatesForOptions(options) {
  return (Array.isArray(options) ? options : []).reduce((acc, option) => {
    acc[option.key] = /^add\b/i.test(cleanStr(option?.label));
    return acc;
  }, {});
}

function staffAttachRowMatchesId(row, rowId) {
  const cleanId = cleanStr(rowId);
  if (!cleanId) return false;
  const cleanIdLower = cleanId.toLowerCase();
  return [row?._rowId, row?._docRef, row?.name].some((value) => {
    const candidate = cleanStr(value);
    return candidate === cleanId || candidate.toLowerCase() === cleanIdLower;
  });
}

function staffAttachUpdateOwnershipArrayRowDate(ownership = {}, collection, rowId, date) {
  const list = Array.isArray(ownership?.[collection]) ? ownership[collection] : [];
  let changed = false;
  const next = list.map((row) => {
    if (!staffAttachRowMatchesId(row, rowId)) return row;
    changed = true;
    return { ...(row || {}), dobOrIncorporationDate: date };
  });
  return changed ? next : null;
}

function staffAttachUpdateOwnershipArrayRowFields(ownership = {}, collection, rowId, fields = {}) {
  const list = Array.isArray(ownership?.[collection]) ? ownership[collection] : [];
  let changed = false;
  const next = list.map((row) => {
    if (!staffAttachRowMatchesId(row, rowId)) return row;
    changed = true;
    return { ...(row || {}), ...(fields || {}) };
  });
  return changed ? next : null;
}

function staffAttachRelatedShareholderIdsForUboRows(rows = [], rowId = "") {
  const ids = new Set();
  const cleanRowId = cleanStr(rowId);
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!staffAttachRowMatchesId(row, cleanRowId)) return;
    [
      row?.relatedShareholderRowId,
      row?.relatedShareholderNodeId,
      row?.treeNodeId,
      row?.subjectTreeNodeId,
      row?.subjectNodeId,
      row?.name,
    ].forEach((value) => {
      const clean = cleanStr(value);
      if (clean) ids.add(clean);
    });
  });
  if (cleanRowId) ids.add(cleanRowId);
  return Array.from(ids);
}

function staffAttachUpdateMatchingDirectShareholderDobForUbo(ownership = {}, uboRowId = "", date) {
  const shareholderTargets = staffAttachRelatedShareholderIdsForUboRows(ownership?.ubos, uboRowId);
  if (!shareholderTargets.length) return null;
  const shareholders = Array.isArray(ownership?.shareholders) ? ownership.shareholders : [];
  let changed = false;
  const nextShareholders = shareholders.map((row) => {
    const matches = shareholderTargets.some((target) => {
      const cleanTarget = cleanStr(target);
      if (!cleanTarget) return false;
      return (
        staffAttachRowMatchesId(row, cleanTarget) ||
        cleanStr(row?.treeNodeId) === cleanTarget ||
        cleanStr(row?.subjectNodeId) === cleanTarget
      );
    });
    if (!matches) return row;
    changed = true;
    return { ...(row || {}), dobOrIncorporationDate: date };
  });
  return changed ? nextShareholders : null;
}

function staffAttachUpdateMatchingDirectShareholderFieldsForUbo(ownership = {}, uboRowId = "", fields = {}) {
  const shareholderTargets = staffAttachRelatedShareholderIdsForUboRows(ownership?.ubos, uboRowId);
  if (!shareholderTargets.length) return null;
  const shareholders = Array.isArray(ownership?.shareholders) ? ownership.shareholders : [];
  let changed = false;
  const nextShareholders = shareholders.map((row) => {
    const matches = shareholderTargets.some((target) => {
      const cleanTarget = cleanStr(target);
      if (!cleanTarget) return false;
      return (
        staffAttachRowMatchesId(row, cleanTarget) ||
        cleanStr(row?.treeNodeId) === cleanTarget ||
        cleanStr(row?.subjectNodeId) === cleanTarget
      );
    });
    if (!matches) return row;
    changed = true;
    return { ...(row || {}), ...(fields || {}) };
  });
  return changed ? nextShareholders : null;
}

function staffAttachUpdateLegalArrangementPartyDate(ownership = {}, target, date) {
  const arrangements = Array.isArray(ownership?.legalArrangements?.arrangements)
    ? ownership.legalArrangements.arrangements
    : [];
  let changed = false;
  const nextArrangements = arrangements.map((arr, idx) => {
    const arrangementMatches =
      staffAttachRowMatchesId(arr, target?.arrangementId) ||
      cleanStr(target?.arrangementId) === String(idx);
    if (!arrangementMatches) return arr;
    const parties = Array.isArray(arr?.parties) ? arr.parties : [];
    let partyChanged = false;
    const nextParties = parties.map((party) => {
      if (!staffAttachRowMatchesId(party, target?.partyId)) return party;
      partyChanged = true;
      changed = true;
      return { ...(party || {}), dobOrIncorporationDate: date };
    });
    return partyChanged ? { ...(arr || {}), parties: nextParties } : arr;
  });
  return changed ? nextArrangements : null;
}

function staffAttachUpdateLegalArrangementPartyFields(ownership = {}, target, fields = {}) {
  const arrangements = Array.isArray(ownership?.legalArrangements?.arrangements)
    ? ownership.legalArrangements.arrangements
    : [];
  let changed = false;
  const nextArrangements = arrangements.map((arr, idx) => {
    const arrangementMatches =
      staffAttachRowMatchesId(arr, target?.arrangementId) ||
      cleanStr(target?.arrangementId) === String(idx);
    if (!arrangementMatches) return arr;
    const parties = Array.isArray(arr?.parties) ? arr.parties : [];
    let partyChanged = false;
    const nextParties = parties.map((party) => {
      if (!staffAttachRowMatchesId(party, target?.partyId)) return party;
      partyChanged = true;
      changed = true;
      return { ...(party || {}), ...(fields || {}) };
    });
    return partyChanged ? { ...(arr || {}), parties: nextParties } : arr;
  });
  return changed ? nextArrangements : null;
}

function staffAttachKycProfileUpdates({ options, selected, form }) {
  const selectedOptions = (Array.isArray(options) ? options : []).filter(
    (option) => selected?.[option.key]
  );
  if (!selectedOptions.length) return {};

  const updates = {};
  const ownership = form?.ownership || {};
  selectedOptions.forEach((option) => {
    if (option.key === "fullName") {
      const name = formatStaffAttachName(option.value);
      if (name) {
        updates["customer.name"] = name;
        updates["customer.givenNames"] = nameFields(option.nameParts).givenNames;
        updates["customer.familyName"] = nameFields(option.nameParts).familyName;
      }
    }
    if (option.key === "dob") {
      const date = staffAttachDateToFirestoreDate(option.value);
      if (!date) return;
      const target = option.updateTarget || { kind: "customer" };
      if (target.kind === "customer") {
        updates["customer.dobOrIncorporationDate"] = date;
      } else if (target.kind === "arrayRow") {
        const nextRows = staffAttachUpdateOwnershipArrayRowDate(
          ownership,
          target.collection,
          target.rowId,
          date
        );
        if (nextRows) updates[`ownership.${target.collection}`] = nextRows;
        if (target.collection === "ubos") {
          const nextShareholders = staffAttachUpdateMatchingDirectShareholderDobForUbo(
            ownership,
            target.rowId,
            date
          );
          if (nextShareholders) updates["ownership.shareholders"] = nextShareholders;
        }
      } else if (target.kind === "legalArrangementParty") {
        const nextArrangements = staffAttachUpdateLegalArrangementPartyDate(ownership, target, date);
        if (nextArrangements) {
          updates["ownership.legalArrangements.arrangements"] = nextArrangements;
        }
      }
    }
    if (option.key === "partyName") {
      const name = formatStaffAttachName(option.value);
      const target = option.updateTarget;
      if (!name || !target) return;
      if (target.kind === "arrayRow") {
        const nextRows = staffAttachUpdateOwnershipArrayRowFields(
          ownership,
          target.collection,
          target.rowId,
          { name, ...nameFields(option.nameParts) }
        );
        if (nextRows) {
          updates[`ownership.${target.collection}`] = nextRows;
        } else if (target.collection === "signatories") {
          const nextControllers = staffAttachUpdateOwnershipArrayRowFields(
            ownership,
            "controllers",
            target.rowId,
            { name, ...nameFields(option.nameParts) }
          );
          if (nextControllers) updates["ownership.controllers"] = nextControllers;
        }
        if (target.collection === "ubos") {
          const nextShareholders = staffAttachUpdateMatchingDirectShareholderFieldsForUbo(
            ownership,
            target.rowId,
            { name, ...nameFields(option.nameParts) }
          );
          if (nextShareholders) updates["ownership.shareholders"] = nextShareholders;
        }
      } else if (target.kind === "legalArrangementParty") {
        const nextArrangements = staffAttachUpdateLegalArrangementPartyFields(
          ownership,
          target,
          { name, ...nameFields(option.nameParts) }
        );
        if (nextArrangements) {
          updates["ownership.legalArrangements.arrangements"] = nextArrangements;
        }
      }
    }
    if (option.key === "nationality") {
      const nationality = cleanStr(option.kycValue || option.value).toUpperCase();
      if (nationality) updates["customer.nationalityOrIncorporationCountry"] = nationality;
    }
    if (option.key === "partyNationality") {
      const nationality = cleanStr(option.kycValue || option.value).toUpperCase();
      const target = option.updateTarget;
      if (!nationality || !target) return;
      if (target.kind === "arrayRow") {
        const nextRows = staffAttachUpdateOwnershipArrayRowFields(
          ownership,
          target.collection,
          target.rowId,
          { nationality }
        );
        if (nextRows) {
          updates[`ownership.${target.collection}`] = nextRows;
        } else if (target.collection === "signatories") {
          const nextControllers = staffAttachUpdateOwnershipArrayRowFields(
            ownership,
            "controllers",
            target.rowId,
            { nationality }
          );
          if (nextControllers) updates["ownership.controllers"] = nextControllers;
        }
        if (target.collection === "ubos") {
          const nextShareholders = staffAttachUpdateMatchingDirectShareholderFieldsForUbo(
            ownership,
            target.rowId,
            { nationality }
          );
          if (nextShareholders) updates["ownership.shareholders"] = nextShareholders;
        }
      } else if (target.kind === "legalArrangementParty") {
        const nextArrangements = staffAttachUpdateLegalArrangementPartyFields(
          ownership,
          target,
          { nationality }
        );
        if (nextArrangements) {
          updates["ownership.legalArrangements.arrangements"] = nextArrangements;
        }
      }
    }
    if (option.key === "poaCustomerName") {
      const name = formatStaffAttachName(option.value);
      if (name) {
        updates["customer.name"] = name;
        updates["customer.givenNames"] = nameFields(option.nameParts).givenNames;
        updates["customer.familyName"] = nameFields(option.nameParts).familyName;
      }
    }
    if (option.key === "poaPartyName") {
      const name = formatStaffAttachName(option.value);
      const target = option.updateTarget;
      if (!name || !target) return;
      if (target.kind === "arrayRow") {
        const nextRows = staffAttachUpdateOwnershipArrayRowFields(
          ownership,
          target.collection,
          target.rowId,
          { name }
        );
        if (nextRows) {
          updates[`ownership.${target.collection}`] = nextRows;
        } else if (target.collection === "signatories") {
          const nextControllers = staffAttachUpdateOwnershipArrayRowFields(
            ownership,
            "controllers",
            target.rowId,
            { name }
          );
          if (nextControllers) updates["ownership.controllers"] = nextControllers;
        }
        if (target.collection === "ubos") {
          const nextShareholders = staffAttachUpdateMatchingDirectShareholderFieldsForUbo(
            ownership,
            target.rowId,
            { name }
          );
          if (nextShareholders) updates["ownership.shareholders"] = nextShareholders;
        }
      } else if (target.kind === "legalArrangementParty") {
        const nextArrangements = staffAttachUpdateLegalArrangementPartyFields(
          ownership,
          target,
          { name }
        );
        if (nextArrangements) {
          updates["ownership.legalArrangements.arrangements"] = nextArrangements;
        }
      }
    }
    if (option.key === "poaCustomerDomicile") {
      const country = cleanStr(option.kycValue || option.value).toUpperCase();
      if (country) updates["customer.country"] = country;
    }
    if (option.key === "poaPartyDomicile") {
      const country = cleanStr(option.kycValue || option.value).toUpperCase();
      const target = option.updateTarget;
      if (!country || !target) return;
      if (target.kind === "arrayRow") {
        const nextRows = staffAttachUpdateOwnershipArrayRowFields(
          ownership,
          target.collection,
          target.rowId,
          { countryOfResidence: country }
        );
        if (nextRows) {
          updates[`ownership.${target.collection}`] = nextRows;
        } else if (target.collection === "signatories") {
          const nextControllers = staffAttachUpdateOwnershipArrayRowFields(
            ownership,
            "controllers",
            target.rowId,
            { countryOfResidence: country }
          );
          if (nextControllers) updates["ownership.controllers"] = nextControllers;
        }
        if (target.collection === "ubos") {
          const nextShareholders = staffAttachUpdateMatchingDirectShareholderFieldsForUbo(
            ownership,
            target.rowId,
            { countryOfResidence: country }
          );
          if (nextShareholders) updates["ownership.shareholders"] = nextShareholders;
        }
      } else if (target.kind === "legalArrangementParty") {
        const nextArrangements = staffAttachUpdateLegalArrangementPartyFields(
          ownership,
          target,
          { domicile: country }
        );
        if (nextArrangements) {
          updates["ownership.legalArrangements.arrangements"] = nextArrangements;
        }
      }
    }
    if (option.key === "corporateName") {
      const name = formatStaffAttachName(option.value);
      if (name) {
        updates["customer.name"] = name;
        updates["customer.givenNames"] = nameFields(option.nameParts).givenNames;
        updates["customer.familyName"] = nameFields(option.nameParts).familyName;
      }
    }
    if (option.key === "corporateIncorporationDate") {
      const date = staffAttachDateToFirestoreDate(option.value);
      if (date) updates["customer.dobOrIncorporationDate"] = date;
    }
    if (option.key === "corporateIncorporationCountry") {
      const country = cleanStr(option.kycValue || option.value).toUpperCase();
      if (country) updates["customer.nationalityOrIncorporationCountry"] = country;
    }
  });
  return updates;
}

// function openApprovedFile(fileObj, showToast) {
//   const url = pickFileUrl(fileObj);
//   if (!url) {
//     showToast?.({
//       severity: "warn",
//       summary: "No file URL",
//       detail: "Approved file exists but has no URL to open.",
//       life: 3500,
//     });
//     return;
//   }
//   window.open(url, "_blank", "noopener,noreferrer");
// }

/* -------------------- Email generation helpers -------------------- */

function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/**
 * Clean plain-text layout (email-friendly):
 *
 * Identification documents
 *
 * 1. Crescent Management Pte Ltd (Country: CH)
 * - Commercial Register Extract
 *
 * 2. Omar Haddad (Nationality: GB)
 * - Passport or national ID
 * - Proof of address ...
 *
 * Source of Wealth corroboration
 *
 * 1. Real estate sale
 * Provide at least ONE of:
 * - Sale agreement
 * - Title deed
 * Also provide (optional):
 * - Bank statement(s)
 *
 * Backward compatible with legacy:
 *  - "Heading:" + bullets
 */
function parsePlainForEmailLayout(text) {
  const raw = String(text || "").replace(/\r\n/g, "\n");
  const lines = raw.split("\n");

  const blocks = [];
  let para = [];

  let currentSection = null;
  let currentCard = null;

  const flushPara = () => {
    const t = para.join("\n").trim();
    if (t) blocks.push({ type: "p", text: t });
    para = [];
  };

  const flushCard = () => {
    if (!currentCard) return;

    const hasContent =
      (currentCard.title && currentCard.title.trim()) ||
      (currentCard.requiredBullets && currentCard.requiredBullets.length) ||
      (currentCard.optionalBullets && currentCard.optionalBullets.length) ||
      (currentCard.texts && currentCard.texts.length);

    if (hasContent) {
      blocks.push({ type: "card", ...currentCard });
    }
    currentCard = null;
  };

  const pushSection = (title) => {
    const t = String(title || "").trim();
    if (!t) return;
    currentSection = t;
    blocks.push({ type: "section", title: currentSection });
  };

  // ✅ accepts "1. Something"
  const isNumberedTitle = (s) => /^\s*\d+\.\s+\S/.test(String(s || ""));

  // ✅ accepts "- item", "-item", "• item", "– item", "* item" (with/without space)
  const matchBullet = (s) => {
    const m = String(s || "").match(/^\s*([-*•‣–—])\s*(.+?)\s*$/);
    if (!m) return null;
    const item = (m[2] || "").trim();
    return item ? item : null;
  };

  const isRequiredLine = (s) =>
    /^required\s*:?\s*$/i.test(String(s || "").trim());

  const isProvideLine = (s) =>
    /^provide at least (one|\d+) of\s*:?\s*$/i.test(String(s || "").trim());

  const isOptionalLine = (s) =>
    /^also provide\s*\(optional\)\s*:?\s*$/i.test(String(s || "").trim());

  // ✅ hard-stop lines that must be OUTSIDE cards
  const isFooterLine = (s) => {
    const t = String(s || "")
      .trim()
      .toLowerCase();
    if (!t) return false;
    return (
      t.startsWith("please reply") ||
      t.startsWith("best regards") ||
      t.startsWith("kind regards") ||
      t === "mykyc team" ||
      t === "mykyc" ||
      t.startsWith("regards,")
    );
  };

  const nextNonEmpty = (idx) => {
    for (let j = idx + 1; j < lines.length; j++) {
      const t = lines[j].trim();
      if (t) return t;
    }
    return "";
  };

  // legacy group (Heading: + bullets => card) only when NOT inside numbered cards
  let legacyGroup = null;
  const flushLegacyGroup = () => {
    if (!legacyGroup) return;
    const ok =
      (legacyGroup.title || "").trim() || (legacyGroup.items || []).length;
    if (ok) {
      blocks.push({
        type: "card",
        section: currentSection,
        title: legacyGroup.title || "",
        texts: [],
        requiredBullets: legacyGroup.items || [],
        requiredGroups: [],
        optionalBullets: [],
        showRequired: false,
        showOptional: false,
      });
    }
    legacyGroup = null;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? "";
    const t = String(line).trim();

    if (!t) {
      flushPara();
      continue;
    }

    const nextLine = nextNonEmpty(i);

    // ===========================
    // Inside a numbered card
    // ===========================
    if (currentCard) {
      // ✅ footer should end the card immediately
      if (isFooterLine(t)) {
        flushCard();
        flushLegacyGroup();
        flushPara();
        para.push(line.trim());
        continue;
      }

      // ✅ provide/optional lines
      if (isRequiredLine(t)) {
        currentCard._mode = "required";
        currentCard.showRequired = true;
        continue;
      }
      if (isProvideLine(t)) {
        // start a new "provide at least N of" group
        const minMatch = t.match(/provide at least (\d+|one) of/i);
        const minRequired = minMatch
          ? (minMatch[1].toLowerCase() === "one" ? 1 : parseInt(minMatch[1], 10))
          : 1;
        currentCard._currentGroup = { minRequired, label: t.replace(/\s*$/, ""), items: [] };
        currentCard.requiredGroups.push(currentCard._currentGroup);
        currentCard._mode = "provide_group";
        continue;
      }
      if (isOptionalLine(t)) {
        currentCard._currentGroup = null;
        currentCard._mode = "optional";
        currentCard.showOptional = true;
        continue;
      }

      // ✅ bullets MUST be handled BEFORE "new section" detection
      const b = matchBullet(line);
      if (b) {
        if (currentCard._mode === "optional")
          currentCard.optionalBullets.push(b);
        else if (currentCard._mode === "provide_group" && currentCard._currentGroup)
          currentCard._currentGroup.items.push(b);
        else currentCard.requiredBullets.push(b);
        continue;
      }

      // next numbered card begins
      if (isNumberedTitle(t)) {
        flushCard();
        flushLegacyGroup();
        flushPara();
        currentCard = {
          section: currentSection,
          title: t,
          texts: [],
          requiredBullets: [],
          requiredGroups: [],
          optionalBullets: [],
          _mode: "required",
          _currentGroup: null,
          showRequired: false,
          showOptional: false,
        };
        continue;
      }

      // new section (only if NOT a bullet)
      if (!isNumberedTitle(t) && isNumberedTitle(nextLine)) {
        flushCard();
        flushLegacyGroup();
        flushPara();
        pushSection(t);
        continue;
      }

      // any other line inside card (rare, but keep)
      currentCard.texts.push(line.trim());
      continue;
    }
    // ===========================
    // Outside cards
    // ===========================

    // section heading: (line not numbered, next is numbered)
    if (!isNumberedTitle(t) && isNumberedTitle(nextLine)) {
      flushLegacyGroup();
      flushPara();
      pushSection(t);
      continue;
    }

    // start numbered card
    if (isNumberedTitle(t)) {
      flushLegacyGroup();
      flushPara();
      flushCard();
      currentCard = {
        section: currentSection,
        title: t,
        texts: [],
        requiredBullets: [],
        requiredGroups: [],
        optionalBullets: [],
        _mode: "required",
        _currentGroup: null,
        showRequired: false,
        showOptional: false,
      };
      continue;
    }

    // legacy heading ":" + bullets => group card
    const endsWithColon = /:$/.test(t);
    const nextIsBullet = !!matchBullet(nextLine);

    const okLegacyHeading =
      endsWithColon &&
      nextIsBullet &&
      !isProvideLine(t) &&
      !isRequiredLine(t) &&
      !isOptionalLine(t) &&
      !isFooterLine(t);

    if (okLegacyHeading) {
      flushPara();
      flushLegacyGroup();
      legacyGroup = { title: t.replace(/:$/, ""), items: [] };
      continue;
    }

    // legacy bullet lines
    const legacyBullet = matchBullet(line);
    if (legacyGroup && legacyBullet) {
      legacyGroup.items.push(legacyBullet);
      continue;
    }

    // default paragraph
    flushLegacyGroup();
    para.push(line);
  }

  flushPara();
  flushLegacyGroup();
  flushCard();

  return blocks;
}

function blocksToRichHtml(blocks) {
  const out = [];

  const cardWrap = (inner) => `
    <div style="margin:12px 0 14px 0;padding:14px 14px;border-radius:14px;background:#f3f5f7;border:1px solid rgba(17,24,39,0.06);">
      ${inner}
    </div>
  `;

  blocks.forEach((b) => {
    if (b.type === "section") {
      out.push(`
        <div style="margin:16px 0 8px 0;font-weight:800;color:#0f172a;font-size:14px;">
          ${escapeHtml(b.title || "")}
        </div>
      `);
      return;
    }

    if (b.type === "p") {
      const t = escapeHtml(b.text || "").replace(/\n/g, "<br/>");
      out.push(
        `<p style="margin:0 0 12px 0;font-weight:400;color:#0f172a;">${t}</p>`
      );
      return;
    }

    if (b.type === "card") {
      const title = escapeHtml(b.title || "");

      const texts = (b.texts || [])
        .map((x) => `<div style="margin:0 0 8px 0;">${escapeHtml(x)}</div>`)
        .join("");

      const req = (b.requiredBullets || [])
        .map((x) => `<li style="margin:0 0 8px 0;">${escapeHtml(x)}</li>`)
        .join("");

      const groups = (b.requiredGroups || [])
        .map((g) => {
          const items = (g.items || [])
            .map((x) => `<li style="margin:0 0 8px 0;">${escapeHtml(x)}</li>`)
            .join("");
          return items
            ? `<div style="margin:10px 0 8px 0;font-weight:700;color:#111827;">${escapeHtml(g.label || "Provide at least 1 of:")}</div><ul style="margin:0;padding-left:18px;color:#111827;">${items}</ul>`
            : "";
        })
        .join("");

      const opt = (b.optionalBullets || [])
        .map((x) => `<li style="margin:0 0 8px 0;">${escapeHtml(x)}</li>`)
        .join("");

      const reqBlock = req
        ? `${b.showRequired ? `<div style="margin:10px 0 8px 0;font-weight:700;color:#111827;">Required:</div>` : ""}<ul style="margin:0;padding-left:18px;color:#111827;">${req}</ul>`
        : "";

      const groupsBlock = groups || "";

      const optBlock = opt
        ? `<div style="margin:12px 0 8px 0;font-weight:700;color:#111827;">Also provide (optional):</div><ul style="margin:0;padding-left:18px;color:#111827;">${opt}</ul>`
        : "";

      out.push(
        cardWrap(`
          ${title ? `<div style="font-weight:800;margin:0 0 8px 0;color:#111827;font-size:14px;">${title}</div>` : ""}
          ${texts ? `<div style="margin:0 0 8px 0;color:#111827;">${texts}</div>` : ""}
          ${reqBlock}
          ${groupsBlock}
          ${optBlock}
        `)
      );
      return;
    }
  });

  return out.join("");
}

function buildPrettyEmailHtml({ title, bodyRichHtml }) {
  return `
  <div style="margin:0;padding:0;background:#eef2f6;">
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background:#eef2f6;padding:0px 0;">
      <tr>
        <td align="center" style="padding:0 16px;">
          <table role="presentation" width="640" cellspacing="0" cellpadding="0" border="0"
            style="max-width:640px;width:100%;background:#ffffff;border-radius:18px;overflow:hidden;border:1px solid rgba(0,0,0,0.06);box-shadow:0 18px 45px rgba(15,23,42,0.08);">
            <tr>
              <td style="background:linear-gradient(135deg,#7f9db2,#9bb2c1);padding:22px 22px;">
                <div style="font-family:Nunito,Arial,sans-serif;font-size:21px;font-weight:800;letter-spacing:0.04em;color:#ffffff;line-height:1.2;">
                  ${escapeHtml(title || "Documents request")}
                </div>
                <div style="margin-top:7px;font-family:Arial,sans-serif;font-size:13px;color:rgba(255,255,255,0.92);line-height:1.4;">
                  Action required
                </div>
              </td>
            </tr>

            <tr>
              <td style="padding:22px 22px 18px 22px;">
                <div style="font-family:Arial,sans-serif;font-size:14px;color:#0f172a;line-height:1.6;">
                  ${bodyRichHtml || ""}
                  <div style="margin-top:18px;padding-top:14px;border-top:1px solid rgba(0,0,0,0.08);font-size:12px;line-height:1.5;color:#64748b;">
                    This message was generated from the MyKYC staff portal.
                  </div>
                </div>
              </td>
            </tr>

          </table>
        </td>
      </tr>
    </table>
  </div>`;
}

async function copyHtmlToClipboard({ html, fallbackText }) {
  try {
    const item = new ClipboardItem({
      "text/html": new Blob([html], { type: "text/html" }),
      "text/plain": new Blob([fallbackText || ""], { type: "text/plain" }),
    });
    await navigator.clipboard.write([item]);
    return { ok: true, rich: true };
  } catch (e) {
    try {
      await navigator.clipboard.writeText(fallbackText || "");
      return { ok: true, rich: false };
    } catch {
      return { ok: false, rich: false };
    }
  }
}

/* PREVENT SCROLLING OUTSIDE OF EMAIL SENDING MODAL */

function useLockBodyScroll(isLocked) {
  React.useEffect(() => {
    if (!isLocked) return;

    const body = document.body;
    const html = document.documentElement;

    const scrollY = window.scrollY || window.pageYOffset;

    const prev = {
      htmlOverflow: html.style.overflow,
      htmlScrollBehavior: html.style.scrollBehavior,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      bodyOverflowY: body.style.overflowY,
      bodyPaddingRight: body.style.paddingRight,
    };

    const scrollbarWidth = window.innerWidth - html.clientWidth;
    if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;

    html.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflowY = "scroll";

    return () => {
      const restoreY = Math.abs(parseInt(body.style.top || "0", 10)) || scrollY;

      html.style.scrollBehavior = "auto";
      html.style.overflow = prev.htmlOverflow;

      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.left = prev.bodyLeft;
      body.style.right = prev.bodyRight;
      body.style.width = prev.bodyWidth;
      body.style.overflowY = prev.bodyOverflowY;
      body.style.paddingRight = prev.bodyPaddingRight;

      window.scrollTo(0, restoreY);

      requestAnimationFrame(() => {
        html.style.scrollBehavior = prev.htmlScrollBehavior;
      });
    };
  }, [isLocked]);
}

/* -------------------- Missing docs builder (new email layout) -------------------- */

function partyMetaLine(p) {
  if (!p) return "";
  if (p.partyType === "trust") {
    return "(Document: first page of trust deed)";
  }
  if (p.partyType === "entity") {
    const c = p.corporateCountry || p.addressCountry || "—";
    return `(Country: ${c})`;
  }
  const nat = p.idCountry || "—";
  return `(Nationality: ${nat})`;
}

function buildEmailCardsFromMissing({
  renderParties,
  form,
  derivedSowItems,
  corporateDocRequirementsByIso2 = {},
}) {
  const idCards = [];
  const sowCards = [];

  let idx = 0; // ✅ single running counter across BOTH sections

  // ------------------------
  // ID cards
  // ------------------------
  (renderParties || []).forEach((p) => {
    const docs = p.docs || {};
    const missingLabels = [];
    const missingGroups = [];

    if (p.partyType === "individual") {
      if (!docs?.id?.obtained) missingLabels.push("Passport or national ID");
      if (!docs?.poa?.obtained)
        missingLabels.push("Proof of address (e.g. utility bill)");
    } else if (p.partyType === "trust") {
      if (!docs?.trustDeed?.obtained)
        missingLabels.push("First page of trust deed");
    } else {
      const requirement = corporateRequirementForIso2(
        p.corporateCountry || p.addressCountry,
        corporateDocRequirementsByIso2
      );
      if (!corporateRequirementSatisfied(docs?.reg || {}, requirement)) {
        const cases = requirement?.cases || [];
        if (cases.length === 1 && cases[0].logic === "ONE_OF") {
          missingGroups.push({
            minRequired: 1,
            labels: corporateRequirementEmailLabels(requirement),
          });
          missingLabels.push(...corporateRequirementEmailLabels(requirement, { optional: true }));
        } else {
          missingLabels.push(...corporateRequirementEmailLines(requirement));
        }
      }
    }

    if (missingLabels.length || missingGroups.length) {
      idx += 1;
      idCards.push({
        title: `${idx}. ${p.name} ${partyMetaLine(p)}`.trim(),
        texts: [],
        requiredBullets: missingLabels,
        requiredGroups: missingGroups,
        optionalBullets: [],
      });
    }
  });

  // ------------------------
  // SoW cards
  // ------------------------
  const sowMap = form?.documentsChecklist?.sow || {};

  (derivedSowItems || []).forEach((it) => {
    const entry = buildSowEntry(it, sowMap?.[it.sowKey]);

    if (it.sowKey === "other") {
      const details = String(entry?.otherDetails || "").trim();
      if (!details) {
        idx += 1;
        sowCards.push({
          title: `${idx}. ${it.label}`,
          texts: ["Please specify details for “Other” source of wealth."],
          requiredBullets: [],
          optionalBullets: [],
        });
      }
      return;
    }

    const missingRequired = [];
    const missingGroups = [];
    const missingOptional = [];
    const missingParts = buildSowMissingParts(entry);
    missingRequired.push(...(missingParts.required || []));
    (missingParts.groups || []).forEach((group) => {
      if (group.singleAsRequired) missingRequired.push(...(group.labels || []));
      else missingGroups.push(group);
    });
    missingOptional.push(...(missingParts.optional || []));

    if (!missingRequired.length && !missingGroups.length && !missingOptional.length) return;

    idx += 1;
    sowCards.push({
      title: `${idx}. ${it.label}`,
      texts: [], // your parser uses Provide/Optional lines as structure
      requiredBullets: missingRequired,
      requiredGroups: missingGroups,
      optionalBullets: missingOptional,
    });
  });

  return { idCards, sowCards };
}

function buildEmailTextFromRequirements(requirements, clientName) {
  const idReqs = (requirements || []).filter((r) => r.section === "Identification documents");
  const sowReqs = (requirements || []).filter((r) => r.section === "Source of Wealth corroboration");

  const lines = [];
  lines.push("Dear Client,");
  lines.push("");
  lines.push("We are finalising your KYC file and still require the following documents.");
  lines.push("");

  if (!idReqs.length && !sowReqs.length) {
    lines.push("At this stage, no missing documents are flagged in the checklist.");
    lines.push("");
  } else {
    if (idReqs.length) {
      lines.push("Identification documents");
      lines.push("");
      idReqs.forEach((r, i) => {
        lines.push(`${i + 1}. ${r.title || ""}`);
        const bullets = String(r.description || "")
          .split("\n")
          .map((l) => l.replace(/^•\s*/, "").trim())
          .filter(Boolean);
        bullets.forEach((b) => lines.push(`- ${b}`));
        lines.push("");
      });
      lines.push("");
    }

    if (sowReqs.length) {
      lines.push("Source of Wealth corroboration");
      lines.push("");
      sowReqs.forEach((r, i) => {
        lines.push(`${idReqs.length + i + 1}. ${r.title || ""}`);
        const descLines = String(r.description || "")
          .split("\n")
          .map((l) => l.trim())
          .filter(Boolean);
        descLines.forEach((l) => {
          const noBullet = l.startsWith("•") ? l.slice(1).trim() : l;
          const lower = noBullet.toLowerCase();
          const isHeading =
            lower === "required:" ||
            lower.startsWith("provide at least") ||
            lower.startsWith("also provide");
          if (isHeading) lines.push(noBullet);
          else lines.push(`- ${noBullet}`);
        });
        lines.push("");
      });
      lines.push("");
    }
  }

  lines.push("Please reply to this email with the attachments.");
  lines.push("");
  lines.push("Best regards,");
  lines.push("MyKYC Team");

  return lines.join("\n").replace(/\n{4,}/g, "\n\n\n");
}

function buildDefaultEmailTextNewLayout({
  renderParties,
  form,
  derivedSowItems,
  corporateDocRequirementsByIso2 = {},
}) {
  const { idCards, sowCards } = buildEmailCardsFromMissing({
    renderParties,
    form,
    derivedSowItems,
    corporateDocRequirementsByIso2,
  });

  const lines = [];

  lines.push("Dear Client,");
  lines.push("");
  lines.push(
    "We are finalising your KYC file and still require the following documents."
  );
  lines.push("");

  if (!idCards.length && !sowCards.length) {
    lines.push(
      "At this stage, no missing documents are flagged in the checklist."
    );
    lines.push("");
  } else {
    if (idCards.length) {
      lines.push("Identification documents");
      lines.push("");

      idCards.forEach((c) => {
        lines.push(c.title);
        (c.requiredBullets || []).forEach((b) => lines.push(`- ${b}`));
        (c.requiredGroups || []).forEach((group) => {
          lines.push(`Provide at least ${group.minRequired || 1} of:`);
          (group.labels || []).forEach((b) => lines.push(`- ${b}`));
        });
        lines.push("");
      });

      lines.push("");
    }

    if (sowCards.length) {
      lines.push("Source of Wealth corroboration");
      lines.push("");

      sowCards.forEach((c) => {
        lines.push(c.title);

        // texts may include both subheads; we want them around lists properly
        const hasReq = (c.requiredBullets || []).length > 0;
        const hasOpt = (c.optionalBullets || []).length > 0;

        if (hasReq) lines.push("Required:");
        (c.requiredBullets || []).forEach((b) => lines.push(`- ${b}`));

        (c.requiredGroups || []).forEach((group) => {
          lines.push(`Provide at least ${group.minRequired || 1} of:`);
          (group.labels || []).forEach((b) => lines.push(`- ${b}`));
        });

        if (hasOpt) lines.push("Also provide (optional):");
        (c.optionalBullets || []).forEach((b) => lines.push(`- ${b}`));

        // any extra texts (like "Please specify...") that are not the standard subheads
        (c.texts || []).forEach((t) => {
          const s = String(t || "").trim();
          const isStandard =
            /^Required:/i.test(s) ||
            /^Provide at least (ONE|\d+) of:/i.test(s) ||
            /^Also provide \(optional\):/i.test(s);
          if (!isStandard && s) lines.push(s);
        });

        lines.push("");
      });

      lines.push("");
    }
  }

  lines.push("Please reply to this email with the attachments.");
  lines.push("");
  lines.push("Best regards,");
  lines.push("MyKYC Team");

  return lines.join("\n").replace(/\n{4,}/g, "\n\n\n");
}

/* --------------------------------------------------- */
/* --------------------------------------------------- */
/* -------------------- Component -------------------- */
/* --------------------------------------------------- */
/* --------------------------------------------------- */

export default function KycSectionDocuments({
  sectionNo = 0,
  form,
  onSetAt,
  readOnly = false,
  title = " | Documents checklist",
  clientId = "",
  apiBaseUrl = "",
  headerStatusText = "",
  headerEmailText = "",
  onHeaderStatusClick,
  isAdmin = false,
  userRole = "",
  userOrgId = "",
  userTeamId = "",
  userUid = "",
  relationshipRiskSettings = DEFAULT_RELATIONSHIP_RISK_SETTINGS,
  onCompletionChange,
  onDocumentIntakeStart,
  intakeLoadingMessage = "",
  onCancelDocumentIntake,
  // onToast,
}) {
  const stored = form?.documentsChecklist || {};
  const storedParties = stored?.parties || {};
  const storedSow = stored?.sow || {};
  const scopeOrgId = String(form?.orgId || userOrgId || "").trim();
  const cleanRelationshipRiskSettings = useMemo(
    () => sanitizeRelationshipRiskSettings(relationshipRiskSettings),
    [relationshipRiskSettings]
  );
  const relationshipRiskModel = cleanRelationshipRiskSettings.activeModel;
  const relationshipRiskLevel = normalizeRelationshipRiskLevel(
    form?.finalOverview?.relationshipRiskResult?.level ||
      form?.finalOverview?.calculatedRiskRating
  );
  const supportingDocRiskReady =
    !!relationshipRiskLevel && relationshipRiskInputsAreReady(form);
  const [supportingDocPolicy, setSupportingDocPolicy] = useState(() =>
    buildDefaultSupportingDocumentPolicy()
  );
  const [supportingDocUboDocumentScope, setSupportingDocUboDocumentScope] =
    useState(DEFAULT_UBO_DOCUMENT_SCOPE);
  const [corporateDocRequirementsByIso2, setCorporateDocRequirementsByIso2] =
    useState({});
  const [corporateDocRequirementsLoading, setCorporateDocRequirementsLoading] =
    useState(false);
  const [loadedPolicyOrg, setLoadedPolicyOrg] = useState(null);
  const [loadedCorporateCountries, setLoadedCorporateCountries] = useState(null);

  const toastRef = useRef(null);
  const showLocalToast = (t) => toastRef.current?.show(t);

  useEffect(() => {
    let cancelled = false;

    async function loadSupportingDocPolicy() {
      setLoadedPolicyOrg(null);
      try {
        const fallback = buildDefaultSupportingDocumentPolicy();
        const generalSnap = await getDoc(
          doc(db, SUPPORTING_DOCUMENT_POLICY_COLLECTION, "lists")
        );
        const generalData = generalSnap.exists() ? generalSnap.data() : {};
        const generalPolicy = sanitizeSupportingDocumentPolicy(
          generalData.sow || generalData.policy || fallback
        );
        const generalUboDocumentScope = normalizeUboDocumentScope(
          generalData.uboDocumentScope
        );

        if (!scopeOrgId) {
          if (!cancelled) {
            setSupportingDocPolicy(generalPolicy);
            setSupportingDocUboDocumentScope(generalUboDocumentScope);
            setLoadedPolicyOrg(scopeOrgId);
          }
          return;
        }

        const orgSnap = await getDoc(
          doc(db, SUPPORTING_DOCUMENT_POLICY_COLLECTION, scopeOrgId)
        );
        const orgData = orgSnap.exists() ? orgSnap.data() : {};
        const nextPolicy =
          orgData.useGeneralList === true
            ? generalPolicy
            : sanitizeSupportingDocumentPolicy(
                orgData.sow || orgData.policy || generalPolicy
              );
        const nextUboDocumentScope = orgData.uboDocumentScope
          ? normalizeUboDocumentScope(orgData.uboDocumentScope)
          : generalUboDocumentScope;

        if (!cancelled) {
          setSupportingDocPolicy(nextPolicy);
          setSupportingDocUboDocumentScope(nextUboDocumentScope);
          setLoadedPolicyOrg(scopeOrgId);
        }
      } catch (e) {
        if (!cancelled) {
          setSupportingDocPolicy(buildDefaultSupportingDocumentPolicy());
          setSupportingDocUboDocumentScope(DEFAULT_UBO_DOCUMENT_SCOPE);
        }
      }
    }

    loadSupportingDocPolicy();
    return () => {
      cancelled = true;
    };
  }, [scopeOrgId]);

  // useEffect(() => {
  //   if (readOnly) return;
  //   const ownership = form?.ownership || {};
  //   const sh = withDocRefsIfMissing(ownership.shareholders, "_docRef");
  //   const ub = withDocRefsIfMissing(ownership.ubos, "_docRef");
  //   const ct = withDocRefsIfMissing(ownership.signatories, "_docRef");
  //   if (sh.changed) onSetAt?.(["ownership", "shareholders"], sh.next);
  //   if (ub.changed) onSetAt?.(["ownership", "ubos"], ub.next);
  //   if (ct.changed) onSetAt?.(["ownership", "controllers"], ct.next);
  // }, [readOnly]);

  useEffect(() => {
    if (readOnly) return;
    const ownership = form?.ownership || {};
    const sh = withDocRefsIfMissing(ownership.shareholders, "_docRef");
    const ub = withDocRefsIfMissing(ownership.ubos, "_docRef");
    const sg = withDocRefsIfMissing(ownership.signatories, "_docRef");
    const ct = withDocRefsIfMissing(ownership.controllers, "_docRef");

    if (sh.changed) onSetAt?.(["ownership", "shareholders"], sh.next);
    if (ub.changed) onSetAt?.(["ownership", "ubos"], ub.next);
    if (sg.changed) onSetAt?.(["ownership", "signatories"], sg.next);
    if (ct.changed) onSetAt?.(["ownership", "controllers"], ct.next);
    // eslint-disable-next-line
  }, [readOnly]);

  // useEffect(() => {
  //   if (readOnly) return;
  //   const ownership = form?.ownership || {};
  //   const sh = withDocRefsIfMissing(ownership.shareholders, "_docRef");
  //   const ub = withDocRefsIfMissing(ownership.ubos, "_docRef");
  //   const sg = withDocRefsIfMissing(
  //     ownership.signatories || ownership.controllers,
  //     "_docRef"
  //   );

  //   if (sh.changed) onSetAt?.(["ownership", "shareholders"], sh.next);
  //   if (ub.changed) onSetAt?.(["ownership", "ubos"], ub.next);
  //   if (sg.changed) onSetAt?.(["ownership", "signatories"], sg.next);
  // }, [readOnly]);

  const derivedParties = useMemo(
    () => buildDerivedParties(form, supportingDocUboDocumentScope),
    [form, supportingDocUboDocumentScope]
  );
  const corporateRequirementCountries = useMemo(() => {
    return Array.from(
      new Set(
        (derivedParties || [])
          .filter((p) => p.partyType === "entity")
          .map((p) => iso2(p.corporateCountry || p.addressCountry))
          .filter(Boolean)
      )
    ).sort();
  }, [derivedParties]);
  const corporateRequirementCountriesSignature = useMemo(
    () => corporateRequirementCountries.join("|"),
    [corporateRequirementCountries]
  );

  useEffect(() => {
    let cancelled = false;

    async function loadCorporateDocumentRequirements() {
      setLoadedCorporateCountries(null);
      if (!corporateRequirementCountries.length) {
        if (!cancelled) {
          setCorporateDocRequirementsByIso2({});
          setCorporateDocRequirementsLoading(false);
          setLoadedCorporateCountries(corporateRequirementCountriesSignature);
        }
        return;
      }

      setCorporateDocRequirementsLoading(true);

      try {
        const snaps = await Promise.all(
          corporateRequirementCountries.map((countryIso2) =>
            getDoc(doc(db, CORPORATE_DOCUMENT_REQUIREMENT_COLLECTION, countryIso2))
              .then((snap) => ({ countryIso2, snap }))
          )
        );
        if (cancelled) return;
        const next = {};
        snaps.forEach(({ countryIso2, snap }) => {
          if (snap.exists()) next[countryIso2] = snap.data() || {};
        });
        setCorporateDocRequirementsByIso2(next);
        setCorporateDocRequirementsLoading(false);
        setLoadedCorporateCountries(corporateRequirementCountriesSignature);
      } catch (e) {
        if (!cancelled) {
          setCorporateDocRequirementsByIso2({});
          setCorporateDocRequirementsLoading(false);
        }
      }
    }

    loadCorporateDocumentRequirements();
    return () => {
      cancelled = true;
    };
  }, [corporateRequirementCountriesSignature]);

  const derivedPartiesSignature = useMemo(
    () =>
      JSON.stringify(
        (derivedParties || []).map((p) => ({
          partyId: p.partyId,
          name: p.name,
          role: p.role,
          partyType: p.partyType,
          idCountry: p.idCountry || "",
          addressCountry: p.addressCountry || "",
          corporateCountry: p.corporateCountry || "",
          sourcePartyIds: p.sourcePartyIds || [],
        }))
      ) + `|scope:${supportingDocUboDocumentScope}`,
    [derivedParties, supportingDocUboDocumentScope]
  );

  useEffect(() => {
    if (readOnly) return;

    const current = storedParties || {};
    let changed = false;

    const derivedIds = new Set((derivedParties || []).map((p) => p.partyId));
    const next = { ...current };

    // ✅ prune parties that no longer exist (removed / filtered out)
    Object.keys(next).forEach((partyId) => {
      if (!derivedIds.has(partyId)) {
        delete next[partyId];
        changed = true;
      }
    });

    // ✅ merge/update current derived parties
    (derivedParties || []).forEach((p) => {
      const merged = mergeParty(current?.[p.partyId], p);
      const prev = current?.[p.partyId];
      if (JSON.stringify(prev || {}) !== JSON.stringify(merged || {})) {
        next[p.partyId] = merged;
        changed = true;
      }
    });

    if (!stored?.parties) changed = true;
    if (stored?.uboDocumentScope !== supportingDocUboDocumentScope) changed = true;

    if (changed) {
      onSetAt?.(["documentsChecklist"], {
        ...(stored || {}),
        uboDocumentScope: supportingDocUboDocumentScope,
        parties: next,
        sow: storedSow || {},
      });
    }
    // eslint-disable-next-line
  }, [readOnly, derivedPartiesSignature]);

  const [approvedMap, setApprovedMap] = useState({});
  const [staffSavedAttachmentMap, setStaffSavedAttachmentMap] = useState({});

  const renderParties = useMemo(() => {
    return derivedParties.map((p) => {
      const party = mergeParty(storedParties?.[p.partyId], p);
      if (party.partyType !== "entity") return party;
      const requirement = corporateRequirementForIso2(
        party.corporateCountry || party.addressCountry, corporateDocRequirementsByIso2
      );
      const relatedApprovedFiles = partyRequirementKeyCandidates(party, "reg").flatMap((key) => {
        return [approvedMap[key], staffSavedAttachmentMap[key]].filter(Boolean).flatMap(
          (file) => file.relatedApprovedFiles || [file]
        );
      });
      return { ...party, docs: { ...party.docs, reg: corporateRegWithApprovedDocuments(
        party.docs?.reg, requirement, { relatedApprovedFiles }
      ) } };
    });
  }, [derivedParties, storedParties, corporateDocRequirementsByIso2, approvedMap, staffSavedAttachmentMap]);

  function resolveChecklistPartyId(partyId) {
    const cleanPartyId = cleanStr(partyId);
    if (!cleanPartyId) return "";
    if (form?.documentsChecklist?.parties?.[cleanPartyId]) return cleanPartyId;
    const match = (renderParties || []).find(
      (party) =>
        cleanStr(party?.partyId) === cleanPartyId ||
        (Array.isArray(party?.sourcePartyIds) &&
          party.sourcePartyIds.some((sourceId) => cleanStr(sourceId) === cleanPartyId))
    );
    return cleanStr(match?.partyId) || cleanPartyId;
  }

  function setPartyObtained(partyId, docKey, nextVal) {
    const current = form?.documentsChecklist?.parties || {};
    const resolvedPartyId = resolveChecklistPartyId(partyId);
    const party = current?.[resolvedPartyId];
    if (!party) return;

    const next = {
      ...current,
      [resolvedPartyId]: {
        ...party,
        docs: {
          ...(party.docs || {}),
          [docKey]: {
            ...(party.docs?.[docKey] || {}),
            required: true,
            obtained: !!nextVal,
          },
        },
      },
    };

    onSetAt?.(["documentsChecklist", "parties"], next);
  }

  function setCorporateDocItemObtained(partyId, item, requirement, nextVal) {
    const current = form?.documentsChecklist?.parties || {};
    const party = current?.[partyId];
    if (!party || !item?.key) return;

    const currentReg = party.docs?.reg || {};
    const nextItems = {
      ...(currentReg.items || {}),
      [item.key]: {
        ...(currentReg.items?.[item.key] || {}),
        key: item.key,
        label: item.label,
        secondaryLabel: item.secondaryLabel || "",
        obtained: !!nextVal,
      },
    };
    const nextReg = {
      ...currentReg,
      required: true,
      doc_logic: requirement?.doc_logic || "ALL",
      doc_condition: requirement?.doc_condition || null,
      fallback: !!requirement?.fallback,
      cases: corporateRequirementSnapshotCases(requirement),
      items: nextItems,
    };
    nextReg.obtained = corporateRequirementSatisfied(nextReg, requirement);

    const next = {
      ...current,
      [partyId]: {
        ...party,
        docs: {
          ...(party.docs || {}),
          reg: nextReg,
        },
      },
    };

    onSetAt?.(["documentsChecklist", "parties"], next);
  }

  const idProgress = useMemo(() => {
    let required = 0;
    let obtained = 0;

    renderParties.forEach((p) => {
      if (p.partyType === "entity") {
        const requirement = corporateRequirementForIso2(
          p.corporateCountry || p.addressCountry,
          corporateDocRequirementsByIso2
        );
        const progress = corporateRequirementProgress(p.docs?.reg || {}, requirement);
        required += progress.required;
        obtained += progress.obtained;
        return;
      }

      Object.entries(p.docs || {}).forEach(([, v]) => {
        if (v?.required) {
          required += 1;
          if (v?.obtained) obtained += 1;
        }
      });
    });

    const pct = required ? Math.round((obtained / required) * 100) : 0;
    return { required, obtained, pct };
  }, [renderParties, corporateDocRequirementsByIso2]);

  const selectedSowTypes = useMemo(() => {
    const sow = form?.sourceOfFundsWealth || {};
    const sourceOfWealth = Array.isArray(sow.sourceOfWealth) ? sow.sourceOfWealth : [];
    const keys = Array.from(new Set(sourceOfWealth.map(normalizeSowKey).filter(Boolean)));
    // Loan in SoF triggers the same corroboration requirements as a SoW type
    if (Array.isArray(sow.sourceOfFundsTypes) && sow.sourceOfFundsTypes.includes("loan")) {
      if (!keys.includes("loan")) keys.push("loan");
    }
    return keys;
  }, [form]);

  const derivedSowItems = useMemo(() => {
    return selectedSowTypes.map((value) => {
      const label = POLICY_SOW_LABEL_BY_VALUE[value] || SOW_LABEL_BY_VALUE[value] || value;
      const policyConfig = supportingDocRiskReady
        ? supportingDocRiskPolicy(
            supportingDocPolicy,
            value,
            relationshipRiskLevel,
            relationshipRiskModel
          )
        : null;
      return {
        sowKey: value,
        label,
        docs: [],
        policyConfig,
        riskLevel: relationshipRiskLevel,
        isOther: value === "other",
      };
    });
  }, [
    selectedSowTypes.join("|"),
    supportingDocRiskReady,
    relationshipRiskLevel,
    relationshipRiskModel,
    supportingDocPolicy,
  ]);

  const visibleSowItems = useMemo(() => {
    if (!supportingDocRiskReady) return [];

    const sow = form?.documentsChecklist?.sow || {};
    return (derivedSowItems || []).filter((it) => {
      const entry = buildSowEntry(it, sow?.[it.sowKey] || storedSow?.[it.sowKey]);
      return sowEntryHasUserFacingRequirements(entry);
    });
  }, [
    form?.documentsChecklist?.sow,
    storedSow,
    derivedSowItems,
    supportingDocRiskReady,
  ]);

  useEffect(() => {
    if (readOnly) return;
    if (!supportingDocRiskReady) return;

    const current = storedSow || {};
    let changed = false;

    const derivedKeys = new Set((derivedSowItems || []).map((it) => it.sowKey));
    const next = { ...current };

    // ✅ prune sow entries that are no longer selected
    Object.keys(next).forEach((sowKey) => {
      if (!derivedKeys.has(sowKey)) {
        delete next[sowKey];
        changed = true;
      }
    });

    (derivedSowItems || []).forEach((it) => {
      const existing = current?.[it.sowKey];
      const mergedEntry = buildSowEntry(it, existing);

      if (JSON.stringify(existing || {}) !== JSON.stringify(mergedEntry || {})) {
        next[it.sowKey] = mergedEntry;
        changed = true;
      }
    });

    if (!stored?.sow) changed = true;

    if (changed) {
      onSetAt?.(["documentsChecklist"], {
        ...(stored || {}),
        parties: storedParties || {},
        sow: next,
      });
    }
    // eslint-disable-next-line
  }, [
    readOnly,
    supportingDocRiskReady,
    relationshipRiskLevel,
    supportingDocPolicy,
    (derivedSowItems || []).map((x) => x.sowKey).join("|"),
  ]);

  function setSowDocObtained(sowKey, docKey, nextVal) {
    const sow = form?.documentsChecklist?.sow || {};
    const item = (derivedSowItems || []).find((it) => it.sowKey === sowKey);
    if (!item) return;

    const entry = buildSowEntry(item, sow?.[sowKey] || storedSow?.[sowKey]);

    const next = {
      ...sow,
      [sowKey]: {
        ...entry,
        docs: {
          ...(entry.docs || {}),
          [docKey]: { ...(entry.docs?.[docKey] || {}), obtained: !!nextVal },
        },
      },
    };

    onSetAt?.(["documentsChecklist", "sow"], next);
  }

  function setSowOtherDetails(sowKey, val) {
    const sow = form?.documentsChecklist?.sow || {};
    const item = (derivedSowItems || []).find((it) => it.sowKey === sowKey);
    if (!item) return;

    const entry = buildSowEntry(item, sow?.[sowKey] || storedSow?.[sowKey]);
    const next = { ...sow, [sowKey]: { ...entry, otherDetails: val } };
    onSetAt?.(["documentsChecklist", "sow"], next);
  }

  const sowProgress = useMemo(() => {
    if (!supportingDocRiskReady) {
      return { required: 0, obtained: 0, pct: 0 };
    }

    const sow = form?.documentsChecklist?.sow || {};
    const total = visibleSowItems.length;
    let completed = 0;

    visibleSowItems.forEach((it) => {
      const entry = buildSowEntry(it, sow?.[it.sowKey] || storedSow?.[it.sowKey]);
      if (isSowEntrySatisfied(entry)) completed += 1;
    });

    const pct = total ? Math.round((completed / total) * 100) : 0;
    return { required: total, obtained: completed, pct };
  }, [form?.documentsChecklist?.sow, storedSow, visibleSowItems, supportingDocRiskReady]);

  const globalProgress = useMemo(() => {
    const required = (idProgress.required || 0) + (sowProgress.required || 0);
    const obtained = (idProgress.obtained || 0) + (sowProgress.obtained || 0);
    const pct = required ? Math.round((obtained / required) * 100) : 0;
    return { required, obtained, pct };
  }, [idProgress, sowProgress]);

  // Share the checklist's own rules, including required document alternatives.
  // Include the form reference so a result for older inputs cannot unlock completion.
  const documentsComplete = supportingDocRiskReady &&
    loadedPolicyOrg === scopeOrgId &&
    loadedCorporateCountries === corporateRequirementCountriesSignature &&
    !corporateDocRequirementsLoading &&
    globalProgress.obtained === globalProgress.required;
  useEffect(() => {
    onCompletionChange?.({ form, complete: documentsComplete });
  }, [onCompletionChange, form, documentsComplete]);

  const badgeForType = (partyType) =>
    partyType === "entity"
      ? "kycDocBadge kycDocBadgeEntity"
      : partyType === "trust"
      ? "kycDocBadge kycDocBadgeEntity"
      : "kycDocBadge kycDocBadgeIndividual";

  const iconForType = (partyType) =>
    partyType === "entity"
      ? "bi bi-building"
      : partyType === "trust"
      ? "bi bi-shield-check"
      : "bi bi-person";

  const sowTypeIcon = (label) => {
    const s = String(label || "").toLowerCase();
    if (s.includes("employment")) return "bi bi-briefcase";
    if (s.includes("real estate")) return "bi bi-house";
    if (s.includes("inherit")) return "bi bi-journal-text";
    if (s.includes("settlement") || s.includes("divorce"))
      return "bi bi-gavel";
    if (s.includes("virtual asset")) return "bi bi-currency-bitcoin";
    if (s.includes("business")) return "bi bi-building-check";
    if (s.includes("invest")) return "bi bi-graph-up";
    if (s.includes("rental")) return "bi bi-house-check";
    return "bi bi-file-earmark-text";
  };

  /* -------------------- Preview Document modal state -------------------- */

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deletingDocumentKey, setDeletingDocumentKey] = useState("");
  const [staffAttach, setStaffAttach] = useState({
    open: false,
    context: null,
    file: null,
    serverFile: null,
    scanRequired: false,
    scanResult: null,
    stage: "",
    error: "",
    busy: false,
    saving: false,
  });
  const staffAttachScanSkipRef = useRef(new Set());
  const [staffAttachDetail, setStaffAttachDetail] = useState(null);
  const [dragAttachKey, setDragAttachKey] = useState("");
  const [checklistDropBounds, setChecklistDropBounds] = useState(null);
  useEffect(() => {
    const reset = () => { setChecklistDropBounds(null); setDragAttachKey(""); };
    const onKeyDown = e => { if (e.key === "Escape") reset(); };
    document.addEventListener("dragend", reset, true);
    document.addEventListener("drop", reset, true);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("blur", reset);
    window.addEventListener("scroll", reset, true);
    window.addEventListener("resize", reset);
    return () => {
      document.removeEventListener("dragend", reset, true);
      document.removeEventListener("drop", reset, true);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("blur", reset);
      window.removeEventListener("scroll", reset, true);
      window.removeEventListener("resize", reset);
    };
  }, []);
  const [staffAttachKycUpdateConfirm, setStaffAttachKycUpdateConfirm] = useState({
    open: false,
    options: [],
    selected: {},
    isCorporate: false,
    isProofOfAddress: false,
  });
  const staffAttachKycUpdateContinueBtnRef = useRef(null);
  const staffAttachKycUpdateYesBtnRef = useRef(null);
  const staffAttachKycUpdateNoBtnRef = useRef(null);
  const previewRequestSeqRef = useRef(0);

  function revokePreviewBlobUrls(file) {
    const files = [
      file,
      ...(Array.isArray(file?.relatedApprovedFiles) ? file.relatedApprovedFiles : []),
    ];
    files.forEach((item) => {
      const url = cleanStr(item?.downloadURL || item?.downloadUrl || item?.url);
      if (url.startsWith("blob:")) {
        try {
          window.URL.revokeObjectURL(url);
        } catch {
          // best effort cleanup only
        }
      }
    });
  }

  function closePreviewModal() {
    previewRequestSeqRef.current += 1;
    revokePreviewBlobUrls(previewFile);
    setPreviewOpen(false);
    setPreviewFile(null);
    setPreviewLoading(false);
  }

  function currentPreviewUrl() {
    return cleanStr(previewFile?.downloadURL || previewFile?.downloadUrl || previewFile?.url);
  }

  function previewFilenamePart(value, fallback = "") {
    const cleaned = cleanStr(value || fallback)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .replace(/-{2,}/g, "-");
    return cleaned || cleanStr(fallback).toLowerCase() || "document";
  }

  function previewRequirementFilenamePart(value, fallback = "kyc-document") {
    return cleanStr(value) === "ID" ? "ID" : previewFilenamePart(value, fallback);
  }

  function previewExtensionFromFile(file) {
    const name = cleanStr(file?.originalName || file?.name);
    const match = name.match(/\.([a-zA-Z0-9]{1,8})$/);
    if (match) return match[1].toLowerCase();

    const contentType = cleanStr(file?.contentType || file?.mimeType).toLowerCase();
    if (contentType.includes("pdf")) return "pdf";
    if (contentType.includes("png")) return "png";
    if (contentType.includes("webp")) return "webp";
    if (contentType.includes("jpeg") || contentType.includes("jpg")) return "jpg";
    return "file";
  }

  function normalizedPreviewRequirementText(value) {
    return cleanStr(value).toLowerCase().replace(/\s+/g, " ");
  }

  function previewRequirementLooksLikeId(file, parsed) {
    const key = cleanStr(file?.review?.requirementKey || file?.requirementKey).toLowerCase();
    const text = normalizedPreviewRequirementText(
      [
        file?.review?.requirementText,
        file?.requirementText,
        file?.cardTitle,
        file?.cardId,
      ]
        .filter(Boolean)
        .join(" ")
    );
    return (
      parsed?.kind === "party" && parsed?.docKey === "id"
    ) || /(^|[:_-])passport($|[:_-])/.test(key) ||
      /(^|[:_-])id($|[:_-])/.test(key) ||
      /(^|[:_-])national[_-]?id($|[:_-])/.test(key) ||
      text.includes("passport") ||
      text.includes("national id");
  }

  function previewRequirementLooksLikeProofOfAddress(file, parsed) {
    const key = cleanStr(file?.review?.requirementKey || file?.requirementKey).toLowerCase();
    const text = normalizedPreviewRequirementText(
      [
        file?.review?.requirementText,
        file?.requirementText,
        file?.cardTitle,
        file?.cardId,
      ]
        .filter(Boolean)
        .join(" ")
    );
    return (
      parsed?.kind === "party" && parsed?.docKey === "poa"
    ) || key.includes("proof_of_address") ||
      key.includes("proof-of-address") ||
      key.includes("poa") ||
      key.includes("utility") ||
      text.includes("proof of address") ||
      text.includes("utility bill") ||
      text.includes("utility document");
  }

  function previewRequirementLooksLikeCorporate(file, parsed) {
    const key = cleanStr(file?.review?.requirementKey || file?.requirementKey).toLowerCase();
    const text = normalizedPreviewRequirementText(
      [
        file?.review?.requirementText,
        file?.requirementText,
        file?.cardTitle,
        file?.cardId,
      ]
        .filter(Boolean)
        .join(" ")
    );
    return (
      parsed?.kind === "party" && parsed?.docKey === "reg"
    ) || key.includes("corporate") ||
      key.includes("trade_licence") ||
      key.includes("trade-license") ||
      key.includes("trade_license") ||
      key.includes("certificate_of_incorporation") ||
      key.includes("memorandum") ||
      key.includes("moa") ||
      text.includes("trade licence") ||
      text.includes("trade license") ||
      text.includes("certificate of incorporation") ||
      text.includes("memorandum of association") ||
      text.includes("company register") ||
      text.includes("corporate registration");
  }

  function previewRequirementLabel(file) {
    const parsed = parseRequirementKey(file?.review?.requirementKey || file?.requirementKey);
    if (parsed?.kind === "sow") return "source-of-wealth";
    if (previewRequirementLooksLikeId(file, parsed)) return "ID";
    if (previewRequirementLooksLikeProofOfAddress(file, parsed)) return "proof-of-address";
    if (previewRequirementLooksLikeCorporate(file, parsed)) return "corporate-document";

    return (
      cleanStr(file?.review?.requirementText) ||
      cleanStr(file?.requirementText) ||
      cleanStr(file?.cardTitle) ||
      cleanStr(file?.cardId) ||
      cleanStr(file?.review?.requirementKey) ||
      "kyc-document"
    );
  }

  function previewCustomerSubjectLabel() {
    return (
      cleanStr(form?.customer?.name) ||
      cleanStr(form?.customer?.fullName) ||
      cleanStr(form?.customerName) ||
      cleanStr(form?.clientName) ||
      cleanStr(clientId) ||
      "client"
    );
  }

  function previewSubjectLabel(file) {
    const parsed = parseRequirementKey(file?.review?.requirementKey || file?.requirementKey);
    if (parsed?.kind === "sow") return previewCustomerSubjectLabel();

    return (
      cleanStr(file?.reviewMeta?.partyName) ||
      cleanStr(file?.partyName) ||
      cleanStr(file?.review?.partyName) ||
      cleanStr(file?.cardSnapshot?.meta?.partyName) ||
      previewCustomerSubjectLabel()
    );
  }

  function previewDuplicateDownloadSuffix({ file, requirement, ext }) {
    const files = Array.isArray(previewFile?.relatedApprovedFiles)
      ? previewFile.relatedApprovedFiles
      : [];
    const peers = files.filter((candidate) => {
      const candidateRequirement = previewRequirementFilenamePart(
        previewRequirementLabel(candidate),
        "kyc-document"
      );
      return candidateRequirement === requirement && previewExtensionFromFile(candidate) === ext;
    });

    if (peers.length <= 1) return "";

    const currentPath = cleanStr(file?.objectPath);
    const index = peers.findIndex((candidate) => cleanStr(candidate?.objectPath) === currentPath);
    const n = index >= 0 ? index : 0;
    let value = "";
    let cursor = n;
    do {
      value = String.fromCharCode(65 + (cursor % 26)) + value;
      cursor = Math.floor(cursor / 26) - 1;
    } while (cursor >= 0);
    return `_${value}`;
  }

  function currentPreviewFilename() {
    const file = previewFile || {};
    const client = previewFilenamePart(previewSubjectLabel(file), "client");
    const requirement = previewRequirementFilenamePart(
      previewRequirementLabel(file),
      "kyc-document"
    );
    const date = new Date().toISOString().slice(0, 10);
    const ext = previewExtensionFromFile(file);
    const suffix = previewDuplicateDownloadSuffix({ file, requirement, ext });
    const base = [client, requirement, date]
      .filter(Boolean)
      .join("_")
      .slice(0, 160)
      .replace(/_+$/g, "");
    return `${base}${suffix}.${ext}`;
  }

  function downloadCurrentPreviewFile() {
    const url = currentPreviewUrl();
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = currentPreviewFilename();
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function waitForPreviewLoaderPaint(minDelayMs = 180) {
    return new Promise((resolve) => {
      window.requestAnimationFrame(() => {
        window.setTimeout(resolve, minDelayMs);
      });
    });
  }

  /* -------------------- Email modal state -------------------- */

  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailText, setEmailText] = useState("");
  const [emailHtml, setEmailHtml] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  // const [emailError, setEmailError] = useState("");
  const [emailNotice, setEmailNotice] = useState("");
  const [docUploadEmailPromptOpen, setDocUploadEmailPromptOpen] = useState(false);
  const [docUploadEmailPromptValue, setDocUploadEmailPromptValue] = useState("");
  const [docUploadEmailPromptError, setDocUploadEmailPromptError] = useState("");
  const docUploadEmailPromptResolveRef = useRef(null);
  const docUploadEmailPromptInputRef = useRef(null);

  useLockBodyScroll(emailModalOpen || docUploadEmailPromptOpen || staffAttach.open || previewOpen);

  useEffect(() => {
    if (!docUploadEmailPromptOpen) return undefined;
    const timer = window.setTimeout(() => {
      docUploadEmailPromptInputRef.current?.focus();
    }, 80);
    return () => window.clearTimeout(timer);
  }, [docUploadEmailPromptOpen]);

  function openDocUploadEmailPrompt(initialValue = "") {
    setDocUploadEmailPromptValue(initialValue);
    setDocUploadEmailPromptError("");
    setDocUploadEmailPromptOpen(true);

    return new Promise((resolve) => {
      docUploadEmailPromptResolveRef.current = resolve;
    });
  }

  function closeDocUploadEmailPrompt(value = null) {
    setDocUploadEmailPromptOpen(false);
    const resolve = docUploadEmailPromptResolveRef.current;
    docUploadEmailPromptResolveRef.current = null;
    resolve?.(value);
  }

  function submitDocUploadEmailPrompt() {
    const nextEmail = String(docUploadEmailPromptValue || "").trim();
    if (!nextEmail) {
      setDocUploadEmailPromptError("Enter the client email address to send the document upload link.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextEmail)) {
      setDocUploadEmailPromptError("Enter a valid email address.");
      return;
    }
    closeDocUploadEmailPrompt(nextEmail);
  }

  const clientName = String(form?.customer?.name || "").trim() || "Client";

  async function openEmailModal() {
    if (intakePendingFiles.length) {
      showLocalToast({ severity: "info", summary: "Documents already received", detail: "Review the uploaded documents before requesting missing documents.", life: 4000 });
      return;
    }
    const defaultSubject = `${clientName} · Documents required for KYC `;
    setEmailSubject(defaultSubject);
    setEmailNotice("");
    setEmailModalOpen(true);

    setEmailText(
      buildDefaultEmailTextNewLayout({
        renderParties,
        form,
        derivedSowItems,
        corporateDocRequirementsByIso2,
      })
    );
  }

  function closeEmailModal() {
    if (emailBusy) return;
    setEmailModalOpen(false);
    setEmailNotice("");
  }

  // ✅ derived HTML
  useEffect(() => {
    if (!emailModalOpen) return;

    const blocks = parsePlainForEmailLayout(emailText);
    const rich = blocksToRichHtml(blocks);

    const html = buildPrettyEmailHtml({
      title: emailSubject,
      bodyRichHtml: rich,
    });

    setEmailHtml(html);
  }, [emailText, emailSubject, emailModalOpen]);

  async function onCopyPlain() {
    try {
      await navigator.clipboard.writeText(emailText || "");
      setEmailNotice("Copied plain text to clipboard.");
    } catch {
      setEmailNotice("Could not copy to clipboard (browser blocked).");
    }
  }

  async function onCopyRich() {
    const r = await copyHtmlToClipboard({
      html: emailHtml,
      fallbackText: emailText,
    });
    if (r.ok && r.rich)
      setEmailNotice("Copied formatted email (HTML) to clipboard.");
    else if (r.ok && !r.rich)
      setEmailNotice("Copied plain text (HTML clipboard not supported here).");
    else setEmailNotice("Could not copy to clipboard (browser blocked).");
  }

  async function onSendEmailViaServer() {
    if (intakePendingFiles.length) {
      showLocalToast({ severity: "info", summary: "Documents already received", detail: "Review the uploaded documents before requesting missing documents.", life: 4000 });
      return;
    }
    // alert("salut");
    try {
      setEmailBusy(true);
      setEmailNotice("");

      if (!clientId) {
        setEmailNotice("Missing clientId prop (required to send via backend).");
        return;
      }

      const user = auth?.currentUser;
      if (!user) {
        setEmailNotice("You are not logged in.");
        return;
      }

      const token = await user.getIdToken();
      const base = String(apiBaseUrl || MAIL_API_BASE).replace(/\/+$/, "");
      const url = `${base}/myKyc/staff/documents-request/send`;

      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          clientId,
          subject: emailSubject,
          bodyText: emailText, // server generates HTML; client doesn't edit HTML
        }),
      });

      const data = await resp.json().catch(() => ({}));

      if (!resp.ok || data?.ok !== true) {
        setEmailNotice(data?.error || "Failed to send email.");
        return;
      }

      // ✅ Email sent successfully

      setEmailModalOpen(false);
      setEmailNotice("");

      const recipientEmail = String(
        form?.customer?.email || form?.customer?.contactEmail || ""
      ).trim();

      showLocalToast({
        severity: "success",
        summary: "Email sent",
        // detail: "Documents request sent successfully.",
        detail: recipientEmail
          ? `Documents request sent to ${recipientEmail}.`
          : "Documents request sent successfully.",
        life: 3500,
      });

      // showLocalToast({
      //   severity: "success",
      //   summary: "Email sent",
      //   detail: "Documents request sent successfully.",
      //   life: 3000,
      // });

      // setEmailModalOpen(false);
      // setEmailNotice("");

      // onToast?.({
      //   severity: "success",
      //   summary: "Email sent",
      //   detail: "Documents request sent successfully.",
      //   life: 3000,
      // });
      // setEmailNotice("Email sent successfully.");
    } catch (e) {
      setEmailNotice(e?.message || "Failed to send email.");
    } finally {
      setEmailBusy(false);
    }
  }

  // async function onSendEmailViaServer_NEW() {
  //   try {
  //     setEmailBusy(true);
  //     setEmailNotice("");

  //     if (!clientId) {
  //       setEmailNotice("Missing clientId prop (required to send via backend).");
  //       return;
  //     }

  //     const user = auth?.currentUser;
  //     if (!user) {
  //       setEmailNotice("You are not logged in.");
  //       return;
  //     }

  //     const token = await user.getIdToken();
  //     const base = String(apiBaseUrl || "").replace(/\/+$/, "");
  //     const url = `${base}/myKyc/staff/documents-request/send`;

  //     const resp = await fetch(url, {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //         Authorization: `Bearer ${token}`,
  //       },
  //       body: JSON.stringify({
  //         clientId,
  //         subject: emailSubject,
  //         bodyText: emailText,
  //       }),
  //     });

  //     const data = await resp.json().catch(() => ({}));

  //     if (!resp.ok || data?.ok !== true) {
  //       setEmailNotice(data?.error || "Failed to send email.");
  //       return;
  //     }

  //     // ✅ success
  //     setEmailModalOpen(false);
  //     setEmailNotice("");

  //     showLocalToast({
  //       severity: "success",
  //       summary: "Email sent",
  //       detail: "Documents request sent successfully.",
  //       life: 3000,
  //     });
  //   } catch (e) {
  //     setEmailNotice(e?.message || "Failed to send email.");
  //   } finally {
  //     setEmailBusy(false);
  //   }
  // }

  /* --------------------  TO SEND LINK FOR CLIENTS TO UPLOAD DOCUMENTS ---------------------- */

  const [docUploadBusy, setDocUploadBusy] = useState(false);

  const [additionalFiles, setAdditionalFiles] = useState([]);
  const [intakePendingFiles, setIntakePendingFiles] = useState([]);
  const [pendingReviewFiles, setPendingReviewFiles] = useState([]);
  const [pendingDocumentReview, setPendingDocumentReview] = useState(null);

  function approvedMapFromSubmissions(submissions = []) {
    setAdditionalFiles(additionalDocumentsFromSubmissions(submissions));
    const files = (Array.isArray(submissions) ? submissions : []).flatMap((sub) =>
      Array.isArray(sub?.files)
        ? sub.files.map((file) => ({
            ...file,
            __submissionId: sub.id,
          }))
        : []
    );

    setPendingReviewFiles(pendingReviewFilesFromSubmissions(submissions, requirementOptionMatchesDetectedDocumentKind));
    setIntakePendingFiles(files.filter(f => f.source === "client_document_intake" && requiresDocumentReview(f) && !["approved", "rejected"].includes(f.review?.status)));
    const grouped = {};
    for (const f of files) {
      if (f?.review?.status !== "approved") continue;

      const k = cleanStr(f?.review?.requirementKey);
      if (!k) continue;

      if (!grouped[k]) grouped[k] = [];
      grouped[k].push(f);
    }

    const next = {};
    Object.entries(grouped).forEach(([key, approvedFiles]) => {
      next[key] = {
        ...(approvedFiles?.[0] || {}),
        relatedApprovedFiles: approvedFiles,
      };
    });
    return next;
  }

  async function refreshApprovedDocuments({ clearOnFailure = true } = {}) {
    if (!clientId) return {};

    try {
      const directSnap = await getDoc(doc(db, "documentSubmissions", clientId));
      if (directSnap.exists()) {
        const directMap = approvedMapFromSubmissions([
          { id: directSnap.id, ...(directSnap.data() || {}) },
        ]);
        setApprovedMap(directMap);
        return directMap;
      }

      const orgId = String(form?.orgId || userOrgId || "").trim();
      const role = String(userRole || "").trim().toLowerCase();
      const teamId = String(form?.teamId || userTeamId || "").trim();
      const ownerUid = String(form?.ownerUid || userUid || "").trim();

      const filters = [
        where("clientId", "==", clientId),
        where("type", "==", "documents"),
      ];

      if (!isAdmin) {
        if (!orgId) {
          if (clearOnFailure) setApprovedMap({});
          return {};
        }

        filters.push(where("orgId", "==", orgId));

        if (role !== "manager") {
          if (teamId) {
            filters.push(where("accessScope", "==", "team"));
            filters.push(where("teamId", "==", teamId));
          } else if (ownerUid) {
            filters.push(where("accessScope", "==", "individual"));
            filters.push(where("ownerUid", "==", ownerUid));
          } else {
            if (clearOnFailure) setApprovedMap({});
            return {};
          }
        }
      }

      const snap = await getDocs(
        query(collection(db, "documentSubmissions"), ...filters, limit(10))
      );
      const submissions = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() || {}),
      }));
      const next = approvedMapFromSubmissions(submissions);
      setApprovedMap(next);
      return next;
    } catch (e) {
      console.warn("Failed to load approved docs:", e?.message || e);
      if (clearOnFailure) setApprovedMap({});
      return {};
    }
  }

  useEffect(() => {
    setAdditionalFiles([]);
    if (!clientId) return undefined;
    // Also refresh after intake saves a document without changing KYC fields.
    return onSnapshot(doc(db, "documentSubmissions", clientId), snap => {
      if (snap.exists()) setApprovedMap(approvedMapFromSubmissions([{ id: snap.id, ...(snap.data() || {}) }]));
      else refreshApprovedDocuments();
    }, () => refreshApprovedDocuments());
    // eslint-disable-next-line
  }, [clientId, form?.orgId, form?.ownerUid, form?.teamId, isAdmin, userOrgId, userRole, userTeamId, userUid, headerStatusText]);

  async function uncheckKycRequirement(requirementKey) {
    const parsed = parseRequirementKey(requirementKey);
    if (!clientId || !parsed) return;

    const kycRef = doc(db, "kyc", clientId);

    if (parsed.kind === "party" && parsed.partyId && parsed.docKey) {
      const resolvedPartyId = resolveChecklistPartyId(parsed.partyId);
      await updateDoc(
        kycRef,
        updateDocFieldPath(
          "documentsChecklist",
          "parties",
          resolvedPartyId,
          "docs",
          parsed.docKey,
          "obtained"
        ),
        false,
        "updatedAt",
        serverTimestamp()
      );

      if (!readOnly) setPartyObtained(parsed.partyId, parsed.docKey, false);
      return;
    }

    if (parsed.kind === "sow" && parsed.sowKey && parsed.docKey) {
      await updateDoc(
        kycRef,
        updateDocFieldPath(
          "documentsChecklist",
          "sow",
          parsed.sowKey,
          "docs",
          parsed.docKey,
          "obtained"
        ),
        false,
        "updatedAt",
        serverTimestamp()
      );

      if (!readOnly) setSowDocObtained(parsed.sowKey, parsed.docKey, false);
    }
  }

  async function performApprovedDocumentDelete({
    requirementKey,
    filesToDelete,
    additional = false,
  }) {
    const count = filesToDelete.length;
    setDeletingDocumentKey(requirementKey);

    try {
      const objectPaths = new Set(filesToDelete.map((x) => cleanStr(x.objectPath)));
      for (const objectPath of objectPaths) {
        try {
          await deleteObject(storageRef(storage, objectPath));
        } catch (error) {
          if (error?.code !== "storage/object-not-found") throw error;
        }
      }

      const submissionIds = Array.from(
        new Set(filesToDelete.map((x) => cleanStr(x.__submissionId)).filter(Boolean))
      );

      for (const submissionId of submissionIds) {
        const subRef = doc(db, "documentSubmissions", submissionId);
        const subSnap = await getDoc(subRef);
        if (!subSnap.exists()) continue;

        const subData = subSnap.data() || {};
        const remainingFiles = (Array.isArray(subData.files) ? subData.files : []).filter(
          (x) => !objectPaths.has(cleanStr(x?.objectPath))
        );
        const realFiles = remainingFiles.filter((x) => cleanStr(x?.objectPath));
        const allReviewed =
          realFiles.length > 0 &&
          realFiles.every((x) =>
            !requiresDocumentReview(x) || ["approved", "rejected"].includes(cleanStr(x?.review?.status))
          );

        await updateDoc(subRef, {
          files: remainingFiles,
          status: additional && !realFiles.some(requiresDocumentReview) && subData.status !== "awaiting_client"
            ? "stored"
            : realFiles.length
            ? allReviewed
              ? "reviewed"
              : "pending_review"
            : "awaiting_client",
          updatedAt: serverTimestamp(),
        });
      }

      if (!additional) await uncheckKycRequirement(requirementKey);

      setApprovedMap((prev) => {
        const next = { ...(prev || {}) };
        delete next[requirementKey];
        return next;
      });
      setStaffSavedAttachmentMap((prev) => {
        const next = { ...(prev || {}) };
        delete next[requirementKey];
        return next;
      });
      if (additional) setAdditionalFiles(prev => prev.filter(file => !objectPaths.has(file.objectPath)));

      if (
        cleanStr(previewFile?.review?.requirementKey) === requirementKey ||
        (Array.isArray(previewFile?.relatedApprovedFiles) &&
          previewFile.relatedApprovedFiles.some((x) =>
            objectPaths.has(cleanStr(x?.objectPath))
          ))
      ) {
        closePreviewModal();
      }

      showLocalToast({
        severity: "success",
        summary: "Document deleted",
        detail: additional ? "The document was removed from Other documents." :
          count > 1
            ? "The files were removed and the checklist item was unticked."
            : "The file was removed and the checklist item was unticked.",
        life: 3200,
      });
    } catch (error) {
      console.warn("Failed to delete approved document:", error?.message || error);
      showLocalToast({
        severity: "error",
        summary: "Delete failed",
        detail: error?.message || "The document could not be deleted.",
        life: 4200,
      });
    } finally {
      setDeletingDocumentKey("");
    }
  }

  function deleteApprovedDocument(file) {
    if (readOnly || deletingDocumentKey) return;
    const requirementKey = cleanStr(file?.review?.requirementKey);
    const approvedFiles =
      Array.isArray(file?.relatedApprovedFiles) &&
      file.relatedApprovedFiles.length
        ? file.relatedApprovedFiles
        : [file];
    const filesToDelete = approvedFiles.filter((x) => cleanStr(x?.objectPath));
    if (!clientId || !requirementKey || !filesToDelete.length) return;

    const count = filesToDelete.length;
    appConfirm({
      group: "appConfirm",
      header: "Delete document",
      message:
        count > 1
          ? `Delete the ${count} files attached to this document requirement?\n\nThis will remove the files and untick the checklist item. This cannot be undone.`
          : "Delete this document?\n\nThis will remove the file and untick the checklist item. This cannot be undone.",
      defaultFocus: "reject",
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      scheme: "danger",
      icon: "bi bi-exclamation-triangle",
      accept: () =>
        performApprovedDocumentDelete({
          requirementKey,
          filesToDelete,
        }),
    });
  }

  function deleteAdditionalDocument(file) {
    if (readOnly || !clientId || !file?.objectPath || deletingDocumentKey) return;
    appConfirm({
      group: "appConfirm", header: "Delete document",
      message: "Delete this additional document? This will permanently remove the file.",
      defaultFocus: "reject", acceptLabel: "Delete", rejectLabel: "Cancel", scheme: "danger",
      icon: "bi bi-exclamation-triangle",
      accept: () => performApprovedDocumentDelete({
        requirementKey: file.review?.requirementKey || file.objectPath,
        filesToDelete: [file], additional: true,
      }),
    });
  }

  function fileWithUploadContext(file, uploadContext) {
    if (!file || !uploadContext) return file;
    const meta = uploadContext?.cardSnapshot?.meta || {};
    const enrich = (item) => ({
      ...(item || {}),
      requirementText:
        cleanStr(item?.requirementText) || cleanStr(uploadContext.requirementText),
      cardId: cleanStr(item?.cardId) || cleanStr(uploadContext.cardId),
      cardTitle: cleanStr(item?.cardTitle) || cleanStr(uploadContext.cardTitle),
      cardSnapshot: item?.cardSnapshot || uploadContext.cardSnapshot,
      reviewMeta: {
        ...(meta || {}),
        ...(item?.reviewMeta || {}),
      },
      review: {
        ...(item?.review || {}),
        requirementKey:
          cleanStr(item?.review?.requirementKey) ||
          cleanStr(uploadContext.requirementKey),
        requirementText:
          cleanStr(item?.review?.requirementText) ||
          cleanStr(uploadContext.requirementText),
      },
    });
    const relatedApprovedFiles = Array.isArray(file?.relatedApprovedFiles)
      ? file.relatedApprovedFiles.map(enrich)
      : undefined;
    return {
      ...enrich(file),
      ...(relatedApprovedFiles ? { relatedApprovedFiles } : {}),
    };
  }

  async function openApprovedDocumentPreview(file) {
    const requestSeq = previewRequestSeqRef.current + 1;
    previewRequestSeqRef.current = requestSeq;
    revokePreviewBlobUrls(previewFile);
    const loadingName =
      cleanStr(file?.originalName || file?.name) ||
      cleanStr(file?.relatedApprovedFiles?.[0]?.originalName || file?.relatedApprovedFiles?.[0]?.name) ||
      "Document";
    setPreviewFile({
      ...(file || {}),
      originalName: loadingName,
      name: loadingName,
      relatedApprovedFiles: [],
    });
    setPreviewLoading(true);
    setPreviewOpen(true);

    const approvedFiles =
      Array.isArray(file?.relatedApprovedFiles) && file.relatedApprovedFiles.length
        ? file.relatedApprovedFiles
        : [file];
    const filesWithUrls = await Promise.all(
      approvedFiles.map(async (approvedFile) => {
        const blobUrl = await fileToAuthenticatedBlobUrl(approvedFile, clientId);
        return {
          ...approvedFile,
          downloadURL: blobUrl,
          downloadUrl: blobUrl,
          url: blobUrl,
        };
      })
    );
    if (previewRequestSeqRef.current !== requestSeq) {
      filesWithUrls.forEach((item) => revokePreviewBlobUrls(item));
      return;
    }
    const firstFile = filesWithUrls[0] || file;
    setPreviewFile({
      ...firstFile,
      relatedApprovedFiles: filesWithUrls,
    });
    setPreviewLoading(false);
    setPreviewOpen(true);
  }

  function ApprovedDocumentActions({ file, uploadContext }) {
    if (!file && !uploadContext) return null;

    const contextFile = fileWithUploadContext(file, uploadContext);
    const requirementKey = cleanStr(contextFile?.review?.requirementKey);
    const deleting = requirementKey && deletingDocumentKey === requirementKey;

    return (
      <div className="kycDocApprovedActions">
        {file ? (
          <button
            type="button"
            className="btn btn-xs btn-outline-secondary"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openApprovedDocumentPreview(contextFile).catch((error) => {
                closePreviewModal();
                showLocalToast?.({
                  severity: "error",
                  summary: "Preview failed",
                  detail: error?.message || "The document could not be opened.",
                  life: 4200,
                });
              });
            }}
          >
            <i className="bi bi-eye me-1" />
            Show
          </button>
        ) : null}
        {uploadContext ? <StaffAttachRequirementActions context={uploadContext} /> : null}
        {file && !readOnly ? (
          <button
            type="button"
            className="btn btn-xs btn-outline-secondary kycDocDeleteApprovedBtn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              deleteApprovedDocument(contextFile);
            }}
            disabled={deleting}
            title="Delete document"
            aria-label="Delete document"
          >
            {deleting ? (
              <span className="kycDocDeleteSpinner" aria-hidden="true" />
            ) : (
              <i className="bi bi-trash3" />
            )}
          </button>
        ) : null}
      </div>
    );
  }

  function buildPartyStaffAttachContext(p, docKey, label, extra = {}) {
    const stablePartyId = stableDocumentPartyId(p);
    const requirementKey = partyRequirementKey(p, docKey);
    const cardId = `party_${slugId(stablePartyId || p.partyId)}`;
    const isEntity = p.partyType === "entity";
    return {
      requirementKey,
      requirementText: label,
      cardId,
      cardTitle: `${p.name || "Party"} ${
        isEntity
          ? `(Country: ${p.corporateCountry || p.addressCountry || "—"})`
          : `(Nationality: ${p.idCountry || "—"})`
      }`.trim(),
      cardSnapshot: {
        id: cardId,
        section: "Identification documents",
        title: `${p.name || "Party"} ${
          isEntity
            ? `(Country: ${p.corporateCountry || p.addressCountry || "—"})`
            : `(Nationality: ${p.idCountry || "—"})`
        }`.trim(),
        description: `• ${label}`,
        meta: {
          kind: p.partyType || "",
          partyId: stablePartyId || p.partyId || "",
          displayPartyId: p.partyId || "",
          sourcePartyIds: p.sourcePartyIds || [],
          partyName: p.name || "",
          partyRole: p.role || "",
          registeredAddress: p.registeredAddress || p.address || "",
          partyAddress: p.registeredAddress || p.address || "",
          nationalityIso2: p.idCountry || "",
          domicileIso2: p.addressCountry || "",
          incorporationIso2: p.corporateCountry || "",
          ...(extra.meta || {}),
        },
        options: [{ key: requirementKey, label, acceptedLabels: extra.acceptedLabels || [] }],
      },
    };
  }

  function buildSowStaffAttachContext(it, docKey, doc) {
    const requirementKey =
      doc?.requirement === "group" && doc?.groupId
        ? `sow:${it.sowKey}:grp:${doc.groupId}:${docKey}`
        : `sow:${it.sowKey}:${doc?.requirement === "optional" ? "opt" : "req"}:${docKey}`;
    const label = doc?.label || docKey;
    const cardId = `sow_${slugId(it.sowKey || it.label)}`;
    return {
      requirementKey,
      requirementText: label,
      cardId,
      cardTitle: it.label || "Source of wealth",
      cardSnapshot: {
        id: cardId,
        section: "Source of Wealth corroboration",
        title: it.label || "Source of wealth",
        description: `• ${label}`,
        meta: { kind: "sow", sowKey: it.sowKey || "" },
        options: [{ key: requirementKey, label }],
      },
    };
  }

  function resetStaffAttach() {
    setStaffAttach({
      open: false,
      context: null,
      file: null,
      serverFile: null,
      scanRequired: false,
      scanResult: null,
      stage: "",
      error: "",
      busy: false,
      saving: false,
    });
    setStaffAttachDetail(null);
    setStaffAttachKycUpdateConfirm({
      open: false,
      options: [],
      selected: {},
      isCorporate: false,
      isProofOfAddress: false,
    });
  }

  async function pollStaffAttachScan(objectPath, docKey, timeoutMs = 90000) {
    const cleanObjectPath = cleanStr(objectPath);
    const started = Date.now();
    while (Date.now() - started < timeoutMs) {
      if (staffAttachScanSkipRef.current.has(cleanObjectPath)) {
        return { file: null, result: null, skipped: true };
      }
      const snap = await getDoc(doc(db, "documentSubmissions", clientId));
      const data = snap.exists() ? snap.data() || {} : {};
      const file = (Array.isArray(data.files) ? data.files : []).find(
        (x) => cleanStr(x?.objectPath) === cleanObjectPath
      );
      const result = staffAttachScanResultFromFile(file, docKey);
      if (result && scanStatusDone(result)) {
        return { file, result };
      }
      await new Promise((resolve) => window.setTimeout(resolve, 1800));
    }
    return { file: null, result: { status: "manual_review", overallStatus: "manual_review", summary: "Scan unavailable." } };
  }

  function skipStaffAttachScan(e) {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const objectPath = cleanStr(staffAttach?.serverFile?.objectPath);
    if (!objectPath) return;
    staffAttachScanSkipRef.current.add(objectPath);
    setStaffAttachDetail(null);
    setStaffAttach((prev) => ({
      ...prev,
      scanRequired: false,
      scanResult: null,
      stage: "Scan skipped. Ready to save.",
      busy: false,
    }));
  }

  async function startStaffAttach(context, file) {
    if (readOnly || !clientId || !context || !file || staffAttach.open) return;
    if (onDocumentIntakeStart) {
      await onDocumentIntakeStart([file], context);
      return;
    }
    if (!allowedStaffAttachFile(file)) {
      showLocalToast({
        severity: "warn",
        summary: "Unsupported file",
        detail: "Upload a PDF or image file.",
        life: 3200,
      });
      return;
    }

    const requirement = parseRequirementKey(context.requirementKey);
    // A shareholder's document must never propose changes to the customer.
    const extractCustomerDetails = requirement?.kind === "party" &&
      requirement.partyId === "customer" && requirement.docKey === "reg" && form.customer?.type === "entity";
    setStaffAttach({
      open: true,
      context,
      file,
      serverFile: null,
      scanRequired: false,
      scanResult: null,
      stage: "Preparing file…",
      error: "",
      busy: true,
      saving: false,
      intakeReview: extractCustomerDetails,
      intakeDocuments: [],
    });
    setStaffAttachDetail(null);

    try {
      let intakeDocuments = [];
      if (extractCustomerDetails) {
        setStaffAttach(prev => ({ ...prev, stage: "Reading document…" }));
        try {
          const extraction = await extractKycDocuments([file], form, undefined, progress => {
            setStaffAttach(prev => ({ ...prev, stage: progress.stage === "extracting" ? "Extracting document details…" : "Reading document…" }));
          });
          intakeDocuments = extraction.documents.filter(document => document.category === "trade_licence");
        } catch (error) {
          // Other corporate documents and failed extraction retain the existing
          // attachment/manual-review flow. Never manufacture extracted details.
          console.warn("Checklist detail extraction could not be completed:", error);
        }
        if (intakeDocuments.length) {
          // Persist the identified type with the attachment from the start, so
          // approval checks only this document's requirement after a reload.
          context = { ...context, requirementText: "Trade licence" };
        }
        setStaffAttach(prev => ({ ...prev, context, intakeDocuments, intakeReview: !!intakeDocuments.length }));
      }
      const dataBase64 = await fileToDataUrl(file);
      setStaffAttach((prev) => ({ ...prev, stage: "Uploading document…" }));
      const user = auth?.currentUser;
      if (!user) throw new Error("You are not logged in.");
      const token = await user.getIdToken();
      const base = staffAttachApiBase();
      const resp = await fetch(`${base}/myKyc/staff/doc-upload/attach/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          clientId,
          requirementKey: context.requirementKey,
          requirementText: context.requirementText,
          cardId: context.cardId,
          cardTitle: context.cardTitle,
          cardSnapshot: context.cardSnapshot,
          file: {
            name: file.name,
            contentType: file.type,
            sizeBytes: file.size,
            dataBase64,
          },
        }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || json?.ok !== true) {
        if (resp.status === 413) {
          throw new Error("The file is too large for the upload endpoint. Please try a smaller PDF/image or contact support to increase the upload limit.");
        }
        throw new Error(json?.error || "Upload failed");
      }

      setStaffAttach((prev) => ({
        ...prev,
        serverFile: json.file,
        scanRequired: !!json.scanRequired,
        stage: json.scanRequired ? "Scanning document…" : "Ready to save.",
      }));

      if (intakeDocuments.length) {
        // Consistency verification is independent of extraction; an incomplete
        // automated check must not hide the successfully extracted proposals.
        setStaffAttach(prev => ({ ...prev, busy: false, stage: "Review document details." }));
      } else if (json.scanRequired) {
        const objectPath = cleanStr(json.file?.objectPath);
        staffAttachScanSkipRef.current.delete(objectPath);
        const { file: scannedFile, result, skipped } = await pollStaffAttachScan(objectPath, parseRequirementKey(context.requirementKey)?.docKey);
        if (skipped) return;
        const comparedResult = compareStaffAttachIdScanWithContext(result, context);
        setStaffAttach((prev) => ({
          ...(staffAttachScanSkipRef.current.has(objectPath)
            ? prev
            : {
                ...prev,
                serverFile: scannedFile || prev.serverFile,
                scanResult: comparedResult,
                stage: staffAttachScanKind(comparedResult, context) === "corporate"
                  ? corporateScanPresentation(comparedResult).stage : "Scan complete.",
                busy: false,
              }),
        }));
      } else {
        setStaffAttach((prev) => ({ ...prev, busy: false }));
      }
    } catch (e) {
      setStaffAttach((prev) => ({
        ...prev,
        stage: "",
        error: e?.message || "Upload failed.",
        busy: false,
      }));
    }
  }

  async function cancelStaffAttach() {
    const objectPath = cleanStr(staffAttach?.serverFile?.objectPath);
    if (objectPath) {
      staffAttachScanSkipRef.current.add(objectPath);
    }
    resetStaffAttach();

    if (!objectPath || staffAttach.attachmentSaved) return;

    try {
      const user = auth?.currentUser;
      const token = await user?.getIdToken?.();
      const base = staffAttachApiBase();
      await fetch(`${base}/myKyc/staff/doc-upload/attach/cancel`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ clientId, objectPath }),
      }).catch(() => {});
    } catch (_) {}
  }

  async function openStaffAttachPreview(e) {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    const serverFile = staffAttach?.serverFile || {};
    const localFile = staffAttach?.file || {};
    const objectPath = cleanStr(serverFile?.objectPath);
    if (!objectPath && !localFile) {
      showLocalToast?.({
        severity: "warn",
        summary: "Preview not ready",
        detail: "The document preview will be available after the upload is ready.",
        life: 3500,
      });
      return;
    }

    const requestSeq = previewRequestSeqRef.current + 1;
    previewRequestSeqRef.current = requestSeq;
    revokePreviewBlobUrls(previewFile);

    const displayFile = {
      ...serverFile,
      name:
        serverFile.name ||
        serverFile.originalName ||
        localFile.name ||
        "Document",
      originalName:
        serverFile.originalName ||
        serverFile.name ||
        localFile.name ||
        "Document",
      contentType:
        serverFile.contentType ||
        serverFile.mimeType ||
        localFile.type ||
        "",
      mimeType:
        serverFile.mimeType ||
        serverFile.contentType ||
        localFile.type ||
        "",
      size: serverFile.size || serverFile.sizeBytes || localFile.size || 0,
    };

    setPreviewFile(displayFile);
    setPreviewLoading(true);
    setPreviewOpen(true);
    await waitForPreviewLoaderPaint();
    if (previewRequestSeqRef.current !== requestSeq) return;

    try {
      const existingUrl = pickFileUrl(serverFile);
      let previewUrl = existingUrl;
      if (!previewUrl && objectPath) {
        previewUrl = await fileToAuthenticatedBlobUrl(displayFile, clientId);
      }
      if (!previewUrl && localFile) {
        previewUrl = window.URL.createObjectURL(localFile);
      }
      if (previewRequestSeqRef.current !== requestSeq) {
        if (previewUrl?.startsWith?.("blob:")) {
          try {
            window.URL.revokeObjectURL(previewUrl);
          } catch {
            // best effort cleanup only
          }
        }
        return;
      }
      if (!previewUrl) throw new Error("Preview is not ready yet.");
      setPreviewFile({
        ...displayFile,
        downloadURL: previewUrl,
        downloadUrl: previewUrl,
        url: previewUrl,
      });
      setPreviewLoading(false);
      setPreviewOpen(true);
    } catch (error) {
      if (previewRequestSeqRef.current !== requestSeq) return;
      setPreviewLoading(false);
      showLocalToast?.({
        severity: "error",
        summary: "Preview failed",
        detail: error?.message || "The document preview could not be loaded.",
        life: 4200,
      });
    }
  }

  function staffAttachKycUpdateOptionsForCurrentUpload() {
    const context = staffAttach?.context || {};
    const result = staffAttach?.scanResult || null;
    if (!result) return [];
    const kind = staffAttachScanKind(result, context);
    const parsed = parseRequirementKey(context.requirementKey);
    if (kind === "corporate") {
      return staffAttachCorporateUpdateOptions({ result, form });
    }
    if (kind === "id" && parsed?.kind === "party" && parsed?.docKey === "id") {
      return staffAttachIdUpdateOptions({ result, form, context });
    }
    if (kind === "utility" && parsed?.kind === "party" && parsed?.docKey === "poa") {
      return staffAttachProofOfAddressUpdateOptions({ result, form, context });
    }
    return [];
  }

  function closeStaffAttachKycUpdateConfirm() {
    setStaffAttachKycUpdateConfirm({
      open: false,
      options: [],
      selected: {},
      isCorporate: false,
      isProofOfAddress: false,
    });
    setStaffAttach((prev) => ({ ...prev, saving: false }));
  }

  async function applyStaffAttachKycProfileUpdates(options = [], selected = {}) {
    const updates = staffAttachKycProfileUpdates({ options, selected, form });
    const updateKeys = Object.keys(updates);
    if (!clientId || !updateKeys.length) return;
    const safeUpdates = sanitizeFirestoreUpdatePayload(updates);
    const safeUpdateKeys = Object.keys(safeUpdates);
    if (!safeUpdateKeys.length) return;

    await updateDoc(doc(db, "kyc", clientId), {
      ...safeUpdates,
      updatedAt: serverTimestamp(),
    });

    safeUpdateKeys.forEach((key) => {
      if (key.includes(".")) onSetAt?.(key.split("."), safeUpdates[key]);
    });
  }

  function markStaffAttachSavedLocally(context, rawSavedFile) {
    if (!context?.requirementKey) return;

    setApprovedMap((prev) => {
      const current = (prev || {})[context.requirementKey] || null;
      const existingRelated = Array.isArray(current?.relatedApprovedFiles)
        ? current.relatedApprovedFiles
        : current
          ? [current]
          : [];
      const byPath = new Map();
      [...existingRelated, rawSavedFile].forEach((file) => {
        const path = cleanStr(file?.objectPath);
        if (!path) return;
        byPath.set(path, file);
      });
      const relatedApprovedFiles = Array.from(byPath.values());
      const savedFile = {
        ...(relatedApprovedFiles[0] || rawSavedFile),
        relatedApprovedFiles,
      };
      return {
        ...(prev || {}),
        [context.requirementKey]: savedFile,
      };
    });
    setStaffSavedAttachmentMap((prev) => ({
      ...(prev || {}),
      [context.requirementKey]: {
        ...(rawSavedFile || {}),
        relatedApprovedFiles: Array.isArray(rawSavedFile?.relatedApprovedFiles)
          ? rawSavedFile.relatedApprovedFiles
          : [rawSavedFile].filter((file) => cleanStr(file?.objectPath)),
      },
    }));

    const parsed = parseRequirementKey(context.requirementKey);
    if (parsed?.kind === "party") {
      setPartyObtained(parsed.partyId, parsed.docKey, true);
    } else if (parsed?.kind === "sow") {
      setSowDocObtained(parsed.sowKey, parsed.docKey, true);
    }
  }

  async function saveStaffAttach({
    kycUpdateOptions = [],
    selectedKycUpdates = {},
    intakeValues = null,
  } = {}) {
    const objectPath = cleanStr(staffAttach?.serverFile?.objectPath);
    const context = staffAttach?.context || {};
    if (readOnly || staffAttach.saving || !objectPath || !context.requirementKey) return;

    setStaffAttach((prev) => ({ ...prev, saving: true, error: "" }));
    try {
      let rawSavedFile = staffAttach.serverFile;
      if (!staffAttach.attachmentSaved) {
        const user = auth?.currentUser;
        const token = await user?.getIdToken?.();
        const base = staffAttachApiBase();
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 45000);
        const resp = await fetch(`${base}/myKyc/staff/doc-upload/attach/save`, {
          method: "POST",
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            clientId,
            objectPath,
            requirementKey: context.requirementKey,
            requirementText: context.requirementText,
          }),
        }).finally(() => clearTimeout(timeoutId));
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok || json?.ok !== true) {
          throw new Error(json?.error || "Save failed");
        }

        rawSavedFile = { ...(staffAttach.serverFile || {}), ...(json.file || {}) };
        setStaffAttach(prev => ({ ...prev, serverFile: rawSavedFile, attachmentSaved: true }));
      }
      markStaffAttachSavedLocally(context, rawSavedFile);

      try {
        if (intakeValues !== null) await applyChecklistIntakeValues(intakeValues);
        else await applyStaffAttachKycProfileUpdates(kycUpdateOptions, selectedKycUpdates);
        markStaffAttachSavedLocally(context, rawSavedFile);
        await refreshApprovedDocuments({ clearOnFailure: false });
      } catch (profileUpdateError) {
        if (intakeValues !== null) throw profileUpdateError;
        console.warn("Document attached, but KYC profile update failed:", profileUpdateError);
        showLocalToast({
          severity: "warn",
          summary: "Document attached",
          detail: profileUpdateError?.message || "The KYC profile update could not be completed.",
          life: 4500,
        });
        await refreshApprovedDocuments({ clearOnFailure: false });
        resetStaffAttach();
        return;
      }

      showLocalToast({
        severity: "success",
        summary: "Document attached",
        detail: "The checklist requirement has been satisfied.",
        life: 3200,
      });
      resetStaffAttach();
    } catch (e) {
      setStaffAttach((prev) => ({
        ...prev,
        saving: false,
        error:
          e?.name === "AbortError"
            ? "Save timed out. Please check the API and try again."
            : e?.message || "Save failed.",
      }));
    }
  }

  async function applyChecklistIntakeValues(values) {
    const next = applyKycIntake(form, values);
    const updates = Object.fromEntries(Object.entries(next.customer).filter(([key, value]) =>
      value !== form.customer?.[key]).map(([key, value]) => [`customer.${key}`, value]));
    const safeUpdates = sanitizeFirestoreUpdatePayload(updates);
    if (!Object.keys(safeUpdates).length) return;
    await updateDoc(doc(db, "kyc", clientId), { ...safeUpdates, updatedAt: serverTimestamp() });
    Object.entries(safeUpdates).forEach(([key, value]) => onSetAt?.(key.split("."), value));
  }

  async function finishChecklistIntake(values, documents) {
    if (readOnly || staffAttach.busy || staffAttach.saving) return;
    if (documents.length || staffAttach.attachmentSaved) {
      await saveStaffAttach({ intakeValues: values });
      return;
    }
    // Removing the original still allows explicitly selected details to be used.
    setStaffAttach(prev => ({ ...prev, saving: true, error: "" }));
    try {
      await applyChecklistIntakeValues(values);
      await cancelStaffAttach();
    } catch (error) {
      setStaffAttach(prev => ({ ...prev, saving: false, error: error.message }));
    }
  }

  async function handleStaffAttachSaveClick() {
    const options = staffAttachKycUpdateOptionsForCurrentUpload();
    if (options.length) {
      setStaffAttach((prev) => ({ ...prev, error: "" }));
      setStaffAttachKycUpdateConfirm({
        open: true,
        options,
        selected: staffAttachSelectedUpdatesForOptions(options),
        isCorporate: staffAttachScanKind(staffAttach?.scanResult, staffAttach?.context) === "corporate",
        isProofOfAddress: staffAttachScanKind(staffAttach?.scanResult, staffAttach?.context) === "utility",
      });
      return;
    }
    await saveStaffAttach();
  }

  function canSaveStaffAttach() {
    return (
      staffAttach.open &&
      !staffAttach.busy &&
      !staffAttach.saving &&
      !!staffAttach.serverFile &&
      !(staffAttach.scanRequired && !staffAttach.scanResult)
    );
  }

  function canDismissStaffAttach() {
    return (
      staffAttach.open &&
      !staffAttach.saving &&
      (!staffAttach.busy || !!cleanStr(staffAttach?.serverFile?.objectPath))
    );
  }

  useEffect(() => {
    if (!staffAttach.open || staffAttach.intakeReview) return undefined;

    const onKeyDown = (event) => {
      if (
        event.key !== "Enter" ||
        event.shiftKey ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        event.isComposing
      ) {
        return;
      }
      if (staffAttachKycUpdateConfirm.open || previewOpen) return;

      const target = event.target;
      const tagName = String(target?.tagName || "").toLowerCase();
      if (["input", "textarea", "select"].includes(tagName)) return;
      if (target?.closest?.("button, a, [role='button']")) return;
      if (!canSaveStaffAttach()) return;

      event.preventDefault();
      handleStaffAttachSaveClick();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    staffAttach.open,
    staffAttach.intakeReview,
    staffAttach.busy,
    staffAttach.saving,
    staffAttach.serverFile,
    staffAttach.scanRequired,
    staffAttach.scanResult,
    staffAttachKycUpdateConfirm.open,
    previewOpen,
  ]);

  async function confirmStaffAttachSaveWithKycUpdates(applyUpdates) {
    const options = staffAttachKycUpdateConfirm.options || [];
    const selected = staffAttachKycUpdateConfirm.selected || {};
    setStaffAttachKycUpdateConfirm((prev) => ({ ...prev, open: false }));
    await saveStaffAttach({
      kycUpdateOptions: applyUpdates ? options : [],
      selectedKycUpdates: applyUpdates ? selected : {},
    });
  }

  function handleStaffAttachKycUpdateContinue() {
    confirmStaffAttachSaveWithKycUpdates(true).catch((e) => {
      setStaffAttach((prev) => ({
        ...prev,
        saving: false,
        error: e?.message || "Save failed.",
      }));
    });
  }

  useEffect(() => {
    if (!staffAttachKycUpdateConfirm.open) return undefined;
    const timer = window.setTimeout(() => {
      staffAttachKycUpdateContinueBtnRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [
    staffAttachKycUpdateConfirm.open,
  ]);

  useEffect(() => {
    if (!staffAttachKycUpdateConfirm.open) return undefined;

    const onKeyDown = (event) => {
      if (
        event.key !== "Enter" ||
        event.shiftKey ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        event.isComposing
      ) {
        return;
      }
      event.preventDefault();
      handleStaffAttachKycUpdateContinue();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    staffAttachKycUpdateConfirm.open,
    staffAttachKycUpdateConfirm.options,
    staffAttachKycUpdateConfirm.selected,
  ]);

  function staffAttachDropProps(context) {
    const key = cleanStr(context?.requirementKey);
    // Retain requirement-level zones for future use; the card owns drops now.
    const requirementDropEnabled = false;
    if (!requirementDropEnabled || readOnly || !key) return {};
    return {
      onDragOver: (e) => {
        e.preventDefault();
        setDragAttachKey(key);
      },
      onDragLeave: () => setDragAttachKey((prev) => (prev === key ? "" : prev)),
      onDrop: (e) => {
        e.preventDefault();
        setDragAttachKey("");
        const file = e.dataTransfer?.files?.[0];
        if (file) startStaffAttach(context, file);
      },
    };
  }

  function checklistDropProps() {
    if (readOnly || intakeLoadingMessage || !onDocumentIntakeStart) return {};
    const hasFiles = e => Array.from(e.dataTransfer?.types || []).includes("Files") || e.dataTransfer?.files?.length;
    return {
      onDragOver: e => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "copy";
        setDragAttachKey("checklist-card");
        const rect = e.currentTarget.getBoundingClientRect();
        const visibleTop = Math.max(0, rect.top);
        const visibleBottom = Math.min(window.innerHeight, rect.bottom);
        const bounds = {
          top: rect.top, left: rect.left, width: rect.width, height: rect.height,
          "--drop-label-top": `${Math.max(0, (visibleTop + visibleBottom) / 2 - rect.top)}px`,
        };
        setChecklistDropBounds(prev => prev && Object.keys(bounds).every(key => prev[key] === bounds[key]) ? prev : bounds);
      },
      onDragLeave: e => {
        if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return;
        setDragAttachKey("");
        setChecklistDropBounds(null);
      },
      onDrop: e => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        e.stopPropagation();
        setDragAttachKey("");
        setChecklistDropBounds(null);
        const files = Array.from(e.dataTransfer?.files || []);
        // Do not bind a drop to the requirement/party underneath the pointer.
        if (files.length) onDocumentIntakeStart(files);
      },
    };
  }

  function StaffAttachRequirementActions({ context }) {
    if (readOnly || !context) return null;
    const pendingFile = pendingReviewFiles.find(file => pendingFileMatchesRequirement(file, context));
    return (
      <>
      {pendingFile && <button
        type="button"
        className="kycDocPendingReviewBtn"
        title={pendingFile.__assignmentPending ? "Review uploaded document — assignment pending" : "Review document — pending"}
        aria-label={`Review ${context.requirementText || "document"} — pending`}
        onClick={e => {
          e.preventDefault(); e.stopPropagation();
          setPendingDocumentReview({docId: pendingFile.__submissionId || clientId, objectPath: pendingFile.objectPath});
        }}
      ><i className="bi bi-eye" aria-hidden="true" /></button>}
      <button
        type="button"
        className="btn btn-xs btn-outline-secondary kycDocStaffAttachBtn"
        title="Upload document"
        aria-label="Upload document"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const input = document.createElement("input");
          input.type = "file";
          input.accept = "application/pdf,image/*";
          input.onchange = () => {
            const file = input.files?.[0];
            if (file) startStaffAttach(context, file);
          };
          input.click();
        }}
      >
        <i className="bi bi-upload" />
      </button>
      </>
    );
  }

  async function onSendDocUploadLinkViaServer() {
    if (intakePendingFiles.length) {
      showLocalToast({ severity: "info", summary: "Documents already received", detail: "Review the uploaded documents before requesting missing documents.", life: 4000 });
      return;
    }
    try {
      setDocUploadBusy(true);

      if (!clientId) {
        showLocalToast({
          severity: "warn",
          summary: "Missing clientId",
          detail: "Cannot send upload link without clientId.",
          life: 3500,
        });
        return;
      }

      const user = auth?.currentUser;
      if (!user) {
        showLocalToast({
          severity: "warn",
          summary: "Not logged in",
          detail: "Please login first.",
          life: 3500,
        });
        return;
      }

      if ((derivedSowItems || []).length && !supportingDocRiskReady) {
        showLocalToast({
          severity: "warn",
          summary: "Risk assessment incomplete",
          detail:
            "Supporting document requirements will be generated once the risk assessment inputs are complete.",
          life: 4500,
        });
        return;
      }

      let currentClientEmail = String(
        form?.customer?.email || form?.customer?.contactEmail || ""
      ).trim();

      if (!currentClientEmail) {
        const promptedEmail = await openDocUploadEmailPrompt();
        if (!promptedEmail) return;
        currentClientEmail = String(promptedEmail || "").trim();
        if (typeof onSetAt === "function") {
          onSetAt(["customer", "email"], currentClientEmail);
        }
      }

      if (
        currentClientEmail &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentClientEmail)
      ) {
        showLocalToast({
          severity: "warn",
          summary: "Invalid email",
          detail: "Please enter a valid client email address.",
          life: 3500,
        });
        return;
      }

      // update firestore with documents list prior to send the email
      // ✅ Build an up-to-date snapshot from the UI state (NOT from possibly-stale parent prop)
      const partiesSnapshot = Object.fromEntries(
        (renderParties || []).map((p) => {
          const docs = { ...(p.docs || {}) };
          if (p.partyType === "entity") {
            const requirement = corporateRequirementForIso2(
              p.corporateCountry || p.addressCountry,
              corporateDocRequirementsByIso2
            );
            docs.reg = buildCorporateRegSnapshot(docs.reg || {}, requirement);
          }

          return [
            p.partyId,
            {
              partyId: p.partyId,
              name: p.name,
              role: p.role,
              roles: p.roles || [],
              sourcePartyIds: p.sourcePartyIds || [],
              partyType: p.partyType,
              idCountry: p.idCountry || "",
              addressCountry: p.addressCountry || "",
              corporateCountry: p.corporateCountry || "",
              docs,
            },
          ];
        })
      );

      // const sowSnapshot = form?.documentsChecklist?.sow || storedSow || {};
      const sowAll = form?.documentsChecklist?.sow || storedSow || {};
      const sowSnapshot = Object.fromEntries(
        (derivedSowItems || []).map((it) => [
          it.sowKey,
          buildSowEntry(it, sowAll[it.sowKey]),
        ])
      );

      const checklistSnapshot = {
        parties: partiesSnapshot,
        sow: sowSnapshot,
      };

      await updateDoc(doc(db, "kyc", clientId), {
        "documentsChecklist.parties": partiesSnapshot,
        "documentsChecklist.sow": sowSnapshot,
        updatedAt: serverTimestamp(),
      });

      // await setDoc(
      //   doc(db, "kyc", clientId),
      //   {
      //     documentsChecklist: checklistSnapshot,
      //     updatedAt: serverTimestamp(),
      //   },
      //   { merge: true }
      // );
      // end update direstore

      const token = await user.getIdToken();
      const base = String(apiBaseUrl || MAIL_API_BASE).replace(/\/+$/, "");
      const url = `${base}/myKyc/staff/doc-upload-link/send`;

      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ clientId, email: currentClientEmail }),
      });

      const data = await resp.json().catch(() => ({}));

      if (!resp.ok || data?.ok !== true) {
        showLocalToast({
          severity: "error",
          summary: "Failed",
          detail: data?.error || "Failed to send upload link.",
          life: 4500,
        });
        return;
      }

      showLocalToast({
        severity: "success",
        summary: "Upload link sent",
        detail: data?.to ? `Sent to ${data.to}` : "Email sent to client.",
        life: 3500,
      });
    } catch (e) {
      showLocalToast({
        severity: "error",
        summary: "Failed",
        detail: e?.message || "Failed to send upload link.",
        life: 4500,
      });
    } finally {
      setDocUploadBusy(false);
    }
  }

  /* -------------------- Modal animation -------------------- */

  const overlayMotion = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  };
  const modalMotion = {
    initial: { opacity: 0, y: 14, scale: 0.985 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: 12, scale: 0.985 },
  };

  /* --------------------------------------------------- */
  /* --------------------  RENDER ---------------------- */
  /* --------------------------------------------------- */

  return (
    <div className={`card kycCard mb-3 kycDocuments ${dragAttachKey === "checklist-card" ? "kycDocumentsDropActive" : ""}`} aria-busy={Boolean(intakeLoadingMessage)} {...checklistDropProps()}>
      {!readOnly && checklistDropBounds && createPortal(
        <div className="kycGeneralDropOverlay" style={checklistDropBounds} aria-hidden="true">
          <span className="kycGeneralDropLabel">
            <i className="bi bi-file-earmark-arrow-up" aria-hidden="true" />
            Drop document to identify and process
          </span>
        </div>, document.body
      )}
      <div className="kycSectionHead kycSectionHeadFlex">
        <h5 className="kycSectionTitle">
          {sectionNo}
          {title}
        </h5>
        {(headerEmailText || headerStatusText) && (
          <div className="d-flex gap-2 align-items-center">
            {headerEmailText && (
              <span className="kycSectionHeaderStatus"><i className="bi bi-envelope me-1" style={{ fontSize: "0.7rem" }} />{headerEmailText}</span>
            )}
            {headerStatusText && (
              onHeaderStatusClick && headerStatusText.includes("submitted") ? (
                <button type="button" className="kycSectionHeaderStatus kycSectionHeaderStatusBtn" onClick={onHeaderStatusClick}>
                  <i className="bi bi-eye me-1" style={{ fontSize: "0.7rem" }} />{headerStatusText}
                </button>
              ) : (
                <span className="kycSectionHeaderStatus"><i className={`bi ${headerStatusText.includes("submitted") ? "bi-eye" : "bi-send"} me-1`} style={{ fontSize: "0.7rem" }} />{headerStatusText}</span>
              )
            )}
          </div>
        )}
      </div>

      <div className="kycDocumentsBody">
      <KycDocumentProcessingOverlay message={intakeLoadingMessage} onCancel={onCancelDocumentIntake} />
      <div className="kycDocumentsContent" inert={intakeLoadingMessage ? true : undefined}>
      <div className="kycSectionBody">
        <div className="kycDocSummary kycDocSummaryInBody">
          <div className="d-flex align-items-end justify-content-between gap-3 flex-wrap">
            {/* LEFT: text + progress */}
            <div className="flex-grow-1 kycDocSummaryLeft">
              <div className="kycDocSummaryLine d-flex justify-content-between">
                <span className="text-muted small">
                  Total completion: {globalProgress.obtained}/
                  {globalProgress.required} documents
                </span>
                <span className="text-muted small">{globalProgress.pct}%</span>
              </div>

              <div
                className="progress kycDocProgress"
                aria-label="Documents progress"
              >
                <div
                  className="progress-bar"
                  style={{ width: `${globalProgress.pct}%` }}
                />
              </div>
            </div>

            {/* <button
              type="button"
              onClick={() =>
                showLocalToast({
                  severity: "success",
                  summary: "Test",
                  detail: "Toast works",
                  life: 2500,
                })
              }
            >
              Test toast
            </button> */}

            {/* RIGHT: button aligned with progress bar */}
            {!readOnly ? (
              <div className="kycDocAction d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={openEmailModal}
                  disabled={emailBusy || docUploadBusy}
                >
                  <i className="bi bi-envelope me-2" />
                  Request missing documents
                </button>

                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={onSendDocUploadLinkViaServer}
                  disabled={docUploadBusy || !clientId}
                  title={
                    !clientId
                      ? "Missing clientId"
                      : "Send upload link to the client"
                  }
                >
                  <i
                    className={`bi ${
                      docUploadBusy ? "bi-hourglass-split" : "bi-cloud-arrow-up"
                    } me-2`}
                  />
                  {docUploadBusy ? "Sending…" : "Send upload link"}
                </button>
              </div>
            ) : null}
            {/* {!readOnly ? (
              <div className="kycDocAction">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={openEmailModal}
                >
                  <i className="bi bi-envelope me-2" />
                  Request missing documents
                </button>
              </div>
            ) : null} */}
          </div>

          {/* {!readOnly ? (
            <div className="text-muted small mt-2">
              Sends via server with Reply-To = staff member.
            </div>
          ) : null} */}
        </div>

        {/* <div className="kycDocSummary kycDocSummaryInBody">
          <div className="kycDocSummaryLine">
            <span className="text-muted small">
              Total completion: {globalProgress.obtained}/
              {globalProgress.required}
            </span>
            <span className="text-muted small">{globalProgress.pct}%</span>
          </div>
          <div
            className="progress kycDocProgress"
            aria-label="Documents progress"
          >
            <div
              className="progress-bar"
              style={{ width: `${globalProgress.pct}%` }}
            />
          </div>

          {!readOnly ? (
            <div className="mt-2 d-flex gap-2 align-items-center flex-wrap">
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={openEmailModal}
              >
                <i className="bi bi-envelope me-2" />
                Request missing documents
              </button>
              <div className="text-muted small">
                Sends via server with Reply-To = staff member.
              </div>
            </div>
          ) : null}
        </div> */}

        {/* ===== ID documents ===== */}
        <div className="kycSubSection kycSubCard">
          <div className="kycDocSubHead">
            <div>
              <div className="kycSubSectionTitle" style={{ color: "#45586B" }}>
                1. Identification documents
              </div>
              <div className="kycSubSectionHint text-muted small">
                Customer + shareholders / controllers / signatories.
              </div>
            </div>

            <div className="kycDocSubProgress">
              <div className="kycDocSummaryLine">
                <span className="text-muted small">
                  {idProgress.obtained}/{idProgress.required}
                </span>
                <span className="text-muted small">{idProgress.pct}%</span>
              </div>
              <div className="progress kycDocProgress kycDocProgressSm">
                <div
                  className="progress-bar"
                  style={{ width: `${idProgress.pct}%` }}
                />
              </div>
            </div>
          </div>

          <div className="kycDocGrid mt-2">
            {renderParties.map((p) => {
              const isIndividual = p.partyType === "individual";
              const isTrust = p.partyType === "trust";
              const docs = p.docs || {};
              const approvedForDocKey = (docKey) =>
                partyRequirementKeyCandidates(p, docKey)
                  .map((key) => approvedMap[key] || staffSavedAttachmentMap[key])
                  .find(Boolean);
              const corporateRequirementCountry = iso2(
                p.corporateCountry || p.addressCountry
              );
              const corporateRequirementPending =
                p.partyType === "entity" &&
                !!corporateRequirementCountry &&
                corporateDocRequirementsLoading &&
                !Object.prototype.hasOwnProperty.call(
                  corporateDocRequirementsByIso2,
                  corporateRequirementCountry
                );
              const corporateRequirement = corporateRequirementForIso2(
                corporateRequirementCountry,
                corporateDocRequirementsByIso2
              );
              const approvedPassport = approvedForDocKey("id");
              const approvedPoa = approvedForDocKey("poa");
              const approvedReg = approvedForDocKey("reg");
              const approvedTrustDeed = approvedForDocKey("trustDeed");
              const corporateRequirementDone =
                corporateRequirementSatisfied(
                  docs?.reg || {},
                  corporateRequirement
                );
              const corporateRequirementMetaText =
                corporateRequirementItemMeta(corporateRequirement);
              const corporateRequirementNote =
                corporateRequirementNoteText(corporateRequirement);

              const idObtained = !!docs?.id?.obtained || !!approvedPassport;
              const poaObtained = !!docs?.poa?.obtained || !!approvedPoa;
              const regObtained = corporateRequirementDone;
              const trustDeedObtained = !!docs?.trustDeed?.obtained || !!approvedTrustDeed;
              const idAttachContext = buildPartyStaffAttachContext(
                p,
                "id",
                "Passport or national ID"
              );
              const poaAttachContext = buildPartyStaffAttachContext(
                p,
                "poa",
                "Proof of address (e.g. utility bill)"
              );
              const trustAttachContext = buildPartyStaffAttachContext(
                p,
                "trustDeed",
                "First page of trust deed"
              );
              const regAttachContext = buildPartyStaffAttachContext(
                p,
                "reg",
                "Corporate document",
                { acceptedLabels: corporateRequirement.docs.flatMap((item) => [item.label, item.secondaryLabel].filter(Boolean)) }
              );
              const showReadOnlyId = shouldShowReadOnlyRequirement(docs, "id");
              const showReadOnlyPoa = shouldShowReadOnlyRequirement(
                docs,
                "poa"
              );
              const showReadOnlyReg = shouldShowReadOnlyRequirement(
                docs,
                "reg"
              );
              const showReadOnlyTrustDeed = shouldShowReadOnlyRequirement(
                docs,
                "trustDeed"
              );
              const idExpiryLabel =
                docs?.id?.obtained && docs?.id?.expiryDate
                  ? formatChecklistDate(docs.id.expiryDate)
                  : "";
              const idExpired = idExpiryLabel
                ? isChecklistDateExpired(docs.id.expiryDate)
                : false;

              return (
                <div key={p.partyId} className="kycDocParty">
                  <div className="kycDocPartyHead">
                    <div className="kycDocPartyLeft">
                      <i className={iconForType(p.partyType)} />
                      <div>
                        <div className="kycDocPartyName">{p.name}</div>
                        <div className="kycDocPartyRole">{p.role}</div>
                      </div>
                    </div>

                    <div className="kycDocPartyRight">
                      <div className={badgeForType(p.partyType)}>
                        {p.partyType === "entity"
                          ? "Entity"
                          : p.partyType === "trust"
                          ? "Trust"
                          : "Individual"}
                      </div>
                      <i
                        className={statusIcon(
                          Object.entries(p.docs || {}).every(([docKey, d]) => {
                            if (!d?.required) return true;
                            if (docKey === "id") return idObtained;
                            if (docKey === "poa") return poaObtained;
                            if (docKey === "reg") return regObtained;
                            if (docKey === "trustDeed") return trustDeedObtained;
                            return !!d?.obtained;
                          })
                        )}
                      />
                    </div>
                  </div>

                  <div className="kycDocPartyBody">
                    {isIndividual ? (
                      <>
                        {readOnly ? (
                          showReadOnlyId ? (
                          <div className="kycDocItem kycDocItemReadOnly">
                            {/* <span className="form-check-label d-inline-flex align-items-center gap-2"> */}
                            <span
                              className={
                                "form-check-label d-inline-flex align-items-center gap-2 " +
                                (idObtained ? "kycDocItemChecked" : "")
                              }
                            >
                              <span
                                className={
                                  "kycDocVisualCheckbox " +
                                  (idObtained ? "isChecked" : "")
                                }
                                aria-hidden="true"
                              />
                              <span>
                                Passport or national ID{" "}
                                <span className="kycDocMeta">
                                  (Nationality: {p.idCountry || "—"})
                                </span>
                                {idExpiryLabel ? (
                                  <span
                                    className={
                                      "kycDocExpiryMeta" +
                                      (idExpired ? " kycDocExpiryMetaExpired" : "")
                                    }
                                  >
                                    {idExpired ? "Expired" : "Expires"}: {idExpiryLabel}
                                  </span>
                                ) : null}
                              </span>

                            </span>

                            <ApprovedDocumentActions
                              file={approvedPassport}
                              uploadContext={idAttachContext}
                            />
                          </div>
                          ) : null
                        ) : (
                          <label
                            className={`form-check kycDocItem ${
                              dragAttachKey === idAttachContext.requirementKey ? "kycDocItemDropActive" : ""
                            }`}
                            {...staffAttachDropProps(idAttachContext)}
                          >
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={idObtained}
                              disabled={readOnly}
                              onChange={(e) =>
                                setPartyObtained(
                                  p.partyId,
                                  "id",
                                  e.target.checked
                                )
                              }
                            />
                            <span
                              className={
                                "form-check-label " +
                                (idObtained ? "kycDocItemChecked" : "")
                              }
                            >
                              Passport or national ID{" "}
                              <span className="kycDocMeta">
                                (Nationality: {p.idCountry || "—"})
                              </span>
                              {idExpiryLabel ? (
                                <span
                                  className={
                                    "kycDocExpiryMeta" +
                                    (idExpired ? " kycDocExpiryMetaExpired" : "")
                                  }
                                >
                                  {idExpired ? "Expired" : "Expires"}: {idExpiryLabel}
                                </span>
                              ) : null}
                            </span>
                            {/* <span className="form-check-label">
                              Passport or national ID{" "}
                              <span className="kycDocMeta">
                                (Nationality: {p.idCountry || "—"})
                              </span>
                            </span> */}

                            <div className="kycDocRequirementActions">
                              <ApprovedDocumentActions
                                file={approvedPassport}
                                uploadContext={idAttachContext}
                              />
                            </div>
                          </label>
                        )}

                        {readOnly ? (
                          showReadOnlyPoa ? (
                          <div className="kycDocItem kycDocItemReadOnly">
                            <span
                              className={
                                "form-check-label d-inline-flex align-items-center gap-2 " +
                                (poaObtained ? "kycDocItemChecked" : "")
                              }
                            >
                              <span
                                className={
                                  "kycDocVisualCheckbox " +
                                  (poaObtained ? "isChecked" : "")
                                }
                                aria-hidden="true"
                              />
                              Proof of address (e.g. utility bill){" "}
                              <span className="kycDocMeta">
                                (Country: {p.addressCountry || "—"})
                              </span>
                            </span>

                            <ApprovedDocumentActions
                              file={approvedPoa}
                              uploadContext={poaAttachContext}
                            />
                          </div>
                          ) : null
                        ) : (
                          <label
                            className={`form-check kycDocItem ${
                              dragAttachKey === poaAttachContext.requirementKey ? "kycDocItemDropActive" : ""
                            }`}
                            {...staffAttachDropProps(poaAttachContext)}
                          >
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={poaObtained}
                              disabled={readOnly}
                              onChange={(e) =>
                                setPartyObtained(
                                  p.partyId,
                                  "poa",
                                  e.target.checked
                                )
                              }
                            />
                            <span className="form-check-label">
                              Proof of address (e.g. utility bill){" "}
                              <span className="kycDocMeta">
                                (Country: {p.addressCountry || "—"})
                              </span>
                            </span>

                            <div className="kycDocRequirementActions">
                              <ApprovedDocumentActions
                                file={approvedPoa}
                                uploadContext={poaAttachContext}
                              />
                            </div>
                          </label>
                        )}
                      </>
                    ) : isTrust ? (
                      readOnly ? (
                        showReadOnlyTrustDeed ? (
                        <div className="kycDocItem kycDocItemReadOnly">
                          <span
                            className={
                              "form-check-label d-inline-flex align-items-center gap-2 " +
                              (trustDeedObtained
                                ? "kycDocItemChecked"
                                : "")
                            }
                          >
                            <span
                              className={
                                "kycDocVisualCheckbox " +
                                (trustDeedObtained ? "isChecked" : "")
                              }
                              aria-hidden="true"
                            />
                            First page of trust deed
                          </span>

                          <ApprovedDocumentActions
                            file={approvedTrustDeed}
                            uploadContext={trustAttachContext}
                          />
                        </div>
                        ) : null
                      ) : (
                        <label
                          className={`form-check kycDocItem ${
                            dragAttachKey === trustAttachContext.requirementKey ? "kycDocItemDropActive" : ""
                          }`}
                          {...staffAttachDropProps(trustAttachContext)}
                        >
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={trustDeedObtained}
                            disabled={readOnly}
                            onChange={(e) =>
                              setPartyObtained(
                                p.partyId,
                                "trustDeed",
                                e.target.checked
                              )
                            }
                          />
                          <span className="form-check-label">
                            First page of trust deed
                          </span>
                          <div className="kycDocRequirementActions">
                            <ApprovedDocumentActions
                              file={approvedTrustDeed}
                              uploadContext={trustAttachContext}
                            />
                          </div>
                        </label>
                      )
                    ) : corporateRequirementPending ? (
                      <div className="kycDocItem kycDocItemReadOnly kycCorporateDocLoading">
                        <div
                          className="kycCorporateDocLoadingState"
                          aria-label="Loading"
                          role="status"
                        >
                          <span
                            className="kycCorporateDocLoadingIndicator"
                            aria-hidden="true"
                          />
                          <span
                            className="kycCorporateDocLoadingText"
                            data-text="Loading"
                          />
                        </div>
                      </div>
                    ) : readOnly ? (
                      showReadOnlyReg || corporateRequirementDone ? (
                      <div className="kycDocItem kycDocItemReadOnly kycCorporateDocGroup">
                        <div
                          className={
                            "form-check-label " +
                            (corporateRequirementDone ? "kycDocItemChecked" : "")
                          }
                        >
                          <div>
                            {corporateRequirementGroups(corporateRequirement).map((group, groupIndex) => (
                              <React.Fragment key={group.title || groupIndex}>
                                {group.title ? (
                                  <div className="kycCorporateDocCase">
                                    {groupIndex > 0 ? <span className="kycCorporateDocOr">or</span> : null}
                                    {group.title}
                                  </div>
                                ) : null}
                                {group.items.map((item) => (
                                  <div key={`${groupIndex}_${item.key}`} className="kycCorporateDocRow">
                                    <span
                                      className={
                                        "kycDocVisualCheckbox " +
                                        (isCorporateDocItemObtained(docs?.reg, item.key) ? "isChecked" : "")
                                      }
                                      aria-hidden="true"
                                    />
                                    <span>
                                      {item.label}
                                      {item.secondaryLabel ? (
                                        <span className="kycDocMeta ms-1">({item.secondaryLabel})</span>
                                      ) : null}
                                      {item.optional ? (
                                        <span className="kycDocMeta ms-1">(optional)</span>
                                      ) : corporateRequirementMetaText ? (
                                        <span className="kycDocMeta ms-1">{corporateRequirementMetaText}</span>
                                      ) : null}
                                    </span>
                                  </div>
                                ))}
                              </React.Fragment>
                            ))}
                            {corporateRequirementNote ? (
                              <div className="kycCorporateDocNote">
                                {corporateRequirementNote}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <ApprovedDocumentActions
                          file={approvedReg}
                          uploadContext={regAttachContext}
                        />
                      </div>
                      ) : null
                    ) : (
                      <div
                        className={`kycDocItem kycCorporateDocGroup ${
                          dragAttachKey === regAttachContext.requirementKey ? "kycDocItemDropActive" : ""
                        }`}
                        {...staffAttachDropProps(regAttachContext)}
                      >
                        <div className="form-check-label">
                          <div className="d-flex flex-column">
                            {corporateRequirementGroups(corporateRequirement).map((group, groupIndex) => (
                              <React.Fragment key={group.title || groupIndex}>
                                {group.title ? (
                                  <div className="kycCorporateDocCase">
                                    {groupIndex > 0 ? <span className="kycCorporateDocOr">or</span> : null}
                                    {group.title}
                                  </div>
                                ) : null}
                                {group.items.map((item) => (
                                  <label key={`${groupIndex}_${item.key}`} className="form-check kycCorporateDocOption mb-0">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={isCorporateDocItemObtained(docs?.reg, item.key)}
                                      disabled={readOnly}
                                      onChange={(e) =>
                                        setCorporateDocItemObtained(
                                          p.partyId,
                                          item,
                                          corporateRequirement,
                                          e.target.checked
                                        )
                                      }
                                    />
                                    <span className="form-check-label">
                                      {item.label}
                                      {item.secondaryLabel ? (
                                        <span className="kycDocMeta ms-1">({item.secondaryLabel})</span>
                                      ) : null}
                                      {item.optional ? (
                                        <span className="kycDocMeta ms-1">(optional)</span>
                                      ) : corporateRequirementMetaText ? (
                                        <span className="kycDocMeta ms-1">{corporateRequirementMetaText}</span>
                                      ) : null}
                                    </span>
                                  </label>
                                ))}
                              </React.Fragment>
                            ))}
                            {corporateRequirementNote ? (
                              <div className="kycCorporateDocNote">
                                {corporateRequirementNote}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="kycDocRequirementActions">
                          <ApprovedDocumentActions
                            file={approvedReg}
                            uploadContext={regAttachContext}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ===== SoW corroboration ===== */}
        <div className="kycSubSection kycSubCard mt-4">
          <div className="kycDocSubHead">
            <div>
              <div className="kycSubSectionTitle">2. SoW & SoF corroboration</div>
              <div
                className="kycSubSectionHint text-muted small"
                style={{ maxWidth: "95%", whiteSpace: "nowrap" }}
              >
                Documents required based on selected Source of Wealth types.
              </div>
            </div>

            <div className="kycDocSubProgress">
              <div className="kycDocSummaryLine">
                <span className="text-muted small">
                  {sowProgress.obtained}/{sowProgress.required}
                </span>
                <span className="text-muted small">{sowProgress.pct}%</span>
              </div>
              <div className="progress kycDocProgress kycDocProgressSm">
                <div
                  className="progress-bar"
                  style={{ width: `${sowProgress.pct}%` }}
                />
              </div>
            </div>
          </div>

          {derivedSowItems.length === 0 ? (
            <div className="text-muted small mt-2">
              No Source of Wealth type selected yet. Select SoW in Section
              SoW/SoF to generate requirements.
            </div>
          ) : !supportingDocRiskReady ? (
            <div className="text-muted small mt-2">
              Supporting document requirements will be generated once the risk
              assessment inputs are complete.
            </div>
          ) : visibleSowItems.length === 0 ? (
            <div className="text-muted small mt-2">
              No SoW/SoF corroboration documents required for the current risk
              outcome.
            </div>
          ) : (
            <div className="kycSowGrid mt-2">
              {visibleSowItems.map((it) => {
                const entry = buildSowEntry(
                  it,
                  (form?.documentsChecklist?.sow || {})[it.sowKey] ||
                    storedSow[it.sowKey]
                );
                const docsMap = entry?.docs || {};
                const otherDetails = entry?.otherDetails || "";
                const sowDocEntries = readOnly
                  ? visibleReadOnlySowDocEntries(docsMap)
                  : Object.entries(docsMap);

                const requirementMet = isSowEntrySatisfied(entry);

                return (
                  <div key={it.sowKey} className="kycSowCard">
                    <div className="kycSowHead">
                      <div className="kycSowHeadRow">
                        <div className="kycSowHeadLeft">
                          <i className={sowTypeIcon(it.label)} />
                          <div className="kycSowTitle">{it.label}</div>
                        </div>
                        <div className="kycSowHeadRight">
                          <i className={statusIcon(requirementMet)} />
                        </div>
                      </div>
                    </div>

                    <div className="kycSowBody">
                      {it.isOther ? (
                        <div className="mb-2">
                          <div className="text-muted small mb-1">
                            Please specify
                          </div>
                          <input
                            className="form-control"
                            value={otherDetails}
                            disabled={readOnly}
                            onChange={(e) =>
                              setSowOtherDetails(it.sowKey, e.target.value)
                            }
                            placeholder="Describe briefly…"
                          />
                        </div>
                      ) : null}
                      {it.isOther ? null : (
                        <div className="d-flex flex-column gap-1">
                          {sowDocEntries.map(([docKey, doc]) => {
                            const sowKeyCandidates = [
                              doc?.requirement === "group" && doc?.groupId
                                ? `sow:${it.sowKey}:grp:${doc.groupId}:${docKey}`
                                : "",
                              `sow:${it.sowKey}:${
                                doc?.requirement === "optional" ? "opt" : "req"
                              }:${docKey}`,
                              `sow:${it.sowKey}:req:${docKey}`,
                              `sow:${it.sowKey}:opt:${docKey}`,
                              `sow:${it.sowKey}:${docKey}`,
                            ];

                            const approvedSowFile = sowKeyCandidates
                              .map((k) => approvedMap[k] || staffSavedAttachmentMap[k])
                              .find(Boolean);
                            const sowAttachContext = buildSowStaffAttachContext(
                              it,
                              docKey,
                              doc
                            );

                            return readOnly ? (
                              <div
                                key={docKey}
                                className="kycDocItem kycDocItemReadOnly"
                              >
                                <span
                                  className={
                                    "form-check-label d-inline-flex align-items-center gap-2 " +
                                    (doc?.obtained ? "kycDocItemChecked" : "")
                                  }
                                >
                                  {doc?.label || docKey}
                                  {doc?.requirement === "optional" ? (
                                    <span className="kycDocMeta">
                                      {" "}
                                      (optional)
                                    </span>
                                  ) : doc?.requirement === "group" ? (
                                    <span className="kycDocMeta">
                                      {" "}
                                      {`(at least ${doc?.minRequired || 1} of)`}
                                    </span>
                                  ) : null}

                                  {doc?.obtained ? (
                                    <i
                                      className={docCheckIcon}
                                      style={{
                                        fontSize: 15,
                                        color: "#8AA395",
                                        marginLeft: 5,
                                      }}
                                    />
                                  ) : null}
                                </span>

                                <ApprovedDocumentActions file={approvedSowFile} />
                              </div>
                            ) : (
                              <label
                                key={docKey}
                                className={`form-check kycDocItem kycDocItemTight ${
                                  dragAttachKey === sowAttachContext.requirementKey ? "kycDocItemDropActive" : ""
                                }`}
                                {...staffAttachDropProps(sowAttachContext)}
                              >
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={!!doc?.obtained}
                                  disabled={readOnly}
                                  onChange={(e) =>
                                    setSowDocObtained(
                                      it.sowKey,
                                      docKey,
                                      e.target.checked
                                    )
                                  }
                                />

                                <span className="form-check-label">
                                  {doc?.label || docKey}
                                  {doc?.requirement === "optional" ? (
                                    <span className="kycDocMeta">
                                      {" "}
                                      (optional)
                                    </span>
                                  ) : doc?.requirement === "group" ? (
                                    <span className="kycDocMeta">
                                      {" "}
                                      {`(at least ${doc?.minRequired || 1} of)`}
                                    </span>
                                  ) : null}
                                </span>

                                <div className="kycDocRequirementActions">
                                  <ApprovedDocumentActions
                                    file={approvedSowFile}
                                    uploadContext={sowAttachContext}
                                  />
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <AdditionalDocuments files={additionalFiles} clientId={clientId}
          loadFile={fileToAuthenticatedBlobUrl} readOnly={readOnly} deleting={!!deletingDocumentKey}
          onDelete={deleteAdditionalDocument}
          onPreview={file => openApprovedDocumentPreview(file).catch(error => {
            closePreviewModal();
            showLocalToast({ severity: "error", summary: "Preview failed", detail: error.message, life: 4200 });
          })} />

        {/* ===================== STAFF ATTACH MODAL ===================== */}
        {staffAttach.open && staffAttach.intakeReview && <KycDocumentIntakeModal
          form={form}
          intake={{
            session: {
              documents: staffAttach.intakeDocuments || [],
              busy: staffAttach.busy,
              saving: staffAttach.saving,
              message: staffAttach.stage,
              error: staffAttach.error,
              saveDisabled: !staffAttach.serverFile,
            },
            close: cancelStaffAttach,
            finish: finishChecklistIntake,
            remove: id => setStaffAttach(prev => ({ ...prev, intakeDocuments: prev.intakeDocuments.map(document =>
              document.id === id ? { ...document, attachmentRemoved: true } : document) })),
          }}
        />}
        <AnimatePresence>
          {staffAttach.open && !staffAttach.intakeReview ? (
            <motion.div
              {...overlayMotion}
              transition={{ duration: 0.16 }}
              className="kycStaffAttachOverlay"
              onClick={(e) => {
                if (e.target === e.currentTarget && canDismissStaffAttach()) {
                  cancelStaffAttach();
                }
              }}
            >
              <motion.div
                {...modalMotion}
                transition={{ type: "spring", stiffness: 420, damping: 34, mass: 0.9 }}
                className="kycStaffAttachModal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="kycStaffAttachHeader">
                  <div>
                    <div className="kycStaffAttachTitle">Attach document</div>
                    <div className="kycStaffAttachSub">
                      {staffAttach?.context?.requirementText || "Document requirement"}
                    </div>
                  </div>
                  <div className="kycStaffAttachHeaderRight">
                    <div className="kycStaffAttachFileRow">
                      <img
                        src={iconUploadBlue}
                        alt=""
                        className="kycStaffAttachFileIcon"
                        aria-hidden="true"
                      />
                      <div>
                        <div className="kycStaffAttachFileName">
                          {staffAttach?.file?.name || "Document"}
                        </div>
                        <div className="text-muted kycStaffAttachFileMeta">
                          {staffAttach?.file?.type || "File"} · {prettyBytes(staffAttach?.file?.size)}
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary kycStaffAttachPreviewBtn"
                    onClick={openStaffAttachPreview}
                    disabled={!staffAttach?.serverFile}
                    aria-label="Preview document"
                    title="Preview document"
                  >
                    <i className="bi bi-eye" />
                  </button>
                </div>

                <div className="kycStaffAttachBody">
                  {staffAttach.stage ? (
                    <div className="kycStaffAttachStage">
                      {staffAttach.busy || staffAttach.saving ? (
                        <span className="kycDocDeleteSpinner" aria-hidden="true" />
                      ) : (
                        <i className="bi bi-check2-circle" />
                      )}
                      <span>{staffAttach.stage}</span>
                    </div>
                  ) : null}

                  {staffAttach.error ? (
                    <div className="alert alert-danger py-2 mb-0">{staffAttach.error}</div>
                  ) : null}

                  {staffAttach.scanRequired ? (
                    <div className="kycStaffAttachScanBox">
                      {!staffAttach.scanResult ? (
                        <div className="kycStaffAttachScanningState">
                          <span className="kycStaffAttachScanningText">
                            <span className="kycDocDeleteSpinner" aria-hidden="true" />
                            <span>Processing...</span>
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary kycStaffAttachSkipScanBtn"
                            onClick={skipStaffAttachScan}
                          >
                            Skip
                          </button>
                        </div>
                      ) : (() => {
                        const kind = staffAttachScanKind(staffAttach.scanResult, staffAttach.context);
                        const reasons = staffAttachReasons(staffAttach.scanResult, kind);
                        const noBadgeScanMessage = staffAttachNoBadgeScanMessage(staffAttach.scanResult, kind);
                        const extractedRows = kind === "corporate" ? corporateScanPresentation(staffAttach.scanResult).rows : [];
                        const activeDetail = staffAttachDetail;
                        return (
                          <>
                            <div className="kycStaffAttachScanHeader">
                              <img
                                src={staffAttachDocumentIcon(kind)}
                                alt=""
                                className="kycStaffAttachScanIcon"
                                aria-hidden="true"
                              />
                              <div>
                                <div className="kycStaffAttachScanDocType">
                                  {staffAttachDocumentTitle(kind)}
                                </div>
                                <div className="kycStaffAttachScanSub">
                                  {reasons.length ? "Scan outcome" : noBadgeScanMessage}
                                </div>
                              </div>
                            </div>

                            <div className="kycStaffAttachBadges">
                              {reasons.map((reason) => (
                                <button
                                  key={reason.key}
                                  type="button"
                                  role="button"
                                  className={`docDataConsistencyInlineBadge ${staffAttachReasonBadgeClass(reason, kind)}`}
                                  onClick={() => {
                                    const nextDetail = staffAttachDetailForReason(staffAttach.scanResult, reason, kind);
                                    setStaffAttachDetail((prev) =>
                                      prev?.detailKey === nextDetail.detailKey ? null : nextDetail
                                    );
                                  }}
                                >
                                  {reason.label}
                                </button>
                              ))}
                            </div>

                            {extractedRows.length > 0 && (
                              <section className="kycStaffAttachExtracted" aria-label="Extracted corporate document details">
                                <div className="kycStaffAttachExtractedTitle">Extracted details</div>
                                <dl className="kycStaffAttachExtractedGrid">
                                  {extractedRows.map(({ label, value }) => (
                                    <div key={label}>
                                      <dt>{label}</dt>
                                      <dd>{/country/i.test(label) ? formatStaffAttachCountry(value) : value}</dd>
                                    </div>
                                  ))}
                                </dl>
                              </section>
                            )}

                            <AnimatePresence initial={false}>
                              {activeDetail ? (
                                <motion.div
                                  key="staff-attach-detail-panel"
                                  initial={{ opacity: 0, height: 0, y: -4 }}
                                  animate={{ opacity: 1, height: "auto", y: 0 }}
                                  exit={{ opacity: 0, height: 0, y: -4 }}
                                  transition={{ duration: 0.18, ease: "easeOut" }}
                                  className="kycStaffAttachDetail docCheckDetailBody"
                                >
                                  <AnimatePresence initial={false} mode="wait">
                                    <motion.div
                                      key={activeDetail.detailKey || activeDetail.reasonTitle || activeDetail.title || "scan-detail"}
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 1 }}
                                      exit={{ opacity: 0 }}
                                      transition={{ duration: 0.14, ease: "easeOut" }}
                                      className="kycStaffAttachDetailInner"
                                    >
                                      <div className={`docCheckReasonCard ${cleanStr(activeDetail?.status) ? `is-${cleanStr(activeDetail.status)}` : ""}`}>
                                        <span className="docCheckReasonEyebrow">
                                          {activeDetail.reasonEyebrow || "Issue"}
                                        </span>
                                        <span className="docCheckReasonTitle">
                                          {activeDetail.reasonTitle || activeDetail.title}
                                        </span>
                                      </div>

                                      {(activeDetail.rows || []).map((row) => {
                                        const childRows = Array.isArray(row?.rows) ? row.rows : [];
                                        if (childRows.length) {
                                          return (
                                            <div
                                              className="docCheckDetailRow is-grouped"
                                              key={row.key || childRows.map((child) => `${child.label}_${child.value}`).join("_")}
                                            >
                                              {childRows.map((child) => (
                                                <div className="docCheckDetailSubRow" key={`${child.label}_${child.value}`}>
                                                  <div className="docCheckDetailLabel">{child.label}</div>
                                                  <div className="docCheckDetailValue">{String(child.value || "—")}</div>
                                                </div>
                                              ))}
                                            </div>
                                          );
                                        }
                                        return (
                                          <div className="docCheckDetailRow" key={`${row.label}_${row.value}`}>
                                            <div className="docCheckDetailLabel">{row.label}</div>
                                            <div className="docCheckDetailValue">{String(row.value || "—")}</div>
                                          </div>
                                        );
                                      })}

                                      {(activeDetail.sections || []).map((section) => (
                                        <div
                                          className={`docCheckDetailSection ${section.variant ? `is-${section.variant}` : ""}`}
                                          key={section.title}
                                        >
                                          <div className="docCheckDetailSectionTitle">{section.title}</div>
                                          {(section.rows || []).map((row) => (
                                            <div
                                              className={`docCheckDetailRow ${
                                                section.variant === "utilityDocumentDetails" ||
                                                section.variant === "idDocumentDetails" ||
                                                section.variant === "corporateDocumentDetails"
                                                  ? "is-utilityDetailCard"
                                                  : ""
                                              }`}
                                              key={`${section.title}_${row.label}_${row.value}`}
                                            >
                                              <div className="docCheckDetailLabel">{row.label}</div>
                                              <div className="docCheckDetailValue">{String(row.value || "—")}</div>
                                            </div>
                                          ))}
                                        </div>
                                      ))}
                                    </motion.div>
                                  </AnimatePresence>
                                </motion.div>
                              ) : null}
                            </AnimatePresence>
                          </>
                        );
                      })()}
                    </div>
                  ) : null}
                </div>

                <div className="kycStaffAttachFooter">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={cancelStaffAttach}
                    disabled={!canDismissStaffAttach()}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn kycStaffAttachSaveBtn"
                    onClick={handleStaffAttachSaveClick}
                    disabled={!canSaveStaffAttach()}
                  >
                    {staffAttach.saving ? "Saving..." : "Save"}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>

        <AnimatePresence>
          {staffAttachKycUpdateConfirm.open ? (
            <motion.div
              className="docModalOverlay docKycUpdateConfirmOverlay"
              style={{ position: "fixed", zIndex: 100002 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onMouseDown={closeStaffAttachKycUpdateConfirm}
            >
              <motion.div
                className="docModalStack"
                initial={{ opacity: 0, scale: 0.82 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
              >
                <div
                  className="docModalShell"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="docModalHeader">
                    <div className="docModalHeaderIcon" aria-hidden="true">
                      <i className="bi bi-person-add" />
                    </div>
                    <div className="docModalTitle">
                      {staffAttachKycUpdateConfirm.isCorporate
                        ? "Update KYC profile from corporate document"
                        : staffAttachKycUpdateConfirm.isProofOfAddress
                        ? "Update KYC profile from utility document"
                        : "Update KYC profile from ID"}
                    </div>
                    <button
                      type="button"
                      className="docModalCloseBtn"
                      onClick={closeStaffAttachKycUpdateConfirm}
                      aria-label="Close"
                    >
                      <i className="bi bi-x-lg" />
                    </button>
                  </div>

                  <div className="docModalBody">
                    <div className="docKycUpdateConfirmBody">
                      <p className="docKycUpdateConfirmText">
                        {staffAttachKycUpdateConfirm.isCorporate
                          ? "The approved corporate document contains company data that can update the current KYC record."
                          : staffAttachKycUpdateConfirm.isProofOfAddress
                          ? "The approved utility document contains profile data that can update the current KYC record."
                          : "The approved ID contains profile data that can update the current KYC record."}
                      </p>

                      <div className="docKycUpdateOptions">
                        {sortKycUpdateOptionsAddFirst(staffAttachKycUpdateConfirm.options).map((option) => (
                          <label className="docKycUpdateOption" key={option.key}>
                            <input
                              type="checkbox"
                              checked={staffAttachKycUpdateConfirm.selected?.[option.key] !== false}
                              onChange={(event) => {
                                const checked = event.target.checked;
                                setStaffAttachKycUpdateConfirm((prev) => ({
                                  ...prev,
                                  selected: {
                                    ...(prev.selected || {}),
                                    [option.key]: checked,
                                  },
                                }));
                              }}
                            />
                            <span>{option.label}</span>
                          </label>
                        ))}
                      </div>

                      <div className="docKycUpdateConfirmActions">
                        {(() => {
                          const hasSelected = hasSelectedKycUpdateOption(
                            staffAttachKycUpdateConfirm.options,
                            staffAttachKycUpdateConfirm.selected
                          );
                          return (
                            <button
                              type="button"
                              className={`docKycUpdateConfirmBtn ${hasSelected ? "isPrimary" : "isSkip"}`}
                              ref={staffAttachKycUpdateContinueBtnRef}
                              onClick={handleStaffAttachKycUpdateContinue}
                            >
                              {hasSelected ? "Update KYC" : "Skip"}
                            </button>
                          );
                        })()}
                        <button
                          type="button"
                          className="docKycUpdateConfirmBtn isPrimary"
                          ref={staffAttachKycUpdateYesBtnRef}
                          style={{ display: "none" }}
                          tabIndex={-1}
                          aria-hidden="true"
                          disabled={
                            !hasSelectedKycUpdateOption(
                              staffAttachKycUpdateConfirm.options,
                              staffAttachKycUpdateConfirm.selected
                            )
                          }
                          onClick={() => {
                            confirmStaffAttachSaveWithKycUpdates(true).catch((e) => {
                              setStaffAttach((prev) => ({
                                ...prev,
                                saving: false,
                                error: e?.message || "Save failed.",
                              }));
                            });
                          }}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          className="docKycUpdateConfirmBtn"
                          ref={staffAttachKycUpdateNoBtnRef}
                          style={{ display: "none" }}
                          tabIndex={-1}
                          aria-hidden="true"
                          onClick={() => {
                            confirmStaffAttachSaveWithKycUpdates(false).catch((e) => {
                              setStaffAttach((prev) => ({
                                ...prev,
                                saving: false,
                                error: e?.message || "Save failed.",
                              }));
                            });
                          }}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>

        {/* ===================== EMAIL MODAL ===================== */}
        <AnimatePresence>
          {emailModalOpen ? (
            <motion.div
              {...overlayMotion}
              transition={{ duration: 0.16 }}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 2000,
                background: "rgba(10, 15, 20, 0.38)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 18,
              }}
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) closeEmailModal();
              }}
            >
              <motion.div
                {...modalMotion}
                transition={{
                  type: "spring",
                  stiffness: 420,
                  damping: 34,
                  mass: 0.9,
                }}
                style={{
                  width: "min(1120px, 100%)",
                  height: "min(740px, 86vh)",
                  background: "#F9F7F5",
                  borderRadius: 18,
                  overflow: "hidden",
                  boxShadow: "0 18px 55px rgba(0,0,0,0.28)",
                  border: "0px solid rgba(255,255,255,0.35)",
                  display: "flex",
                  flexDirection: "column",
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div
                  style={{
                    padding: "14px 16px",
                    borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                    background: "#D2C1B7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 12,
                        display: "grid",
                        placeItems: "center",
                        background: "#F9F7F5",
                        border: "1px solid rgba(15,23,42,0.08)",
                      }}
                    >
                      <i
                        className="bi bi-envelope-paper"
                        style={{ fontSize: 16, color: "#415a6c" }}
                      />
                    </div>

                    <div>
                      <div
                        style={{
                          fontFamily: "Nunito",
                          fontSize: 24,
                          fontWeight: 600,
                          color: "#fff",
                        }}
                      >
                        Email Composer · Request missing documents
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-light"
                    disabled={emailBusy}
                    onClick={closeEmailModal}
                  >
                    Close
                  </button>
                </div>

                {/* Body (NO modal scroll) */}
                <div style={{ padding: 16, flex: 1, overflow: "hidden" }}>
                  <div style={{ height: "100%" }} className="row g-3">
                    {/* Left column */}
                    <div
                      className="col-lg-5"
                      style={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <div>
                        <label className="form-label small text-muted mb-1">
                          Subject
                        </label>
                        <input
                          className="form-control"
                          value={emailSubject}
                          onChange={(e) => setEmailSubject(e.target.value)}
                          disabled={emailBusy}
                        />
                      </div>

                      <div
                        className="mt-3"
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          minHeight: 0,
                        }}
                      >
                        <label className="form-label small text-muted mb-1">
                          Email body (plain text)
                        </label>
                        <textarea
                          className="form-control"
                          rows={12}
                          value={emailText}
                          onChange={(e) => setEmailText(e.target.value)}
                          disabled={emailBusy}
                          style={{ flex: 1, minHeight: 0, resize: "none" }}
                        />
                        <div className="text-muted small mt-1">
                          Tip: After clicking <b>Copy formatted</b>, paste into
                          Outlook / Gmail / Apple Mail — it should keep the
                          layout.
                        </div>
                      </div>

                      <div className="mt-3 d-flex gap-2 flex-wrap">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={onCopyPlain}
                          disabled={emailBusy}
                        >
                          <i className="bi bi-clipboard me-2" />
                          Copy plain
                        </button>

                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={onCopyRich}
                          disabled={emailBusy}
                        >
                          <i className="bi bi-clipboard-check me-2" />
                          Copy formatted
                        </button>

                        <button
                          type="button"
                          className="btn kycBtnLightGreen"
                          onClick={onSendEmailViaServer}
                          disabled={emailBusy || !clientId}
                          style={{ marginLeft: "auto" }}
                        >
                          <i
                            className={`bi ${
                              emailBusy ? "bi-hourglass-split" : "bi-send"
                            } me-2`}
                          />
                          {emailBusy ? "Sending…" : "Send Email"}
                        </button>
                        {/* <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={onSendEmailViaServer}
                          disabled={emailBusy || !clientId}
                          style={{ marginLeft: "auto" }}
                        >
                          {emailBusy ? "Sending…" : "Send Email"}
                        </button> */}
                      </div>

                      {emailNotice ? (
                        <div
                          className="alert alert-secondary py-2 mt-3 mb-0"
                          role="alert"
                        >
                          {emailNotice}
                        </div>
                      ) : null}
                    </div>

                    {/* Right column (only this scrolls) */}
                    <div
                      className="col-lg-7"
                      style={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <label className="form-label small text-muted mb-1">
                        {/* Preview of formatted email */}
                      </label>

                      <div
                        style={{
                          flex: 1,
                          minHeight: 0,
                          overflow: "auto",
                          background: "#EAEEF4",
                          // background: "rgba(255,255,255,0.85)",
                          borderRadius: 14,
                          border: "1px solid rgba(15,23,42,0.08)",
                          boxShadow: "0 10px 26px rgba(15,23,42,0.08)",
                          padding: "15px 0 15px 0", // top, right, bottom, left
                          // padding: 10,
                          marginTop: 15,
                          marginBottom: 20,
                        }}
                      >
                        <div dangerouslySetInnerHTML={{ __html: emailHtml }} />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
      {docUploadEmailPromptOpen && (
        <div
          className="kycEmailPromptBackdrop"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeDocUploadEmailPrompt(null);
          }}
        >
          <div
            className="kycEmailPromptCard"
            role="dialog"
            aria-modal="true"
            aria-labelledby="docUploadEmailPromptTitle"
          >
            <button
              type="button"
              className="kycEmailPromptClose"
              aria-label="Close"
              onClick={() => closeDocUploadEmailPrompt(null)}
            >
              ×
            </button>
            <div className="kycEmailPromptIcon">
              <i className="bi bi-envelope" aria-hidden="true" />
            </div>
            <div id="docUploadEmailPromptTitle" className="kycEmailPromptTitle">
              Client email is missing
            </div>
            <p className="kycEmailPromptText">
              Enter the client email address to send the document upload link.
            </p>
            <input
              ref={docUploadEmailPromptInputRef}
              type="email"
              className={`kycEmailPromptInput ${
                docUploadEmailPromptError ? "isInvalid" : ""
              }`}
              value={docUploadEmailPromptValue}
              placeholder="client@example.com"
              onChange={(e) => {
                setDocUploadEmailPromptValue(e.target.value);
                if (docUploadEmailPromptError) setDocUploadEmailPromptError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitDocUploadEmailPrompt();
                if (e.key === "Escape") closeDocUploadEmailPrompt(null);
              }}
            />
            {docUploadEmailPromptError ? (
              <div className="kycEmailPromptError">{docUploadEmailPromptError}</div>
            ) : null}
            <div className="kycEmailPromptActions">
              <button
                type="button"
                className="kycEmailPromptCancel"
                onClick={() => closeDocUploadEmailPrompt(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="kycEmailPromptSubmit"
                onClick={submitDocUploadEmailPrompt}
              >
                Send link
              </button>
            </div>
          </div>
        </div>
      )}
      {pendingDocumentReview && createPortal(
        <StaffDocumentsReviewPage
          key={`${pendingDocumentReview.docId}:${pendingDocumentReview.objectPath}`}
          docId={pendingDocumentReview.docId}
          initialObjectPath={pendingDocumentReview.objectPath}
          isModal
          documentOnly
          onClose={() => {
            setPendingDocumentReview(null);
            refreshApprovedDocuments({ clearOnFailure: false });
          }}
        />,
        document.body
      )}

      <DocumentPreviewModal
        open={previewOpen}
        pdfRenderer={isAdditionalDocument(previewFile) ? PdfDocumentPreview : undefined}
        className={isAdditionalDocument(previewFile) ? 'docModalOverlayPreview kycAdditionalPreview' : 'docModalOverlayPreview'}
        file={previewFile}
        title="Preview"
        loading={previewLoading}
        loadingText="Loading..."
        showOpenInNewTab={false}
        floatingActions={
          currentPreviewUrl() ? (
            <>
              <a
                className="docModalFloatingActionBtn"
                href={currentPreviewUrl()}
                target="_blank"
                rel="noreferrer"
                title="Open in new tab"
                aria-label="Open in new tab"
              >
                <img src={iconCircleBlueView} alt="" aria-hidden="true" />
              </a>
              <button
                type="button"
                className="docModalFloatingActionBtn"
                title="Download"
                aria-label="Download"
                onClick={downloadCurrentPreviewFile}
              >
                <img src={iconCircleBlueDownload} alt="" aria-hidden="true" />
              </button>
            </>
          ) : null
        }
        onClose={closePreviewModal}
      />
      <Toast ref={toastRef} baseZIndex={10000} />
      </div>
      </div>
    </div>
  );
}
